/**
 * AgentControlPage — Operate ▸ Agents (Agent Control Plane, Phase 1, read-only).
 *
 * The single place to SEE every agent across the platform: the catalog
 * (agent_registry), recent runs (central ai_tasks tracker), the human-approval
 * gate queue (NBA drafts + RCA fixes), and a cost/health summary. Controls
 * (pause / run-now / approve) arrive in Phase 2 — this surface is observe-only.
 *
 * Reads: agentsApi.getSummary / getRegistry / getRuns / getGated.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Bot, AlertTriangle, Clock, ShieldCheck, Play, Pause, Check, X, Wallet, Plus, ScrollText, Inbox, ExternalLink, ChevronDown, ChevronUp, ImageIcon, Paperclip, Search, Filter, Circle, Loader2, GitMerge, GitPullRequest, Rocket, PauseCircle, HelpCircle, Download, FileVideo, FileText, Layers, Phone, Wrench, Send, MessageSquare, Sparkles, Tags, ArrowRight } from 'lucide-react';
import { tvTokens, TvPageBar } from '../../shared/components/triveny';
import { useTabbedChrome } from '@/components/weavein';
import ObservabilityIssuesPanel from './ObservabilityIssuesPanel';
import ContributorPrsPanel from './ContributorPrsPanel';
import FunderInterfacesPanel from './FunderInterfacesPanel';

type AgentTab = 'tickets' | 'catalog' | 'issues' | 'prs' | 'funders';
// Survives remounts within this browser tab (module scope, not component state).
let lastAgentTab: AgentTab | null = null;
import { INTAKE_AGENT_ASKS, isReanalyzableStatus } from './agentReviewLoop';
import { agentsApi, getPlatformApi, marketingApi, type RcaLogParams } from '../../shared/services/platformApi';
import { config as productConfig } from '@/src/config';
import {
  FOCUS_RCA_JOB_EVENT,
  clearPendingRcaJob,
  consumeAgentJobFromUrl,
  normalizeJobId,
  peekPendingRcaJob,
} from '../../shared/utils/agentJobLink';
import { useRcaFilters } from '../components/agents/useRcaFilters';
import { RcaFilterBar, RcaActiveFilters, buildProvenanceAxes, type FilterAxisDef } from '../components/agents/RcaFilterBar';
import { RcaViewsMenu } from '../components/agents/RcaViewsMenu';
import { RcaGroupSections } from '../components/agents/RcaGroupSections';
import { RcaOriginBadge, RcaPeopleLine } from '../components/agents/RcaRowMeta';
import { filterChip, filterLabel } from '../components/agents/rcaStyles';
import {
  EMPTY_ROLLUPS,
  GROUP_BY_LABEL,
  GROUP_BY_OPTIONS,
  groupFilterFor,
  parseRcaGroups,
  parseRcaRollups,
  serverSortFor,
  stateFromViewParams,
  sameFilterState,
  waitingOn,
  WAITING_ON_LABEL,
  WAITING_ON_ORDER,
  type RcaGroup,
  type RcaGroupBy,
  type RcaPerson,
  type RcaRollups,
  type RcaSavedView,
  type RcaSortKey,
} from '../components/agents/rcaFilterModel';

// Split a plain-text string on URLs and render them as links, leaving all other
// text untouched. Two link kinds are recognized:
//   • absolute http(s) URLs  → open in a new tab (external)
//   • app-relative paths ("/admin/member-fields", "/settings/…") → same-tab,
//     in-app navigation (so RCA deep-links to console pages just work)
// The regex is deliberately conservative: it stops a match before common
// trailing punctuation (.,;:!?)]}>"') so a URL at the end of a sentence doesn't
// swallow the period. Callers keep whiteSpace:'pre-wrap' on the container so
// wrapping/newlines are preserved. Returns a React fragment of text + <a> nodes.
// Also recognizes `tab:<tabid>` deep-link tokens (emitted by the RCA agent in
// its analysis / proposed-changes text) and renders them as an in-app nav pill
// that dispatches the app-wide `changeTab` CustomEvent — real tab navigation
// (App.tsx pushes browser history, so back returns to the console).
const URL_OR_PATH_RE = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"\])}]|tab:[a-z0-9][a-z0-9-]*|\/[A-Za-z0-9][A-Za-z0-9\-._~/]*[A-Za-z0-9\-_~/])/g;

// Friendly labels for known platform tab ids (App.tsx switches on activeTab).
// Unknown ids fall back to the raw id.
const TAB_LABELS: Record<string, string> = {
  'member-fields': 'Field Configuration',
  'my-forms': 'Forms',
  members: 'Members',
};

// Navigate the tab-state-driven platform SPA to `tabId` via the same
// `changeTab` CustomEvent App.tsx listens for (detail === the tab-id string).
function navigateToTab(tabId: string) {
  window.dispatchEvent(new CustomEvent('changeTab', { detail: tabId }));
}

// An in-app tab deep-link rendered as a small pill button. Styled like the
// external links (accent ink) but clearly a button since it navigates in-app.
function TabLink({ tabId }: { tabId: string }) {
  const label = TAB_LABELS[tabId] || tabId;
  return (
    <button
      type="button"
      onClick={() => navigateToTab(tabId)}
      title={`Open ${label}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 8px',
        margin: '0 1px',
        borderRadius: 999,
        border: `1px solid ${tvTokens.border}`,
        background: tvTokens.accentSoft,
        color: tvTokens.accentInk,
        font: 'inherit',
        fontSize: 12,
        lineHeight: 1.4,
        cursor: 'pointer',
        verticalAlign: 'baseline',
      }}
    >
      Open {label} →
    </button>
  );
}

function renderTextWithLinks(text: string): React.ReactNode {
  if (!text) return text;
  const linkStyle: React.CSSProperties = { color: tvTokens.accentInk, textDecoration: 'underline', wordBreak: 'break-word' };
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  URL_OR_PATH_RE.lastIndex = 0;
  let key = 0;
  while ((m = URL_OR_PATH_RE.exec(text)) !== null) {
    const token = m[0];
    if (m.index > last) out.push(text.slice(last, m.index));
    if (token.startsWith('tab:')) {
      // In-app deep link → nav pill (no <a>; dispatches changeTab).
      out.push(<TabLink key={`l${key++}`} tabId={token.slice(4)} />);
    } else if (token.startsWith('http')) {
      out.push(
        <a key={`l${key++}`} href={token} target="_blank" rel="noopener noreferrer" style={linkStyle}>
          {token}
        </a>
      );
    } else {
      // App-relative path → same-tab, in-app navigation.
      out.push(
        <a key={`l${key++}`} href={token} style={linkStyle}>
          {token}
        </a>
      );
    }
    last = m.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  // If nothing matched, return the raw string so callers stay lightweight.
  return out.length === 0 ? text : <>{out}</>;
}

// Responsive helper — true on phone-width viewports (≤640px). SSR-safe (guards
// `window`) and updates live via a matchMedia listener. Drives the structural
// (JS) layout switches; simple flex-wrap / auto-fit tweaks are done in CSS.
function useIsMobile(): boolean {
  const query = '(max-width: 640px)';
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    // addEventListener is the modern API; older Safari only has addListener.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

// ---- Cron: friendly presets + human-readable rendering ----
const CRON_PRESETS: { label: string; value: string }[] = [
  { label: 'Every 15 minutes', value: '*/15 * * * *' },
  { label: 'Every 30 minutes', value: '*/30 * * * *' },
  { label: 'Hourly', value: '0 * * * *' },
  { label: 'Every 2 hours', value: '0 */2 * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Daily at 09:00', value: '0 9 * * *' },
  { label: 'Daily at 13:00', value: '0 13 * * *' },
  { label: 'Weekly · Mon 09:00', value: '0 9 * * 1' },
];

function humanizeCron(expr?: string): string {
  if (!expr) return '';
  const p = expr.trim().split(/\s+/);
  if (p.length !== 5) return expr;
  const [mi, ho, dom, , dow] = p;
  const preset = CRON_PRESETS.find((x) => x.value === expr);
  if (preset) return preset.label;
  const everyN = (f: string) => { const m = /^\*\/(\d+)$/.exec(f); return m ? +m[1] : null; };
  if (everyN(mi) && ho === '*') return `Every ${everyN(mi)} min`;
  if (/^\d+$/.test(mi) && ho === '*') return 'Hourly';
  const hn = everyN(ho);
  if (hn && /^\d+$/.test(mi)) return `Every ${hn}h`;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  if (/^\d+$/.test(mi) && /^\d+$/.test(ho) && /^\d+$/.test(dow)) {
    return `Weekly · ${days[+dow] || dow} ${ho.padStart(2, '0')}:${mi.padStart(2, '0')}`;
  }
  if (/^\d+$/.test(mi) && /^\d+$/.test(ho) && dom === '*') {
    return `Daily at ${ho.padStart(2, '0')}:${mi.padStart(2, '0')}`;
  }
  return expr;
}

// A frequency picker: presets + an "advanced" raw cron escape hatch.
function CronPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const isPreset = CRON_PRESETS.some((p) => p.value === value);
  const [advanced, setAdvanced] = useState(!isPreset);
  const sel: React.CSSProperties = { width: '100%', height: 38, padding: '0 11px', borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, background: tvTokens.paper, color: tvTokens.ink, fontSize: 14, outline: 'none' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {!advanced ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={sel}>
          {!isPreset && <option value={value}>{humanizeCron(value)}</option>}
          {CRON_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="0 13 * * *" style={{ ...sel, fontFamily: tvTokens.fontMono, fontSize: 13 }} />
      )}
      <button
        type="button"
        onClick={() => setAdvanced((a) => !a)}
        style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: tvTokens.accentInk }}
      >
        {advanced ? '‹ Use a preset' : 'Advanced (cron) ›'}
      </button>
    </div>
  );
}

// Which agents "Run now" will accept is decided by the SERVER (can_run_now on
// each row, mirroring _resolve_target). This page used to keep its own copy of
// that list, and the copy had drifted: 5 keys here against 7 on the server, so
// ops.observability and ops.ticket_poller were runnable with no button, and the
// two RAG agents shipped with a working endpoint and no way to reach it. A list
// maintained in two places is a list that is wrong in one of them.

interface Agent {
  key: string;
  display_name: string;
  description?: string;
  product: string;
  kind: string;
  trigger_type: string;
  schedule_ref?: string;
  model?: string;
  gate_policy: string;
  budget_usd?: number | null;
  org_scope: string;
  task_category?: string;
  status: string;
  cron_expr?: string;
  schedule_editable?: boolean;
  /** Server's verdict on whether POST /agents/{key}/run will be honoured.
   *  Optional so an older API simply shows no Run button rather than throwing. */
  can_run_now?: boolean;
  has_policy?: boolean;
  policy_version?: number | null;
  policy_updated_at?: string | null;
  voice_mode?: string; // none | command | dialogue
}
interface Run {
  id: number;
  task_type: string;
  task_name: string;
  model: string;
  status: string;
  total_tokens: number;
  cost_usd: number;
  duration_ms: number | null;
  execution_mode: string;
  error?: string | null;
  at?: string | null;
}
interface Summary {
  runs_24h: number;
  failed_24h: number;
  cost_24h: number;
  gated: number;
  gated_nba: number;
  gated_rca: number;
  queued_rca?: number;
  can_control?: boolean;
}
interface GatedItem {
  id: string;
  agent: string;
  title: string;
  detail?: string;
  created_at?: string;
  // RCA-kind items carry richer review context (guard every field — other
  // kinds like NBA drafts / gated output won't have them).
  kind?: string;
  verdict?: string | null;
  summary?: string | null;
  branch?: string | null;
  pr_url?: string | null;
  priority?: string | null;
  source?: string | null;
  ticket?: string | null;
  // Human ticket identity. `ticket` is the pipeline's external key — a Jira key
  // for Jira-sourced work, but an opaque UUID for a support-widget ticket. These
  // two carry the number the reporter was given and the Jira issue it mirrors to.
  ticket_number?: string | null;
  jira_key?: string | null;
  // For BUG/GAP fixes this is the actual unified diff; for FEATURE items a
  // proposed design. Preformatted text; may be null.
  proposed_changes?: string | null;
  // Accumulated context a reviewer previously added when asking for a re-analysis.
  reviewer_context?: string | null;
  // When held, the question a reviewer posed to the ticket creator (via Jira).
  held_question?: string | null;
  // When held: audience — 'admin' (awaiting admin review/approval, e.g. works-as-designed)
  // or 'creator' (awaiting the reporter). NULL on pre-migration rows.
  held_for?: string | null;
  // The creator round trip on a held ticket: the question a human released to the reporter,
  // and their answer once it came back (from a Jira comment or recorded by an admin).
  creator_ask_question?: string | null;
  creator_asked_at?: string | null;
  creator_reply?: string | null;
  creator_replied_at?: string | null;
  // Non-null while the ticket waits on the creator: the admin/product decision PARKED behind
  // the question, restored with the answer attached instead of the ticket being re-triaged.
  held_for_before_ask?: string | null;
}

// The triage log — every ticket the RCA pipeline has touched, not just the
// awaiting_review gate. GET /agents/rca/log. Read-only; actions live in the gate.
// GET /agents/rca/coverage — Jira project issues vs. tickets in the pipeline.
interface RcaCoverage {
  project: string;
  jira_total: number;
  jira_by_type: Record<string, number>;
  rca_total: number;
  rca_by_source: Record<string, number>;
  jira_tracked: number;
  jira_missing: number;
  jira_missing_by_type: Record<string, number>;
  missing_created_before_first_job: number;
  first_job_enqueued_at: string | null;
  missing: Array<{ key: string; type: string; created: string | null; status: string | null }>;
}

interface RcaLogItem {
  id: string;
  source?: string | null; // jira | cs_ticket | observability
  ticket?: string | null; // external key, e.g. TRUS-129 (or a cs_ticket UUID)
  // Human ticket identity for widget-sourced jobs: the TKT- number the reporter
  // sees, and the Jira issue this ticket is mirrored to.
  ticket_number?: string | null;
  jira_key?: string | null;
  // Org that hit the issue — name resolved server-side from public.cbo so the
  // console shows "Somos Mayfair", not just "cbo 961" (backend rca_log).
  organization?: string | null;
  cbo_id?: number | null;
  status: string; // pending | claimed | awaiting_review | skipped | done | failed
  verdict?: string | null;
  title?: string | null;
  priority?: string | null; // Jira priority name, e.g. "Highest"
  // Urgency rank of `priority` from the server (lower = more urgent; 6 = none/unknown) and
  // the Jira due date ('YYYY-MM-DD'). Both drive the default "most urgent first" order.
  priority_rank?: number | null;
  due_date?: string | null;
  summary?: string | null; // full analysis; may be long/null
  proposed_changes?: string | null; // diff or design; may be long/null
  branch?: string | null;
  pr_url?: string | null;
  ticket_created_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  // Accumulated context a reviewer previously added when asking for a re-analysis.
  reviewer_context?: string | null;
  // Lifecycle signals from the review action the pipeline recorded.
  build_requested?: boolean | null; // reviewer approved a FEATURE → re-queued to build
  reviewed_at?: string | null; // ISO when a reviewer approved (status='reviewed')
  // When held, the question a reviewer posed to the ticket creator (via Jira).
  held_question?: string | null;
  // When held: audience — 'admin' (awaiting admin review/approval, e.g. works-as-designed)
  // or 'creator' (awaiting the reporter). NULL on pre-migration rows.
  held_for?: string | null;
  // The creator round trip on a held ticket: the question a human released to the reporter,
  // and their answer once it came back (from a Jira comment or recorded by an admin).
  creator_ask_question?: string | null;
  creator_asked_at?: string | null;
  creator_reply?: string | null;
  creator_replied_at?: string | null;
  // Non-null while the ticket waits on the creator: the admin/product decision PARKED behind
  // the question, restored with the answer attached instead of the ticket being re-triaged.
  held_for_before_ask?: string | null;
  // Pre-development intake review: required for tickets enqueued under the gate; reviewed_at is
  // when a super admin approved the analysis for implementation.
  intake_required?: boolean;
  intake_reviewed_at?: string | null;
  intake_reviewed_by?: string | null;
  // TRUS-611: the analysis in plain language for non-technical reviewers. 'agent' = written
  // with the analysis; 'generated' = produced on demand from it. NULL until one exists.
  business_summary?: string | null;
  business_summary_source?: string | null;
  business_summary_at?: string | null;
  // Timeline stamps: when the agent claimed (analyzed) + drafted the ticket.
  claimed_at?: string | null;
  drafted_at?: string | null;
  // Per-part progress for a multi-part OPERATIONAL ticket (e.g. TRUS-192).
  parts?: Array<{ n?: number; title?: string; tool?: string; status?: string; detail?: string }> | null;
  // Jira labels (e.g. 'razingthebar', 'IFR') — powers the label filter.
  labels?: string[] | null;
  // TRUS-687 provenance & people (absent on an older backend): where the ticket came from,
  // who reported it, who it's assigned to, and its Jira issue type / status category.
  origin?: string | null;
  reporter?: RcaPerson | null;
  assignee?: RcaPerson | null;
  issue_type?: string | null;
  jira_status_category?: string | null;
  has_pr?: boolean | null;
}
// A status→count map keyed by the pipeline's status vocabulary. Server sends an
// entry for EVERY status present (incl 'reviewed'), so treat it as open-ended
// rather than a fixed shape.
type RcaCounts = Record<string, number | undefined>;

const JIRA_BROWSE_BASE = 'https://psyssurance.atlassian.net/browse';

/**
 * How a job's ticket identity should read in the console.
 *
 * `ticket` is the pipeline's external key: a Jira key for Jira-sourced work, but
 * an opaque UUID for one raised from the support widget — which made a widget
 * ticket impossible to recognise here, or to tie to the TKT- number the reporter
 * quotes and the TRUS- issue triage works in. Show the human number first and the
 * Jira key alongside it.
 */
function ticketIdentity(item: { source?: string | null; ticket?: string | null; ticket_number?: string | null; jira_key?: string | null }) {
  const isUuid = !!item.ticket && /^[0-9a-f-]{36}$/i.test(item.ticket);
  const primary = item.ticket_number || (isUuid ? null : item.ticket) || item.ticket || null;
  const jira = item.jira_key && item.jira_key !== primary ? item.jira_key : null;
  return {
    primary,
    jira,
    jiraUrl: jira ? `${JIRA_BROWSE_BASE}/${jira}` : null,
    // Keep the raw key reachable on hover — it's what the agent APIs are keyed by.
    title: [primary, jira, item.ticket].filter(Boolean).join(' · '),
  };
}

// Status → tone, reusing the palette already on this page (gate blue, amber,
// green, red, muted). Keyed by the pipeline's status vocabulary.
function statusTone(status?: string | null): { color: string; background: string } {
  switch ((status || '').toLowerCase()) {
    case 'pending':
      return { color: '#46739e', background: '#e6eef6' };
    case 'claimed':
      return { color: '#46739e', background: '#e6eef6' };
    case 'awaiting_review':
      return { color: '#5a4fcf', background: '#efedfb' };
    case 'reviewed':
    case 'done':
      return { color: '#12855f', background: '#e3f3ec' };
    case 'failed':
      return { color: '#cc3b57', background: '#fbe6ea' };
    case 'held':
      return { color: '#b9781f', background: '#f8efda' };
    case 'skipped':
    default:
      return { color: tvTokens.ink500, background: tvTokens.paper2 };
  }
}

// Raw pipeline status → reviewer-friendly label for the status-filter chips.
const STATUS_LABEL: Record<string, string> = {
  pending: 'Queued',
  claimed: 'In progress',
  awaiting_review: 'Awaiting review',
  reviewed: 'Approved for implementation',
  skipped: 'Auto-resolved',
  done: 'Done',
  failed: 'Failed',
  held: 'Held — needs review',
  product_approved: 'Product-approved',
};
const statusLabel = (status: string): string => STATUS_LABEL[status] || status;

// Preferred display order for the status chips; any status not listed (unknown
// future value) sorts to the end but is still shown.
const STATUS_ORDER = ['awaiting_review', 'pending', 'claimed', 'reviewed', 'skipped', 'done', 'failed'];
const statusRank = (status: string): number => {
  const i = STATUS_ORDER.indexOf(status);
  return i === -1 ? STATUS_ORDER.length : i;
};

// The monospace, diff-colored block used for a fix's proposed_changes. Factored
// out so both the gate queue and the triage log render it identically.
function ProposedChangesBlock({ proposed }: { proposed: string }) {
  return (
    <div
      style={{
        marginTop: 6,
        overflowX: 'auto',
        background: tvTokens.paper2,
        border: `1px solid ${tvTokens.border}`,
        borderRadius: tvTokens.rSm,
        padding: '8px 10px',
      }}
    >
      <pre
        style={{
          margin: 0,
          fontFamily: tvTokens.fontMono,
          fontSize: 11,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          color: tvTokens.ink500,
        }}
      >
        {proposed.split('\n').map((line, i) => {
          const isHeader = line.startsWith('+++') || line.startsWith('---');
          const color = isHeader
            ? tvTokens.ink400
            : line.startsWith('+')
              ? '#0f7d70'
              : line.startsWith('-')
                ? '#cc3b57'
                : tvTokens.ink500;
          return (
            <div key={i} style={{ color }}>
              {line || ' '}
            </div>
          );
        })}
      </pre>
    </div>
  );
}

// ---- Ticket context (original description + screenshots) ----
// GET /agents/rca/{id}/context. Every field is guarded — Jira may be
// unconfigured (jira_configured===false → browse-only), and attachments may
// be empty.
interface TicketAttachment {
  id: string;
  filename?: string | null;
  mime?: string | null;
  size?: number | null;
  is_image?: boolean;
  url?: string | null; // absolute API path; we fetch by id to avoid double-prefix
}
interface TicketPerson {
  name?: string | null;
  email?: string | null;
  account_id?: string | null;
}
interface TicketComment {
  id?: string | null;
  author?: string | null;
  author_email?: string | null;
  created?: string | null;
  body?: string | null;
  is_agent?: boolean;
}
interface TicketContext {
  source?: string | null;
  ticket?: string | null;
  // Widget-sourced tickets: the TKT- number the reporter quotes, the Jira issue
  // it mirrors to, and the screen the widget captured at report time.
  ticket_number?: string | null;
  jira_key?: string | null;
  screen?: string | null;
  page_url?: string | null;
  datadog_session_url?: string | null;
  browse_url?: string | null;
  title?: string | null;
  description?: string | null;
  attachments?: TicketAttachment[];
  reporter?: TicketPerson | null;
  assignee?: TicketPerson | null;
  comments?: TicketComment[];
  responders?: string[];
  jira_configured?: boolean;
}

// A single image attachment, fetched WITH auth (Authorization + X-CBO-ID) and
// turned into an object URL — a bare <img src> would omit those headers. We
// fetch by attachment id (not att.url) because this.baseUrl already carries the
// /api/platform/v1 prefix that att.url also includes; using the full path would
// double it. The object URL is revoked on unmount/collapse to avoid leaks.
function AttachmentImage({ att }: { att: TicketAttachment }) {
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    getPlatformApi()
      .fetchObjectUrl(`/agents/rca/attachment/${encodeURIComponent(att.id)}`)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          created = url;
          setSrc(url);
          setState('ok');
        } else {
          setState('error');
        }
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [att.id]);

  if (state === 'error') {
    return att.url ? (
      <a
        href={att.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: tvTokens.accentInk, textDecoration: 'none' }}
      >
        <Paperclip size={12} /> {att.filename || 'attachment'}
      </a>
    ) : (
      <span style={{ fontSize: 12, color: tvTokens.ink400 }}>{att.filename || 'attachment'} (unavailable)</span>
    );
  }
  if (state === 'loading' || !src) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 96,
          padding: '0 12px',
          borderRadius: tvTokens.r,
          border: `1px dashed ${tvTokens.border}`,
          background: tvTokens.paper2,
          color: tvTokens.ink400,
          fontSize: 11.5,
        }}
      >
        <ImageIcon size={13} /> Loading {att.filename || 'image'}…
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={att.filename || 'ticket attachment'}
      style={{ maxWidth: '100%', borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, display: 'block' }}
    />
  );
}

// Small object-URL hook shared by the inline video/PDF players. Fetches the
// attachment blob WITH auth (via fetchObjectUrl) once mounted, and revokes the
// object URL on unmount to avoid leaks — mirrors AttachmentImage's lifecycle.
// Mount is the lazy-load trigger: these only render when the row is expanded.
function useAttachmentObjectUrl(attId: string): { state: 'loading' | 'ok' | 'error'; src: string | null } {
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    getPlatformApi()
      .fetchObjectUrl(`/agents/rca/attachment/${encodeURIComponent(attId)}`)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          created = url;
          setSrc(url);
          setState('ok');
        } else {
          setState('error');
        }
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [attId]);
  return { state, src };
}

// Inline video player for video/* attachments. Spinner while the authed blob
// loads; on failure falls back to the download chip so it degrades gracefully.
function AttachmentVideo({ att, chipStyle }: { att: TicketAttachment; chipStyle: React.CSSProperties }) {
  const { state, src } = useAttachmentObjectUrl(att.id);
  if (state === 'error' || (state === 'ok' && !src)) {
    return <AttachmentFile att={att} chipStyle={chipStyle} />;
  }
  if (state === 'loading' || !src) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 96,
          padding: '0 12px',
          borderRadius: tvTokens.r,
          border: `1px dashed ${tvTokens.border}`,
          background: tvTokens.paper2,
          color: tvTokens.ink400,
          fontSize: 11.5,
        }}
      >
        <FileVideo size={13} /> Loading {att.filename || 'video'}…
      </div>
    );
  }
  return (
    <video
      controls
      src={src}
      style={{ maxWidth: '100%', borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, display: 'block' }}
    />
  );
}

// Inline PDF preview for application/pdf attachments via an <iframe> on the
// authed object URL, plus a Download fallback link. Spinner while loading; on
// failure falls back to the download chip.
function AttachmentPdf({ att, chipStyle }: { att: TicketAttachment; chipStyle: React.CSSProperties }) {
  const { state, src } = useAttachmentObjectUrl(att.id);
  if (state === 'error' || (state === 'ok' && !src)) {
    return <AttachmentFile att={att} chipStyle={chipStyle} />;
  }
  if (state === 'loading' || !src) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 96,
          padding: '0 12px',
          borderRadius: tvTokens.r,
          border: `1px dashed ${tvTokens.border}`,
          background: tvTokens.paper2,
          color: tvTokens.ink400,
          fontSize: 11.5,
        }}
      >
        <FileText size={13} /> Loading {att.filename || 'PDF'}…
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <iframe
        src={src}
        title={att.filename || 'PDF preview'}
        style={{ width: '100%', height: 'clamp(300px, 60vh, 600px)', border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r, background: tvTokens.paper2 }}
      />
      <a
        href={src}
        download={att.filename || 'document.pdf'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: tvTokens.accentInk, textDecoration: 'none' }}
      >
        <Download size={12} /> Download {att.filename || 'PDF'}
      </a>
    </div>
  );
}

// A single NON-image attachment (e.g. .xlsx, .pdf) rendered as a clickable
// download chip. Clicking fetches the blob WITH auth (same authed path used for
// images — a bare <a href={att.url} download> would omit Authorization/X-CBO-ID
// and 401), then triggers a browser download named after the attachment's
// filename. Shows a spinner while fetching; on failure falls back to the plain
// external link (att.url) or an "unavailable" note so it degrades gracefully.
function AttachmentFile({ att, chipStyle }: { att: TicketAttachment; chipStyle: React.CSSProperties }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const download = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    let objectUrl: string | null = null;
    try {
      objectUrl = await getPlatformApi().fetchObjectUrl(`/agents/rca/attachment/${encodeURIComponent(att.id)}`);
      if (!objectUrl) {
        setFailed(true);
        return;
      }
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      setFailed(true);
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setBusy(false);
    }
  };

  // On failure, degrade to the existing chip behavior (external link if we have
  // a url, else an unavailable note).
  if (failed) {
    return att.url ? (
      <a
        href={att.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ ...chipStyle, display: 'inline-flex', alignItems: 'center', gap: 4, color: tvTokens.ink500, textDecoration: 'none' }}
      >
        <Paperclip size={11} /> {att.filename || 'file'}
      </a>
    ) : (
      <span style={{ ...chipStyle, display: 'inline-flex', alignItems: 'center', gap: 4, color: tvTokens.ink400 }}>
        <Paperclip size={11} /> {att.filename || 'file'} (unavailable)
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      title={`Download ${att.filename || 'file'}`}
      style={{
        ...chipStyle,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: tvTokens.accentInk,
        background: tvTokens.accentSoft,
        border: 'none',
        cursor: busy ? 'wait' : 'pointer',
        font: 'inherit',
      }}
    >
      {busy ? (
        <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
      ) : (
        <Download size={11} />
      )}
      {att.filename || 'file'}
    </button>
  );
}

// The "Ticket context" body — original description + inline screenshots + a
// "View in Jira" link. Rendered only when a row is expanded (lazy). The context
// payload is fetched/cached by the parent and passed in; this component owns the
// per-image object URLs (via AttachmentImage), which unmount-revoke on collapse.
function ReplyComposer({ ticketId, onReplied }: { ticketId: string; onReplied?: () => void }) {
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setPosting(true);
    setErr(null);
    try {
      const res: any = await agentsApi.replyRca(ticketId, t);
      const data = res?.data ?? res;
      if (data?.posted || res?.success !== false) {
        setText('');
        onReplied?.();
      } else {
        setErr(res?.error?.message || 'Could not post the reply.');
      }
    } catch (e: any) {
      setErr(e?.message || 'Could not post the reply.');
    } finally {
      setPosting(false);
    }
  };
  return (
    <div style={{ marginTop: 10 }}>
      <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
        Reply to reporter (posts to Jira)
      </span>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Write a reply — it posts as a Jira comment and notifies the reporter/assignee/watchers."
        style={{ width: '100%', marginTop: 6, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, resize: 'vertical', fontFamily: 'inherit' }}
      />
      {err && <div style={{ fontSize: 11.5, color: '#cc3b57', marginTop: 4 }}>{err}</div>}
      <button
        onClick={send}
        disabled={posting || !text.trim()}
        style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: tvTokens.accentInk, border: 'none', borderRadius: tvTokens.rSm, cursor: posting || !text.trim() ? 'default' : 'pointer', opacity: posting || !text.trim() ? 0.6 : 1 }}
      >
        {posting ? 'Posting…' : 'Reply on Jira'}
      </button>
    </div>
  );
}

// "Respond to creator & close" — the admin AGREES a held ticket is working as designed:
// one message that both replies to the creator on Jira (explaining the design) AND closes
// the ticket + Jira issue. Two API calls behind one action so the admin doesn't have to
// reply, then separately hunt for a close button. Collapsed by default.
function RespondCloseControl({ ticketId, onDone }: { ticketId: string; onDone?: () => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    setErr(null);
    try {
      await agentsApi.replyRca(ticketId, t); // posts the explanation to the creator (Jira)
      await agentsApi.rcaResolve(ticketId, t); // closes the ticket + transitions Jira to Done
      setText('');
      setOpen(false);
      onDone?.();
    } catch (e: any) {
      setErr(e?.message || 'Could not respond & close.');
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ marginTop: 8, marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: tvTokens.ink600, background: 'transparent', border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, cursor: 'pointer' }}
        title="Agree it's as designed: reply to the creator explaining the design and close the ticket"
      >
        <Check size={13} /> Respond to creator &amp; close
      </button>
    );
  }
  return (
    <div style={{ marginTop: 10 }}>
      <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
        Respond to creator &amp; close (posts to Jira, then closes)
      </span>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Explain to the creator why this works as designed — it posts as a Jira comment (notifying the reporter) and then closes the ticket + Jira issue."
        style={{ width: '100%', marginTop: 6, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, resize: 'vertical', fontFamily: 'inherit' }}
      />
      {err && <div style={{ fontSize: 11.5, color: '#cc3b57', marginTop: 4 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button
          onClick={submit}
          disabled={busy || !text.trim()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: tvTokens.accentInk, border: 'none', borderRadius: tvTokens.rSm, cursor: busy || !text.trim() ? 'default' : 'pointer', opacity: busy || !text.trim() ? 0.6 : 1 }}
        >
          {busy ? 'Sending…' : 'Respond & close'}
        </button>
        <button
          onClick={() => { setOpen(false); setErr(null); }}
          disabled={busy}
          style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, color: tvTokens.ink500, background: 'transparent', border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Release a question to the person who filed the ticket — the ONE door to a creator.
//
// Two situations arrive here and both end in the same call (POST /agents/rca/{id}/hold, the
// only path that actually contacts a creator):
//
// 1. held_for='creator_draft' — the AGENT drafted the question. Nothing has reached them: an
//    agent may not speak to a customer on its own, because an unreviewed robot question on a
//    support-widget ticket reads as the product breaking twice (TRUS-369). The admin edits the
//    wording and sends.
// 2. held_for='admin' | 'product' — the agent analyzed the ticket correctly and parked a
//    DECISION, writing the open questions the reporter has to answer into the hold note. The
//    console used to offer nothing but "Unblock & proceed" or "Respond to creator & close",
//    so those questions had no way out (TRUS-370). Releasing them from here keeps the parked
//    decision (`return_to: 'restore'`): when the answer lands, the admin gets their decision
//    back with the answer attached rather than a from-scratch re-triage.
//
// `holdNote` is the analysis/decision text of the current hold — offered as a starting point
// so an admin can trim it into a customer-readable question instead of retyping it, but never
// sent as-is on an admin/product hold: that text is written for us, not for them.
function AskCreatorControl({
  ticketId,
  audience,
  holdNote,
  prefill,
  onDone,
}: {
  ticketId: string;
  audience: string;
  holdNote: string;
  // A question the agent proposed inside a decision note (intake review) — prefilled like a
  // draft, but the decision stays parked behind it.
  prefill?: string;
  onDone?: () => void;
}) {
  const isDraft = audience === 'creator_draft';
  const alreadyAsked = audience === 'creator';
  const isIntake = audience === 'intake';
  const [open, setOpen] = useState(false);
  // An agent draft IS the question, so it prefills. A decision note is not — start empty and
  // let the admin pull the note in deliberately.
  const [text, setText] = useState(isDraft ? holdNote : prefill || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Keep the editor in step with the draft until the admin starts editing.
  useEffect(() => { if (!open && isDraft) setText(holdNote); }, [holdNote, open, isDraft]);

  const submit = async () => {
    const q = text.trim();
    if (!q) return;
    setBusy(true);
    setErr(null);
    try {
      // 'restore' = keep whatever decision this question is being released from, so the
      // answer comes back INTO it. The backend infers "nothing to restore" for a draft hold.
      await agentsApi.holdRca(ticketId, q, 'restore');
      setOpen(false);
      if (!isDraft) setText('');
      onDone?.();
    } catch (e: any) {
      setErr(e?.message || 'Could not send that to the creator.');
    } finally {
      setBusy(false);
    }
  };

  const buttonLabel = isDraft
    ? 'Review & send to creator'
    : alreadyAsked
      ? 'Ask a follow-up'
      : isIntake
        ? 'Send to creator for verification'
        : 'Ask the creator';

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: isDraft ? '#fff' : tvTokens.ink500, background: isDraft ? tvTokens.accentInk : '#fff', border: isDraft ? 'none' : `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, cursor: 'pointer' }}
        title={
          isDraft
            ? "Review the agent's proposed question, edit the wording, and send it to the person who filed this ticket"
            : 'Send the open questions to the person who filed this ticket — this decision stays parked and comes back with their answer'
        }
      >
        <Send size={13} /> {buttonLabel}
      </button>
    );
  }
  return (
    <div style={{ marginTop: 10 }}>
      <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
        {isDraft ? 'Send to creator — edit before sending' : 'Ask the creator — your words, not the agent’s'}
      </span>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="The question the creator will receive. Write it as a person, not as the agent."
        style={{ width: '100%', marginTop: 6, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, resize: 'vertical', fontFamily: 'inherit' }}
      />
      {!isDraft && holdNote.trim() && (
        <button
          onClick={() => setText(holdNote)}
          style={{ marginTop: 4, background: 'none', border: 'none', padding: 0, fontSize: 11, color: tvTokens.accentInk, cursor: 'pointer' }}
          title="Copy the analysis note into the box so you can trim it into a question"
        >
          Start from the note above
        </button>
      )}
      <div style={{ fontSize: 11, color: tvTokens.ink400, marginTop: 4 }}>
        Posts as a comment on the ticket, flags it <code>awaiting-creator</code>, moves it back to To Do and notifies the reporter.
        {!isDraft && !alreadyAsked && ' This decision stays parked and returns to you with their answer.'}
      </div>
      {err && <div style={{ fontSize: 11.5, color: '#cc3b57', marginTop: 4 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button
          onClick={submit}
          disabled={busy || !text.trim()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: tvTokens.accentInk, border: 'none', borderRadius: tvTokens.rSm, cursor: busy || !text.trim() ? 'default' : 'pointer', opacity: busy || !text.trim() ? 0.6 : 1 }}
        >
          {busy ? 'Sending…' : 'Send to creator'}
        </button>
        <button
          onClick={() => { setOpen(false); setErr(null); setText(isDraft ? holdNote : prefill || ''); }}
          disabled={busy}
          style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, color: tvTokens.ink500, background: 'transparent', border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Pre-development INTAKE REVIEW — the first decision on a new ticket. The agent has analysed
// it and written a plan but built nothing; a super admin decides what happens next. Every
// path out of intake is here, so the reviewer never has to hunt for it:
//   - Approve for implementation → the agent builds it (or the service executor runs it, or an
//     already-drafted PR goes to code review). Nothing is deployed without the later
//     "Approve for deploy" + Merge on the PR.
//   - Send to the creator for verification → their answer comes back to intake.
//   - Ask the agent to refine / add requirements / split / create a follow-on ticket →
//     re-analysed with the instruction; the result comes back to intake.
//   - Close with a response to the creator.
// INTAKE_AGENT_ASKS (incl. the TRUS-618 open-ended ask) and isReanalyzableStatus live in a
// pure, UI-free module so they can be unit-tested without rendering this page.

// TRUS-611: an icon-only row action. The label is the accessible name AND the tooltip, so
// the action is still announced by screen readers and explained on hover; `hint` adds the
// longer "what happens" sentence to the tooltip only.
function IconAction({
  icon,
  label,
  hint,
  onClick,
  disabled,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  onClick: () => void;
  disabled?: boolean;
  tone: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={disabled}
      aria-label={label}
      title={hint ? `${label} — ${hint}` : label}
      style={{ ...tone, gap: 0, padding: 0, width: 28, height: 26, justifyContent: 'center', opacity: disabled ? 0.55 : 1 }}
    >
      {icon}
    </button>
  );
}

// The four plain-language headings the agent / generator writes (TRUS-611).
const BUSINESS_HEADINGS = ["What's wrong", 'What we found', 'What we recommend', 'What it affects'];

// TRUS-611: the ticket in plain language, for reviewers who are not engineers. Shown first in
// the expanded row. When the agent didn't write one (tickets analysed before this shipped),
// a control user can generate it from the stored analysis; it is cached on the ticket.
function BusinessSummaryPanel({
  item,
  canGenerate,
  onSaved,
}: {
  item: RcaLogItem;
  canGenerate: boolean;
  onSaved: () => void;
}) {
  const [text, setText] = useState<string | null>(item.business_summary || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setText(item.business_summary || null); }, [item.business_summary]);
  const hasAnalysis = !!((item.summary || '').trim() || (item.proposed_changes || '').trim());

  const generate = async (regenerate: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      const res: any = await agentsApi.rcaBusinessSummary(item.id, regenerate);
      if (res && res.success === false) throw new Error(res.error?.message || 'Could not write the summary.');
      const out = (res?.data ?? res)?.business_summary;
      if (out) setText(out);
      onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Could not write the summary.');
    } finally {
      setBusy(false);
    }
  };

  const label: React.CSSProperties = { fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: '#12855f' };
  const lines = (text || '').split('\n').map((l) => l.trim()).filter(Boolean);

  return (
    <div style={{ marginBottom: 10, padding: '10px 12px', border: '1px solid #cfe8dc', background: '#f3faf6', borderRadius: tvTokens.r }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={label}>In plain language</span>
        {item.business_summary_source === 'generated' && (
          <span style={{ fontSize: 10.5, color: tvTokens.ink400 }}>· generated from the analysis</span>
        )}
        {canGenerate && text && (
          <span style={{ marginLeft: 'auto' }}>
            <IconAction
              icon={busy ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={13} />}
              label="Rewrite summary"
              hint="regenerate the plain-language summary from the current analysis"
              onClick={() => generate(true)}
              disabled={busy}
              tone={btnMerge}
            />
          </span>
        )}
      </div>
      {text ? (
        <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.55, color: tvTokens.ink700 }}>
          {lines.map((l, i) => {
            const isHeading = BUSINESS_HEADINGS.some((h) => l.replace(/[:*#]/g, '').trim().toLowerCase() === h.toLowerCase());
            return isHeading ? (
              <div key={i} style={{ fontWeight: 700, color: tvTokens.ink, marginTop: i === 0 ? 0 : 8 }}>{l.replace(/[:*#]/g, '').trim()}</div>
            ) : (
              <div key={i}>{renderTextWithLinks(l)}</div>
            );
          })}
        </div>
      ) : (
        <div style={{ marginTop: 6, fontSize: 12.5, color: tvTokens.ink500 }}>
          {!hasAnalysis
            ? 'The agent has not analysed this ticket yet.'
            : canGenerate
              ? 'No plain-language summary yet.'
              : 'No plain-language summary yet — a super admin can generate one.'}
          {canGenerate && hasAnalysis && (
            <button
              type="button"
              onClick={() => generate(false)}
              disabled={busy}
              style={{ ...btnApprove, marginLeft: 8, opacity: busy ? 0.6 : 1 }}
              title="Write a short, non-technical explanation of the issue, findings and recommendation from the agent's analysis"
            >
              {busy ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={13} />}
              {busy ? 'Writing…' : 'Explain in plain language'}
            </button>
          )}
        </div>
      )}
      {err && <div style={{ marginTop: 6, fontSize: 12, color: '#cc3b57' }}>{err}</div>}
    </div>
  );
}

function IntakeReviewPanel({
  item,
  onDone,
  flash,
}: {
  item: RcaLogItem;
  onDone: () => void;
  flash: (msg: string) => void;
}) {
  const vu = (item.verdict || '').toUpperCase();
  const noWork = ['WORKS-AS-DESIGNED', 'INVALID', 'DUPLICATE'].includes(vu);
  const [approveOpen, setApproveOpen] = useState(false);
  const [note, setNote] = useState('');
  const [ask, setAsk] = useState<string | null>(null);
  const [askText, setAskText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The agent's proposed question for the reporter, if it wrote one, rides inside the
  // intake note — offer it as the starting text for the verification ask.
  const proposedQ = ((item.held_question || '').split('Proposed question for the creator (not sent):')[1] || '').trim();

  const approve = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res: any = await agentsApi.rcaIntakeApprove(item.id, note.trim() || undefined);
      if (res && res.success === false) throw new Error(res.error?.message || 'Could not approve.');
      const st = (res?.data ?? res)?.status;
      flash(
        st === 'code_review'
          ? 'Approved — the drafted PR is now in code review'
          : st === 'queued_for_execution'
            ? 'Approved — handed to the service executor'
            : 'Approved — the agent will build it',
      );
      onDone();
    } catch (e: any) {
      setErr(e?.message || 'Could not approve.');
    } finally {
      setBusy(false);
    }
  };

  const sendAsk = async () => {
    const a = INTAKE_AGENT_ASKS.find((x) => x.key === ask);
    if (!a || !askText.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await agentsApi.reanalyzeRca(item.id, `${a.prefix}\n${askText.trim()}`, false);
      flash(`${a.label} — sent to the agent; it comes back here for review`);
      setAsk(null);
      setAskText('');
      onDone();
    } catch (e: any) {
      setErr(e?.message || 'Could not send that to the agent.');
    } finally {
      setBusy(false);
    }
  };

  const label: React.CSSProperties = { fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 };
  const box: React.CSSProperties = { width: '100%', marginTop: 6, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, resize: 'vertical', fontFamily: 'inherit' };
  const primary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: tvTokens.accentInk, border: 'none', borderRadius: tvTokens.rSm, cursor: 'pointer' };
  const secondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: tvTokens.ink500, background: '#fff', border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, cursor: 'pointer' };

  return (
    <div style={{ marginTop: 10, padding: '10px 12px', border: `1px solid #d9d4f5`, background: '#f7f6fd', borderRadius: tvTokens.r }}>
      <div style={{ ...label, color: '#5a4fcf' }}>Pre-development review — nothing has been built</div>
      <div style={{ fontSize: 11.5, color: tvTokens.ink500, marginTop: 3 }}>
        Read the plain-language summary and the ticket above (technical detail is below it), then choose what happens next.
      </div>

      <div style={{ marginTop: 8 }}>
        <div style={label}>1 · Decide</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4, alignItems: 'flex-start' }}>
          <button
            onClick={() => setApproveOpen((o) => !o)}
            disabled={busy || noWork}
            style={{ ...btnApprove, opacity: noWork ? 0.5 : 1, cursor: noWork ? 'not-allowed' : 'pointer' }}
            title={
              noWork
                ? `Nothing to implement for a ${item.verdict} ticket — close it with a response, or reclassify it first`
                : item.pr_url
                  ? 'Approve the analysis — the drafted PR moves to code review (approve for deploy + merge next)'
                  : 'Approve the analysis for implementation — the agent builds it next. Nothing deploys until you approve the PR for deploy and merge it.'
            }
          >
            <Check size={13} /> Approve for implementation
          </button>
          <RespondCloseControl ticketId={item.id} onDone={() => { flash('Responded to creator & closed.'); onDone(); }} />
        </div>
        {approveOpen && !noWork && (
          <div style={{ marginTop: 6 }}>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Optional note for the building agent — scope decisions, constraints, what not to change."
              style={box}
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button onClick={approve} disabled={busy} style={{ ...primary, opacity: busy ? 0.6 : 1 }}>
                {busy ? 'Approving…' : item.pr_url ? 'Approve → code review' : 'Approve → agent builds it'}
              </button>
              <button onClick={() => setApproveOpen(false)} disabled={busy} style={secondary}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={label}>2 · Verify with the creator</div>
        <AskCreatorControl
          ticketId={item.id}
          audience="intake"
          holdNote={item.held_question || ''}
          prefill={proposedQ}
          onDone={() => { flash('Sent to the creator — comes back to intake with their answer.'); onDone(); }}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={label}>3 · Ask the agent</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {INTAKE_AGENT_ASKS.map((a) => (
            <button
              key={a.key}
              onClick={() => { setAsk(ask === a.key ? null : a.key); setErr(null); }}
              disabled={busy}
              style={ask === a.key ? { ...secondary, borderColor: tvTokens.accentInk, color: tvTokens.accentInk, background: tvTokens.accentSoft } : secondary}
            >
              <Bot size={13} /> {a.label}
            </button>
          ))}
        </div>
        {ask && (
          <div style={{ marginTop: 6 }}>
            <textarea
              value={askText}
              onChange={(e) => setAskText(e.target.value)}
              rows={4}
              placeholder={INTAKE_AGENT_ASKS.find((x) => x.key === ask)?.placeholder}
              style={box}
            />
            <div style={{ fontSize: 11, color: tvTokens.ink400, marginTop: 4 }}>
              The agent re-analyses with this instruction and builds nothing — the result comes back here for review.
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button onClick={sendAsk} disabled={busy || !askText.trim()} style={{ ...primary, opacity: busy || !askText.trim() ? 0.6 : 1 }}>
                {busy ? 'Sending…' : 'Send to agent'}
              </button>
              <button onClick={() => { setAsk(null); setAskText(''); }} disabled={busy} style={secondary}>Cancel</button>
            </div>
          </div>
        )}
      </div>
      {err && <div style={{ fontSize: 11.5, color: '#cc3b57', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// The other half of the round trip: record the creator's answer to a released question.
//
// A Jira reply is picked up automatically by the hourly dispatcher, but plenty of answers
// arrive by email, on a call, or in Slack — and until the answer is ON the ticket the parked
// decision stays parked. This records it and chooses what happens next:
//   • "Return for decision" (default) — restores the admin/product hold the question came out
//     of, with the answer appended to the note. The admin decides; nothing is re-triaged.
//   • "Re-analyze with the answer" — the answer changes the ask, so hand it to the agent for a
//     fresh triage with the answer as context.
function RecordCreatorReplyControl({
  ticketId,
  parkedFor,
  onDone,
}: {
  ticketId: string;
  parkedFor?: string | null;
  onDone?: (mode: 'restore' | 'reanalyze') => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<'restore' | 'reanalyze' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (mode: 'restore' | 'reanalyze') => {
    const reply = text.trim();
    if (!reply) return;
    setBusy(mode);
    setErr(null);
    try {
      await agentsApi.creatorReplyRca(ticketId, reply, mode);
      setOpen(false);
      setText('');
      onDone?.(mode);
    } catch (e: any) {
      setErr(e?.message || 'Could not record that answer.');
    } finally {
      setBusy(null);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: tvTokens.accentInk, border: 'none', borderRadius: tvTokens.rSm, cursor: 'pointer' }}
        title="The creator answered by email, on a call or in Slack — record their answer here (a Jira reply is picked up automatically)"
      >
        <MessageSquare size={13} /> Record the creator’s answer
      </button>
    );
  }
  const parked = (parkedFor || '').toLowerCase();
  return (
    <div style={{ marginTop: 10 }}>
      <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
        The creator’s answer
      </span>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="Paste or summarize what they said. It's saved on the ticket and every later agent run sees it."
        style={{ width: '100%', marginTop: 6, padding: '8px 10px', fontSize: 12.5, lineHeight: 1.5, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, resize: 'vertical', fontFamily: 'inherit' }}
      />
      <div style={{ fontSize: 11, color: tvTokens.ink400, marginTop: 4 }}>
        {parked === 'product'
          ? 'Returns the product decision to you with the answer attached, or hand it back to the agent to re-analyze.'
          : parked === 'admin'
            ? 'Returns the admin decision to you with the answer attached, or hand it back to the agent to re-analyze.'
            : 'Comes back for admin review with the answer attached, or hand it to the agent to re-analyze.'}
      </div>
      {err && <div style={{ fontSize: 11.5, color: '#cc3b57', marginTop: 4 }}>{err}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
        <button
          onClick={() => submit('restore')}
          disabled={!!busy || !text.trim()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: tvTokens.accentInk, border: 'none', borderRadius: tvTokens.rSm, cursor: busy || !text.trim() ? 'default' : 'pointer', opacity: busy || !text.trim() ? 0.6 : 1 }}
        >
          {busy === 'restore' ? 'Saving…' : 'Save & return for decision'}
        </button>
        <button
          onClick={() => submit('reanalyze')}
          disabled={!!busy || !text.trim()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: tvTokens.ink500, background: '#fff', border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, cursor: busy || !text.trim() ? 'default' : 'pointer', opacity: busy || !text.trim() ? 0.6 : 1 }}
          title="The answer changes the ask — re-queue the agent to triage again with it"
        >
          <Bot size={13} /> {busy === 'reanalyze' ? 'Saving…' : 'Save & re-analyze'}
        </button>
        <button
          onClick={() => { setOpen(false); setErr(null); }}
          disabled={!!busy}
          style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, color: tvTokens.ink500, background: 'transparent', border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function TicketContextBody({ entry, fallbackUrl, ticketId, canControl, onReplied }: { entry?: { loading: boolean; data: TicketContext | null; error: boolean }; fallbackUrl?: string | null; ticketId?: string; canControl?: boolean; onReplied?: () => void }) {
  if (!entry || entry.loading) {
    return <div style={{ fontSize: 12, color: tvTokens.ink400 }}>Loading context…</div>;
  }
  if (entry.error || !entry.data) {
    return (
      <div style={{ fontSize: 12, color: tvTokens.ink400 }}>
        Couldn’t load ticket context.
        {fallbackUrl && (
          <>
            {' '}
            <a href={fallbackUrl} target="_blank" rel="noopener noreferrer" style={{ color: tvTokens.accentInk, textDecoration: 'none' }}>
              View in Jira →
            </a>
          </>
        )}
      </div>
    );
  }
  const ctx = entry.data;
  const browseUrl = ctx.browse_url || fallbackUrl || null;
  const images = (ctx.attachments || []).filter((a) => a.is_image);
  const nonImages = (ctx.attachments || []).filter((a) => !a.is_image);
  const videos = nonImages.filter((a) => (a.mime || '').toLowerCase().startsWith('video/'));
  const pdfs = nonImages.filter((a) => (a.mime || '').toLowerCase() === 'application/pdf');
  // Everything else (xlsx/docx/etc.) keeps the plain download chip.
  const files = nonImages.filter((a) => !videos.includes(a) && !pdfs.includes(a));
  const desc = ctx.description;
  const chip: React.CSSProperties = { ...pillMono, fontSize: 10, padding: '1px 7px' };
  return (
    <div>
      {/* Ticket identity + where the reporter hit it. A widget ticket is keyed
          internally by UUID; show the TKT- number, the Jira issue it mirrors to,
          and the screen breadcrumb the widget captured. */}
      {(ctx.ticket_number || ctx.jira_key || ctx.screen || ctx.datadog_session_url) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          {ctx.ticket_number && (
            <span style={{ ...chip, color: tvTokens.ink500 }} title="Support ticket number">{ctx.ticket_number}</span>
          )}
          {ctx.jira_key && (
            <a
              href={browseUrl || `${JIRA_BROWSE_BASE}/${ctx.jira_key}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...chip, display: 'inline-flex', alignItems: 'center', gap: 4, color: tvTokens.accentInk, background: tvTokens.accentSoft, textDecoration: 'none' }}
              title="Open the Jira issue this ticket is mirrored to"
            >
              {ctx.jira_key} <ExternalLink size={11} />
            </a>
          )}
          {ctx.screen && (
            <span style={{ fontSize: 12, color: tvTokens.ink500 }} title={ctx.page_url || undefined}>
              <span style={{ color: tvTokens.ink400 }}>Screen: </span>
              {ctx.screen}
            </span>
          )}
          {ctx.datadog_session_url && (
            <a
              href={ctx.datadog_session_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: tvTokens.accentInk, textDecoration: 'none' }}
            >
              Session replay →
            </a>
          )}
        </div>
      )}

      {/* People — who opened the ticket + who has responded */}
      {(ctx.reporter?.name || ctx.assignee?.name || (ctx.responders && ctx.responders.length > 0)) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 10, fontSize: 12 }}>
          {ctx.reporter?.name && (
            <span style={{ color: tvTokens.ink500 }}>
              <span style={{ color: tvTokens.ink400 }}>Reporter: </span>
              {ctx.reporter.name}
            </span>
          )}
          {ctx.assignee?.name && (
            <span style={{ color: tvTokens.ink500 }}>
              <span style={{ color: tvTokens.ink400 }}>Assignee: </span>
              {ctx.assignee.name}
            </span>
          )}
          {ctx.responders && ctx.responders.length > 0 && (
            <span style={{ color: tvTokens.ink500 }}>
              <span style={{ color: tvTokens.ink400 }}>Responders: </span>
              {ctx.responders.join(', ')}
            </span>
          )}
        </div>
      )}

      {/* Description */}
      {desc && desc.trim() ? (
        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: tvTokens.ink500, whiteSpace: 'pre-wrap' }}>{renderTextWithLinks(desc)}</div>
      ) : (
        <div style={{ fontSize: 12.5, color: tvTokens.ink400 }}>No description on the ticket.</div>
      )}

      {/* Image attachments — inline screenshots */}
      {images.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {images.map((a) => (
            <AttachmentImage key={a.id} att={a} />
          ))}
        </div>
      )}

      {/* Video attachments — inline players (authed blob → object URL). */}
      {videos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {videos.map((a) => (
            <AttachmentVideo key={a.id} att={a} chipStyle={chip} />
          ))}
        </div>
      )}

      {/* PDF attachments — inline iframe preview + download fallback. */}
      {pdfs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {pdfs.map((a) => (
            <AttachmentPdf key={a.id} att={a} chipStyle={chip} />
          ))}
        </div>
      )}

      {/* Other attachments — clickable downloads (authed blob fetch). */}
      {files.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 10 }}>
          {files.map((a) => (
            <AttachmentFile key={a.id} att={a} chipStyle={chip} />
          ))}
        </div>
      )}

      {/* Responses — the Jira comment thread */}
      {ctx.comments && ctx.comments.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
            Responses ({ctx.comments.length})
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
            {ctx.comments.map((c, i) => (
              <div
                key={c.id || i}
                style={{ padding: '8px 10px', background: c.is_agent ? '#eef3fb' : tvTokens.paper2, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm }}
              >
                <div style={{ fontSize: 11, color: tvTokens.ink500, marginBottom: 3 }}>
                  <strong>{c.author || 'Unknown'}</strong>
                  {c.is_agent ? ' 🤖' : ''}
                  {c.created ? ` · ${rel(c.created)}` : ''}
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.5, color: tvTokens.ink, whiteSpace: 'pre-wrap' }}>
                  {renderTextWithLinks(c.body || '')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reply back to Jira (control users, Jira-sourced tickets) */}
      {canControl && ticketId && ctx.source === 'jira' && ctx.jira_configured !== false && (
        <ReplyComposer ticketId={ticketId} onReplied={onReplied} />
      )}

      {/* Always offer the Jira link */}
      {browseUrl && (
        <div style={{ marginTop: 10 }}>
          <a
            href={browseUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: tvTokens.accentInk, textDecoration: 'none' }}
          >
            View in Jira <ExternalLink size={11} />
          </a>
          {ctx.jira_configured === false && (
            <span style={{ marginLeft: 8, fontSize: 11, color: tvTokens.ink400 }}>Connect Jira to preview here</span>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Resolution journey (implementation arc) ----
// GET /agents/rca/{id}/journey. Renders a horizontal stepper of the ticket's
// arc (drafted → CI → approved → merged → deployed for code mode; triaged →
// resolved for nocode). Every field is guarded — the pipeline may be mid-flight
// and GitHub may be unconfigured (github_configured===false → static stages,
// no live CI checks).
type JourneyState = 'done' | 'running' | 'pending' | 'failed' | 'skip';
interface JourneyCheck {
  name?: string | null;
  status?: string | null;
  conclusion?: string | null;
}
interface JourneyStage {
  key: string;
  label: string;
  state: JourneyState;
  at?: string | null;
  detail?: string | null;
  link?: string | null;
  checks?: JourneyCheck[] | null;
}
interface Journey {
  ticket?: string | null;
  verdict?: string | null;
  status?: string | null;
  mode?: 'code' | 'nocode' | string;
  pr_url?: string | null;
  pr_state?: string | null;
  github_configured?: boolean;
  stages?: JourneyStage[];
}

// One entry in the integrated activity thread (GET /agents/rca/{id}/timeline).
// `kind` drives the label + accent; `actor` drives the lane (agent/reviewer/
// creator/system). `backfilled` marks a coarse stamp synthesised from the old
// lifecycle columns before the event log existed → rendered "approx.".
interface TimelineItem {
  id: string;
  kind: string;
  actor: 'agent' | 'reviewer' | 'creator' | 'system' | string;
  actor_label?: string | null;
  body?: string | null;
  meta?: Record<string, any> | null;
  at?: string | null;
  backfilled?: boolean;
}
interface TimelineData {
  ticket?: string | null;
  title?: string | null;
  source?: string | null;
  verdict?: string | null;
  status?: string | null;
  items: TimelineItem[];
  count?: number;
  has_backfill?: boolean;
}

// Colors + icon for a stage node, keyed off its state. Reuses the v2 semantic
// tokens (success/danger/accent) so the stepper matches the rest of the page.
function journeyVisual(state: JourneyState): {
  color: string;
  fill: string;
  Icon: typeof Check;
  spin?: boolean;
  dashed?: boolean;
} {
  switch (state) {
    case 'done':
      return { color: '#12855f', fill: '#e3f3ec', Icon: Check };
    case 'running':
      return { color: '#46739e', fill: '#e6eef6', Icon: Loader2, spin: true };
    case 'failed':
      return { color: '#cc3b57', fill: '#fbe6ea', Icon: X };
    case 'skip':
      return { color: tvTokens.ink400, fill: 'transparent', Icon: Circle, dashed: true };
    case 'pending':
    default:
      return { color: tvTokens.ink400, fill: 'transparent', Icon: Circle };
  }
}

// Icon-per-stage-key for the well-known code-mode stages (falls back to the
// state icon for anything unrecognized like nocode's triaged/resolved).
function stageKeyIcon(key: string): typeof Check | null {
  switch (key) {
    case 'merged':
      return GitMerge;
    case 'approved':
      return GitPullRequest;
    case 'deployed':
      return Rocket;
    default:
      return null;
  }
}

// A single CI check row — a colored dot (by conclusion) + the check name.
function checkDot(check: JourneyCheck): string {
  const c = (check.conclusion || '').toLowerCase();
  const s = (check.status || '').toLowerCase();
  if (c === 'success') return '#12855f';
  if (c === 'failure' || c === 'timed_out' || c === 'cancelled' || c === 'action_required') return '#cc3b57';
  if (c === 'skipped' || c === 'neutral') return tvTokens.ink400;
  // No conclusion yet — in progress / queued.
  if (s === 'in_progress' || s === 'queued' || s === 'pending' || !c) return '#c98a1a';
  return tvTokens.ink400;
}

// The horizontal stepper body. `entry` mirrors the ticketCtx cache shape
// (loading / data / error). The CI stage is independently expandable to show
// per-check conclusions. Wrapped in an overflow-x:auto rail so it never breaks
// page layout on narrow widths.
function JourneyBody({ entry }: { entry?: { loading: boolean; data: Journey | null; error: boolean } }) {
  const [ciOpen, setCiOpen] = useState(false);
  if (!entry || entry.loading) {
    return <div style={{ fontSize: 12, color: tvTokens.ink400 }}>Loading journey…</div>;
  }
  if (entry.error || !entry.data) {
    return <div style={{ fontSize: 12, color: tvTokens.ink400 }}>Couldn’t load the resolution journey.</div>;
  }
  const j = entry.data;
  const stages = Array.isArray(j.stages) ? j.stages : [];
  if (stages.length === 0) {
    return <div style={{ fontSize: 12, color: tvTokens.ink400 }}>No journey stages yet.</div>;
  }
  // The connector up to (and including) the last done/running/failed stage is
  // "reached"; beyond that it's muted. Compute the furthest reached index.
  let reached = -1;
  stages.forEach((s, i) => {
    if (s.state === 'done' || s.state === 'running' || s.state === 'failed') reached = Math.max(reached, i);
  });
  const prState = j.pr_state ? j.pr_state.replace(/_/g, ' ') : null;

  return (
    <div>
      {/* PR link + state + github note */}
      {(j.pr_url || prState || j.github_configured === false) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          {j.pr_url && (
            <a
              href={j.pr_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: tvTokens.accentInk, textDecoration: 'none' }}
            >
              <GitPullRequest size={12} /> View PR <ExternalLink size={11} />
            </a>
          )}
          {prState && (
            <span style={{ ...pillMono, fontSize: 10, padding: '1px 7px', color: tvTokens.ink500 }}>{prState}</span>
          )}
          {j.github_configured === false && (
            <span style={{ fontSize: 11, color: tvTokens.ink400 }}>Connect GitHub for live CI detail</span>
          )}
        </div>
      )}

      {/* The stepper rail — scrolls horizontally on narrow widths. */}
      <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 'min-content' }}>
          {stages.map((stage, i) => {
            const vis = journeyVisual(stage.state);
            const keyIcon = stageKeyIcon(stage.key);
            const NodeIcon = stage.state === 'done' && keyIcon ? keyIcon : vis.Icon;
            const isCi = stage.key === 'ci';
            const checks = Array.isArray(stage.checks) ? stage.checks : [];
            const passed = checks.filter((c) => (c.conclusion || '').toLowerCase() === 'success').length;
            const connectorReached = i < reached || (i === reached && stage.state === 'done');
            return (
              <Fragment key={stage.key || i}>
                {/* Node column */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 92, flex: '0 0 auto' }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: vis.fill,
                      border: `1.5px ${vis.dashed ? 'dashed' : 'solid'} ${vis.color}`,
                      color: vis.color,
                    }}
                  >
                    <NodeIcon
                      size={15}
                      style={vis.spin ? { animation: 'spin 1s linear infinite' } : undefined}
                    />
                  </div>
                  <div
                    style={{
                      marginTop: 5,
                      fontSize: 11,
                      fontWeight: 600,
                      textAlign: 'center',
                      color: stage.state === 'pending' || stage.state === 'skip' ? tvTokens.ink400 : tvTokens.ink,
                      lineHeight: 1.25,
                    }}
                  >
                    {stage.label || stage.key}
                  </div>
                  {stage.at && (
                    <div style={{ marginTop: 2, fontSize: 10, color: tvTokens.ink400, fontVariantNumeric: 'tabular-nums' }}>
                      {rel(stage.at)}
                    </div>
                  )}
                  {/* CI summary + expand toggle */}
                  {isCi && checks.length > 0 && (
                    <button
                      onClick={() => setCiOpen((o) => !o)}
                      style={{ marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: tvTokens.accentInk, fontSize: 10 }}
                    >
                      {passed}/{checks.length} passed
                      {ciOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                    </button>
                  )}
                  {isCi && checks.length === 0 && j.github_configured === false && (
                    <div style={{ marginTop: 3, fontSize: 10, color: tvTokens.ink400, textAlign: 'center' }}>No live CI</div>
                  )}
                </div>
                {/* Connector line (not after the last node) */}
                {i < stages.length - 1 && (
                  <div
                    style={{
                      flex: '1 1 24px',
                      minWidth: 24,
                      height: 2,
                      marginTop: 14,
                      background: connectorReached ? '#12855f' : tvTokens.border,
                      alignSelf: 'flex-start',
                    }}
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      </div>

      {/* Expanded CI checks list */}
      {ciOpen && (
        <div style={{ marginTop: 8 }}>
          {stages
            .filter((s) => s.key === 'ci')
            .flatMap((s) => (Array.isArray(s.checks) ? s.checks : []))
            .map((c, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '3px 0', fontSize: 12, color: tvTokens.ink500 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: checkDot(c), flex: '0 0 auto' }} />
                <span>{c.name || 'check'}</span>
                {c.conclusion && (
                  <span style={{ fontSize: 10.5, color: tvTokens.ink400 }}>{c.conclusion.replace(/_/g, ' ')}</span>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// "Add context & re-analyze" — a toggle that reveals an inline textarea so a
// reviewer can give the agent more context on an ambiguous ticket and re-queue
// it. Shared by the gate queue and the All-tickets rows. Gated by canControl
// upstream (only rendered for control users).
function ReanalyzeControl({
  reviewerContext,
  open,
  text,
  busy,
  onToggle,
  onChange,
  onSubmit,
}: {
  reviewerContext?: string | null;
  open: boolean;
  text: string;
  busy: boolean;
  onToggle: () => void;
  onChange: (v: string) => void;
  onSubmit: (implement: boolean) => void;
}) {
  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={onToggle}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: tvTokens.accentInk }}
      >
        <RefreshCw size={12} />
        <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>
          Add context — re-analyze or build
        </span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div style={{ marginTop: 6 }}>
          {reviewerContext && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink400, marginBottom: 3 }}>
                Reviewer context
              </div>
              <div
                style={{
                  maxHeight: 120,
                  overflowY: 'auto',
                  padding: '7px 9px',
                  background: tvTokens.paper2,
                  border: `1px solid ${tvTokens.border}`,
                  borderRadius: tvTokens.rSm,
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  color: tvTokens.ink500,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {reviewerContext}
              </div>
            </div>
          )}
          <textarea
            value={text}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Add context — what the agent missed, or a product decision to implement (e.g. 'Approved: build feature items 1,2,3,5 with these rules…')"
            rows={3}
            style={{
              width: '100%',
              resize: 'vertical',
              padding: '8px 10px',
              borderRadius: tvTokens.rSm,
              border: `1px solid ${tvTokens.border}`,
              background: tvTokens.paper,
              color: tvTokens.ink,
              fontSize: 12.5,
              lineHeight: 1.5,
              fontFamily: 'inherit',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button
              onClick={() => onSubmit(false)}
              disabled={busy || !text.trim()}
              style={{
                ...btnGhost,
                opacity: busy || !text.trim() ? 0.5 : 1,
                cursor: busy || !text.trim() ? 'not-allowed' : 'pointer',
              }}
              title="Re-queue for the agent to re-triage with this context (may change the verdict)"
            >
              <RefreshCw size={13} style={{ animation: busy ? 'spin 1s linear infinite' : 'none' }} /> Re-analyze
            </button>
            <button
              onClick={() => onSubmit(true)}
              disabled={busy || !text.trim()}
              style={{
                ...btnPrimary,
                opacity: busy || !text.trim() ? 0.5 : 1,
                cursor: busy || !text.trim() ? 'not-allowed' : 'pointer',
              }}
              title="Confirm this as a product decision and hand off to the agent to BUILD it"
            >
              <Bot size={13} /> Approve &amp; build
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// "Hold & ask creator" control — mirrors ReanalyzeControl. A toggle button
// reveals an inline textarea for the reviewer's question; on submit the API
// posts the question on Jira, notifies admins, and flips the ticket to 'held'.
function HoldControl({
  open,
  text,
  busy,
  onToggle,
  onChange,
  onSubmit,
}: {
  open: boolean;
  text: string;
  busy: boolean;
  onToggle: () => void;
  onChange: (v: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={onToggle}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: tvTokens.accentInk }}
      >
        <PauseCircle size={12} />
        <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>
          Hold &amp; ask creator
        </span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div style={{ marginTop: 6 }}>
          <textarea
            value={text}
            onChange={(e) => onChange(e.target.value)}
            placeholder="What do you need from the ticket creator?"
            rows={3}
            style={{
              width: '100%',
              resize: 'vertical',
              padding: '8px 10px',
              borderRadius: tvTokens.rSm,
              border: `1px solid ${tvTokens.border}`,
              background: tvTokens.paper,
              color: tvTokens.ink,
              fontSize: 12.5,
              lineHeight: 1.5,
              fontFamily: 'inherit',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ marginTop: 6 }}>
            <button
              onClick={onSubmit}
              disabled={busy || !text.trim()}
              style={{
                ...btnPrimary,
                opacity: busy || !text.trim() ? 0.5 : 1,
                cursor: busy || !text.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              <PauseCircle size={13} style={{ animation: busy ? 'spin 1s linear infinite' : 'none' }} /> Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Verdict → tone, reusing palette already present on this page (RCA red
// #cc3b57, attention amber #b9781f/#f8efda, gate blue #5a4fcf/#efedfb).
// OPERATIONAL and CONFIGURATION are the two hand-off/no-code verdicts (the RCA
// agent emits `configuration` for member-field-config asks; the executor's svc-*
// tools were originally labeled OPERATIONAL). Both route to "Hand off to agent",
// NOT the code-review (Approve/Merge PR) affordance, so treat them as one family.
function isHandoffVerdict(verdict?: string | null): boolean {
  const v = (verdict || '').toUpperCase();
  return v === 'OPERATIONAL' || v === 'CONFIGURATION';
}

function verdictTone(verdict?: string | null): { color: string; background: string } {
  switch ((verdict || '').toUpperCase()) {
    case 'BUG':
      return { color: '#cc3b57', background: '#fbe6ea' };
    case 'GAP':
      return { color: '#b9781f', background: '#f8efda' };
    case 'FEATURE':
      return { color: '#46739e', background: '#e6eef6' };
    case 'OPERATIONAL':
    case 'CONFIGURATION':
      return { color: '#b9781f', background: '#f8efda' };
    default:
      return { color: tvTokens.ink500, background: tvTokens.paper2 };
  }
}

// Per-ticket lifecycle/outcome pill: what actually HAPPENED to this ticket,
// derived from status + the review-action signals (reviewer_context /
// build_requested / verdict / reviewed_at). Tones reuse the page palette.
function lifecycle(t: {
  status: string;
  verdict?: string | null;
  reviewer_context?: string | null;
  build_requested?: boolean | null;
  reviewed_at?: string | null;
  pr_url?: string | null;
  updated_at?: string | null;
  held_for?: string | null;
  intake_reviewed_at?: string | null;
}): { label: string; color: string; background: string } {
  const status = (t.status || '').toLowerCase();
  // A build handed to the hourly agent that hasn't moved in a while is queued, not
  // actively running — say so (with age) so "Building" doesn't read as stuck progress.
  const staleMin =
    t.updated_at ? Math.floor((Date.now() - new Date(t.updated_at).getTime()) / 60000) : 0;
  const ageSuffix = staleMin >= 12 ? ` · waiting ${staleMin >= 90 ? `${Math.floor(staleMin / 60)}h` : `${staleMin}m`}` : '';
  const isOperational = isHandoffVerdict(t.verdict);
  const blue = { color: '#46739e', background: '#e6eef6' };
  const amber = { color: '#b9781f', background: '#f8efda' };
  const purple = { color: '#5a4fcf', background: '#efedfb' };
  const green = { color: '#12855f', background: '#e3f3ec' };
  const red = { color: '#cc3b57', background: '#fbe6ea' };
  const muted = { color: tvTokens.ink500, background: tvTokens.paper2 };
  switch (status) {
    case 'reviewed': {
      // TRUS-611: say WHAT was approved and what happens next. A code ticket with a PR is
      // waiting on one thing — the merge, which deploys it.
      // The " · Next: …" suffix renders as its own line under the pill (see the log row).
      if (t.pr_url && !isOperational) return { label: 'Approved for implementation · Next: merge', ...green };
      if (isOperational) return { label: 'Approved for implementation · Next: complete', ...green };
      return { label: 'Approved for implementation', ...green };
    }
    case 'pending':
      if (t.build_requested)
        return {
          label: (isOperational ? 'Agent completing' : 'Building') + ageSuffix,
          // amber once it's been waiting a while — signals it needs a run, not that it's progressing
          ...(ageSuffix ? amber : purple),
        };
      if (t.reviewer_context) return { label: 'Re-analyzing — context added', ...amber };
      return { label: 'Queued', ...blue };
    case 'claimed':
      // A claim the run never reported back on sits here indefinitely, and plain
      // "In progress" hides that (TRUS-361 read "In progress" for 19 hours). Past the
      // stale-claim threshold it is an orphan, not progress — say so and go amber.
      if (staleMin >= 30) return { label: `Stalled — claimed ${staleMin >= 90 ? `${Math.floor(staleMin / 60)}h` : `${staleMin}m`} ago`, ...amber };
      return { label: 'In progress' + ageSuffix, ...blue };
    case 'awaiting_review':
      if (isOperational) return { label: 'Needs action', ...amber };
      // Passed the pre-development review; the drafted PR now needs a code review before merge.
      if (t.intake_reviewed_at && t.pr_url) return { label: 'Approved for implementation · Next: code review', ...purple };
      return { label: 'Awaiting review', ...purple };
    case 'skipped':
      // The verdict (e.g. WORKS-AS-DESIGNED) is shown in its own Type column beside this
      // badge — don't repeat it here, or the long label overflows the fixed status column
      // and overlaps the Type chip.
      return { label: 'Auto-resolved', ...muted };
    case 'product_approved':
      // A FEATURE an admin approved for a SEPARATE build session (not handed to the agent).
      // Open, non-terminal, and explicitly NOT auto-resolved.
      return { label: 'Product-approved — ready to build', ...green };
    case 'done':
      // A done ticket with a PR was merged + deployed by the pipeline; say so
      // rather than a generic "Done" so the outcome is legible at a glance.
      return { label: t.pr_url ? 'Merged & deployed' : 'Done', ...green };
    case 'failed':
      return { label: 'Failed', ...red };
    case 'held': {
      // A hold has four audiences (held_for): 'product' = a FEATURE awaiting a product build
      // decision; 'admin' = a works-as-designed assessment / operational change-set / closed-epic
      // child awaiting admin approval; 'creator_draft' = the agent drafted a question for the
      // reporter and a human must review/edit/send it (nothing has reached the creator yet);
      // 'creator'/NULL = a human sent it and we're parked on the reporter (legacy default).
      const audience = (t.held_for || '').toLowerCase();
      if (audience === 'intake') return { label: 'Intake review', ...purple };
      if (audience === 'product') return { label: 'Needs product decision', ...purple };
      if (audience === 'admin') return { label: 'Needs admin review', ...amber };
      if (audience === 'creator_draft') return { label: 'Reply to creator — needs review', ...amber };
      return { label: 'Held — awaiting creator', ...amber };
    }
    default:
      return { label: statusLabel(status), ...muted };
  }
}

const KIND_LABEL: Record<string, string> = { llm: 'LLM', ml: 'ML', workflow: 'Workflow', operator: 'Operator' };

function kindColor(kind: string): string {
  return kind === 'llm'
    ? '#5a4fcf'
    : kind === 'ml'
      ? '#0f7d70'
      : kind === 'workflow'
        ? '#46739e'
        : tvTokens.ink500;
}

function rel(iso?: string | null): string {
  if (!iso) return '—';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// Jira priority name -> urgency rank. MIRRORS the backend's _PRIORITY_RANK (rca_log) so a
// client-side re-sort agrees with the server order; anything unknown ranks last.
const PRIORITY_RANK: Record<string, number> = {
  blocker: 0, highest: 1, critical: 1, urgent: 1, high: 2, medium: 3, normal: 3, low: 4, lowest: 5, trivial: 5,
};
const PRIORITY_UNRANKED = 6;
function priorityRank(r: { priority?: string | null; priority_rank?: number | null }): number {
  if (typeof r.priority_rank === 'number') return r.priority_rank;
  return PRIORITY_RANK[(r.priority || '').trim().toLowerCase()] ?? PRIORITY_UNRANKED;
}
function priorityTone(rank: number): { color: string; background: string } {
  if (rank <= 1) return { color: '#a3243d', background: '#fbe5ea' };
  if (rank === 2) return { color: '#9a5a12', background: '#fcefdc' };
  if (rank === 3) return { color: '#46739e', background: '#e7f0f8' };
  return { color: tvTokens.ink500, background: 'transparent' };
}
// Whole days from today (local) to a 'YYYY-MM-DD' due date: negative = overdue.
function dueInDays(due?: string | null): number | null {
  if (!due) return null;
  const [y, m, d] = due.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((Date.UTC(y, m - 1, d) - today) / 86400000);
}
const isSettled = (status?: string | null) => ['done', 'skipped'].includes((status || '').toLowerCase());
// Due-date buckets. MIRROR the backend's _DUE_PREDICATES: overdue / due_soon only count
// OPEN tickets (a done ticket past its date isn't late).
function dueMatch(r: { due_date?: string | null; status?: string | null }, key: string): boolean {
  const days = dueInDays(r.due_date);
  if (key === 'overdue') return days !== null && days < 0 && !isSettled(r.status);
  if (key === 'due_soon') return days !== null && days >= 0 && days <= 7 && !isSettled(r.status);
  if (key === 'has_due') return days !== null;
  if (key === 'no_due') return days === null;
  return true;
}
const DUE_OPTIONS: { key: string; label: string; tone: string }[] = [
  { key: 'overdue', label: 'Overdue', tone: '#cc3b57' },
  { key: 'due_soon', label: 'Due in 7 days', tone: '#b9781f' },
  { key: 'has_due', label: 'Has due date', tone: tvTokens.ink400 },
  { key: 'no_due', label: 'No due date', tone: tvTokens.ink400 },
];

function absTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// A compact vertical timeline of a ticket's lifecycle stamps. Only steps that
// actually happened (have a timestamp) render, in chronological order, so a
// reviewer sees exactly when it was received, analyzed, drafted, approved,
// merged and resolved. Distinct from the resolution journey (a PR/CI arc).
function TicketTimeline({ t }: { t: RcaLogItem }) {
  const st = (t.status || '').toLowerCase();
  const steps: { label: string; at?: string | null }[] = [
    { label: 'Received', at: t.ticket_created_at || t.created_at },
    { label: 'Analyzed by agent', at: t.claimed_at },
    { label: 'Drafted', at: t.drafted_at },
    { label: 'Approved for implementation', at: t.reviewed_at },
  ];
  if (st === 'done') steps.push({ label: 'Resolved', at: t.updated_at });
  const present = steps.filter((s) => !!s.at);
  if (present.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {present.map((s, i) => (
        <div key={s.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'stretch' }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: '#5a4fcf', marginTop: 4, flexShrink: 0 }} />
            {i < present.length - 1 && <span style={{ width: 2, flex: 1, background: tvTokens.borderMuted, minHeight: 14 }} />}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline', paddingBottom: i < present.length - 1 ? 8 : 0 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: tvTokens.ink }}>{s.label}</span>
            <span style={{ fontSize: 11, color: tvTokens.ink400, fontVariantNumeric: 'tabular-nums' }}>{absTime(s.at)}</span>
            <span style={{ fontSize: 10.5, color: tvTokens.ink400 }}>· {rel(s.at)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Label + accent for a thread entry. `actor` picks the lane color; `kind` the label.
function threadVisual(it: TimelineItem): { label: string; color: string; mono?: boolean } {
  const v = (it.meta?.verdict as string) || '';
  const byKind: Record<string, string> = {
    received: 'Request from creator',
    claimed: 'Agent started analysis',
    analysis: 'Agent analysis',
    proposed_changes: 'Proposed changes',
    verdict: v ? `Classified as ${v}` : 'Classified',
    held_question: 'Question to creator',
    reply: 'Reply sent',
    // The creator round trip on a held ticket: what an admin released to the reporter, and
    // the answer coming back into the decision that was parked behind it.
    creator_ask: it.meta?.return_to ? `Question released to creator — ${it.meta.return_to} decision parked` : 'Question released to creator',
    creator_reply: it.meta?.restored_to ? `Creator answered — back for the ${it.meta.restored_to} decision` : 'Creator answered',
    reviewer_context: it.meta?.implement ? 'Approved to build — context added' : 'Context added',
    comment: it.actor_label ? `${it.actor_label} commented` : 'Comment',
    approved: 'Approved for implementation',
    handoff: 'Handed off to agent',
    merged: 'PR merged',
    done: 'Resolved',
    dismissed: 'Dismissed',
    failed: 'Agent failed',
    unhold: 'Unblocked',
    part: 'Progress update',
    note: 'Note',
    reanalyze: 'Re-analysis queued',
  };
  const byActor: Record<string, string> = {
    agent: '#5a4fcf',
    reviewer: '#46739e',
    creator: '#b9781f',
    system: tvTokens.ink500,
  };
  return {
    label: byKind[it.kind] || it.kind,
    color: byActor[it.actor] || tvTokens.ink500,
    mono: it.kind === 'proposed_changes',
  };
}

// The integrated activity thread for a ticket: creator request → agent analysis →
// proposed changes → reviewer feedback / context → held question / replies →
// resolution, each entry with its own timestamp. Falls back to the coarse
// lifecycle stepper (TicketTimeline) if the timeline endpoint is unavailable.
function TicketThread({
  entry,
  item,
}: {
  entry?: { loading: boolean; data: TimelineData | null; error: boolean };
  item: RcaLogItem;
}) {
  if (!entry || entry.loading) {
    return <div style={{ fontSize: 12, color: tvTokens.ink400 }}>Loading activity…</div>;
  }
  const items = entry.data?.items || [];
  if (entry.error || items.length === 0) {
    // Graceful fallback to the stage stepper (also covers the has-no-events edge case).
    return <TicketTimeline t={item} />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {items.map((it, i) => {
        const vis = threadVisual(it);
        const last = i === items.length - 1;
        return (
          <div key={it.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'stretch' }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: vis.color, marginTop: 4, flexShrink: 0 }} />
              {!last && <span style={{ width: 2, flex: 1, background: tvTokens.borderMuted, minHeight: 14 }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 0 : 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: vis.color }}>{vis.label}</span>
                {it.actor_label && it.kind !== 'comment' && (
                  <span style={{ fontSize: 11, color: tvTokens.ink400 }}>{it.actor_label}</span>
                )}
                <span style={{ fontSize: 11, color: tvTokens.ink400, fontVariantNumeric: 'tabular-nums' }}>{absTime(it.at)}</span>
                <span style={{ fontSize: 10.5, color: tvTokens.ink400 }}>· {rel(it.at)}</span>
                {it.backfilled && (
                  <span style={{ fontSize: 9.5, color: tvTokens.ink400, fontStyle: 'italic' }} title="Approximate — synthesised from the ticket's lifecycle stamps before the activity log existed">
                    approx.
                  </span>
                )}
              </div>
              {it.body && it.body.trim() && (
                <div
                  style={
                    vis.mono
                      ? {
                          marginTop: 5,
                          padding: '8px 10px',
                          background: tvTokens.paper2,
                          border: `1px solid ${tvTokens.border}`,
                          borderRadius: tvTokens.rSm,
                          fontFamily: tvTokens.fontMono,
                          fontSize: 11,
                          lineHeight: 1.5,
                          color: tvTokens.ink500,
                          whiteSpace: 'pre-wrap',
                          overflowX: 'auto',
                        }
                      : { marginTop: 4, fontSize: 12, lineHeight: 1.5, color: tvTokens.ink500, whiteSpace: 'pre-wrap' }
                  }
                >
                  {vis.mono ? it.body : renderTextWithLinks(it.body)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Full-refresh coalescing guards for AgentControlPage. Module scope (not useRef)
// so they hold across REMOUNTS of the page within a tab — see the comment on
// loadCoalesced below for why that distinction is the whole point.
let agentLoadInFlight = false;
let lastAgentLoadAt = 0;
const MIN_AGENT_REFRESH_GAP_MS = 5000;
// Which RCA-log backend answered last (TRUS-687 paged vs. legacy), kept across remounts so a
// remount doesn't re-probe. 'unknown' until the first response.
let lastRcaApiMode: 'unknown' | 'paged' | 'legacy' = 'unknown';
// Order-independent identity of a log request (undefined params dropped).
function rcaParamsKey(p: object): string {
  return JSON.stringify(
    Object.entries(p)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

export function AgentControlPage(
  { product, initialTab, focusIssueId }: { product?: string; initialTab?: AgentTab; focusIssueId?: number | null } = {},
) {
  const prod = product || (productConfig.isTriveny ? 'triveny' : 'weavein');
  const isMobile = useIsMobile();
  const tabbed = useTabbedChrome();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [gated, setGated] = useState<GatedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  // Agent capabilities catalog (what "Hand off to agent" can do) — lazy-loaded on open.
  const [showCaps, setShowCaps] = useState(false);
  const [caps, setCaps] = useState<{ groups: any[]; cannot: string[] } | null>(null);
  const openCapabilities = async () => {
    setShowCaps(true);
    if (caps) return;
    try {
      const res: any = await agentsApi.getCapabilities();
      setCaps((res?.data ?? res) as { groups: any[]; cannot: string[] });
    } catch {
      setCaps({ groups: [], cannot: [] });
    }
  };
  // Wave 1: the shared marketing/sales tool catalogue (what agents + this console can do),
  // and the voice-call composer for dialogue agents.
  const [showTools, setShowTools] = useState(false);
  const [toolGroups, setToolGroups] = useState<Record<string, any[]> | null>(null);
  const openTools = async () => {
    setShowTools(true);
    if (toolGroups) return;
    try {
      const res: any = await agentsApi.getToolCatalogue();
      const data = res?.data ?? res;
      setToolGroups((data?.groups ?? {}) as Record<string, any[]>);
    } catch {
      setToolGroups({});
    }
  };
  const [voiceAgent, setVoiceAgent] = useState<Agent | null>(null);
  const [voiceNumber, setVoiceNumber] = useState('');
  const [voiceBusy, setVoiceBusy] = useState(false);
  const startCall = async () => {
    if (!voiceAgent || !voiceNumber.trim()) return;
    setVoiceBusy(true);
    try {
      const res: any = await agentsApi.startVoiceCall({ agent_key: voiceAgent.key, to_number: voiceNumber.trim() });
      const data = res?.data ?? res;
      flash(data?.call_placed ? 'Call placed.' : data?.message || 'Could not place the call (check Twilio config).');
      setVoiceAgent(null);
      setVoiceNumber('');
    } catch {
      flash('Could not place the call.');
    } finally {
      setVoiceBusy(false);
    }
  };
  // Wave 2: run an orchestrator/LLM agent on an instruction (e.g. the web studio director
  // builds a draft page) and show what it did + what awaits approval.
  const [runTarget, setRunTarget] = useState<Agent | null>(null);
  const [runInstruction, setRunInstruction] = useState('');
  const [runBusy, setRunBusy] = useState(false);
  const [runResult, setRunResult] = useState<any | null>(null);
  const doRun = async () => {
    if (!runTarget || !runInstruction.trim()) return;
    setRunBusy(true);
    setRunResult(null);
    try {
      const res: any = await agentsApi.runAgentOrchestrator(runTarget.key, { instruction: runInstruction.trim() });
      setRunResult(res?.data ?? res);
    } catch {
      setRunResult({ ok: false, error: 'The run failed to start.' });
    } finally {
      setRunBusy(false);
    }
  };
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [busyGate, setBusyGate] = useState<string | null>(null);
  const [expandedGate, setExpandedGate] = useState<Record<string, boolean>>({});
  const [expandedChanges, setExpandedChanges] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [kindFilter, setKindFilter] = useState<string>('all');
  const [scopeAgent, setScopeAgent] = useState<Agent | null>(null);
  const [scheduleAgent, setScheduleAgent] = useState<Agent | null>(null);
  const [policyAgent, setPolicyAgent] = useState<Agent | null>(null);
  // Triage log — every ticket the RCA pipeline touched, filtered server-side (rcaParams).
  const [rcaLog, setRcaLog] = useState<RcaLogItem[]>([]);
  const [rcaCounts, setRcaCounts] = useState<RcaCounts>({});
  // Label→count rollup from the server envelope (every Jira label present, e.g.
  // 'IFR', 'razingthebar') so the console can slice tickets by client/workstream.
  const [labelCounts, setLabelCounts] = useState<RcaCounts>({});
  // Grand total + verdict/journey rollups from the server envelope. These are counted
  // over the FULL set (not the fetched page), so the "All"/Type/Needs-action chips no
  // longer pin at the fetch limit once the log grows past it.
  const [rcaTotal, setRcaTotal] = useState<number>(0);
  // Count of rows matching the ACTIVE filters (server-side, before the fetch window).
  // Equals rcaTotal when no filters are set. Drives the honest "N of M matching" footer.
  const [rcaFilteredTotal, setRcaFilteredTotal] = useState<number>(0);
  // True while the log is being refetched for a filter change — dims the list so it
  // doesn't look stale mid-swap.
  const [rcaLogLoading, setRcaLogLoading] = useState(false);
  const [serverVerdictCounts, setServerVerdictCounts] = useState<RcaCounts>({});
  const [serverJourneyCounts, setServerJourneyCounts] = useState<RcaCounts>({});
  // Epics seen by the pipeline (per-Epic auto/hold/closed gate).
  const [epics, setEpics] = useState<any[]>([]);
  const [epicBusy, setEpicBusy] = useState<string | null>(null);
  // The Epics section is collapsible; default collapsed to keep the queue uncluttered
  // (kept-open Epics live here permanently). The header still shows how many need a decision.
  const [epicsOpen, setEpicsOpen] = useState(false);
  // Ticket filters (TRUS-687): every axis multi-select, kept in the URL (ids/enums only;
  // the search text stays in component state). The default view is the action queue —
  // awaiting_review is what needs a human; everything else is one chip away.
  const filters = useRcaFilters();
  const fState = filters.state;
  const fAxes = fState.axes;
  // Which top-level tab is shown (summary tiles stay above the tabs).
  // 'prs' and 'funders' exist only on the WeaveIn console. The remembered tab is
  // shared across products, so coming back to Triveny on one of them rendered a
  // page with no tab selected and nothing under it.
  const [agentTab, setAgentTab] = useState<AgentTab>(() => {
    const wanted = initialTab ?? lastAgentTab ?? 'tickets';
    return prod !== 'weavein' && (wanted === 'prs' || wanted === 'funders') ? 'tickets' : wanted;
  });
  // Remember the tab at module scope: the page remounts while auth inputs settle
  // (see loadCoalesced below), and each remount used to drop the user back on
  // Tickets — and unmount whatever tab they were reading.
  useEffect(() => {
    lastAgentTab = agentTab;
  }, [agentTab]);
  // Rollups for the priority and due axes (the rest are below with the TRUS-687 ones).
  const [priorityCounts, setPriorityCounts] = useState<Record<string, { rank: number; count: number }>>({});
  const [dueCounts, setDueCounts] = useState<RcaCounts>({});
  // Sortable columns for the All-tickets table. Default: most urgent first — Jira priority,
  // then the nearest due date — so the tickets that matter most lead the list. Part of the
  // filter state (and URL) because rcaParams reads it for the server-side order.
  const logSort = fState.sort;
  // TRUS-687 rollups (org / origin / reporter / assignee / issue type / Jira status / has PR);
  // each is null until a backend that sends it answers.
  const [rollups, setRollups] = useState<RcaRollups>(EMPTY_ROLLUPS);
  // Keyset paging: the cursor for the next page (null at the end, or on an older backend).
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Which backend answered: 'paged' (TRUS-687: cursor, rollups, group_by, views) or 'legacy'.
  const [rcaApiMode, setRcaApiMode] = useState<'unknown' | 'paged' | 'legacy'>(lastRcaApiMode);
  const rcaApiModeRef = useRef(rcaApiMode);
  rcaApiModeRef.current = rcaApiMode;
  const paged = rcaApiMode === 'paged';
  // group_by response: sections of the first N rows per group (null = a flat list).
  const [rcaGroups, setRcaGroups] = useState<RcaGroup<RcaLogItem>[] | null>(null);
  const [groupLimit, setGroupLimit] = useState(10);
  // The chip filter rows take a lot of vertical space; let the reviewer fold them away. The
  // choice is a per-browser convenience, so it lives in localStorage (guarded — it can throw).
  const [filtersOpen, setFiltersOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('agents.rcaFiltersOpen') !== '0';
    } catch {
      return true;
    }
  });
  const toggleFiltersOpen = () =>
    setFiltersOpen((o) => {
      try {
        localStorage.setItem('agents.rcaFiltersOpen', o ? '0' : '1');
      } catch {
        /* storage unavailable — keep it in memory */
      }
      return !o;
    });
  // "Why this total?" — the console counts tickets in the pipeline, not Jira issues; the
  // coverage report says which Jira issues never became a job and why.
  const [coverage, setCoverage] = useState<RcaCoverage | null>(null);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverageBusy, setCoverageBusy] = useState(false);
  const [coverageErr, setCoverageErr] = useState<string | null>(null);
  const toggleCoverage = async () => {
    if (coverageOpen) { setCoverageOpen(false); return; }
    setCoverageOpen(true);
    if (coverage) return;
    setCoverageBusy(true);
    setCoverageErr(null);
    try {
      const res: any = await agentsApi.getRcaCoverage();
      if (res && res.success === false) throw new Error(res.error?.message || 'Could not reach Jira.');
      setCoverage(res?.data ?? res);
    } catch (e: any) {
      setCoverageErr(e?.message || 'Could not load the Jira comparison.');
    } finally {
      setCoverageBusy(false);
    }
  };
  const [expandedLog, setExpandedLog] = useState<Record<string, boolean>>({});
  // Original ticket context (description + screenshots), lazy-loaded + cached
  // per RCA job id when a reviewer expands the item. Keyed by item.id.
  const [ticketCtx, setTicketCtx] = useState<Record<string, { loading: boolean; data: TicketContext | null; error: boolean }>>({});
  const [expandedTicketCtx, setExpandedTicketCtx] = useState<Record<string, boolean>>({});
  // TRUS-611: the technical analysis + proposed changes, per ticket. Collapsed by default once a
  // plain-language summary exists (that's what a reviewer reads first); open when there isn't one.
  const [expandedTech, setExpandedTech] = useState<Record<string, boolean>>({});
  const [iconKeyOpen, setIconKeyOpen] = useState(false);
  // Resolution journey (implementation arc), lazy-loaded + cached per RCA job id
  // when a reviewer expands the item. Keyed by item.id. Same shape as ticketCtx.
  const [journeyCache, setJourneyCache] = useState<Record<string, { loading: boolean; data: Journey | null; error: boolean }>>({});
  const [expandedJourney, setExpandedJourney] = useState<Record<string, boolean>>({});
  // Integrated activity thread, lazy-loaded + cached per RCA job id when a reviewer
  // expands the row (the Timeline card auto-loads it). Keyed by item.id.
  const [timelineCache, setTimelineCache] = useState<Record<string, { loading: boolean; data: TimelineData | null; error: boolean }>>({});
  // "Add context & re-analyze" inline input state, keyed by item id.
  const [reanalyzeOpen, setReanalyzeOpen] = useState<Record<string, boolean>>({});
  const [reanalyzeText, setReanalyzeText] = useState<Record<string, string>>({});
  const [reanalyzeBusy, setReanalyzeBusy] = useState<string | null>(null);
  // "Hold & ask creator" inline input state, keyed by item id.
  const [holdOpen, setHoldOpen] = useState<Record<string, boolean>>({});
  const [holdText, setHoldText] = useState<Record<string, string>>({});
  const [holdBusy, setHoldBusy] = useState<string | null>(null);
  // Multi-select for bulk actions on the All-tickets log (set of rca_job ids).
  const [selectedRca, setSelectedRca] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Merge-a-PR-from-the-console state: which item is mid-merge, and any
  // "can't merge" reason surfaced inline under the button (failing CI, conflict).
  const [mergeBusy, setMergeBusy] = useState<string | null>(null);
  const [mergeReason, setMergeReason] = useState<Record<string, string>>({});
  // True when the last merge attempt failed on a merge conflict → show the fallbacks.
  const [mergeConflict, setMergeConflict] = useState<Record<string, boolean>>({});
  // True when the last merge attempt failed because CI is red (agent can fix the build).
  const [mergeCiFail, setMergeCiFail] = useState<Record<string, boolean>>({});

  // Fetch (once) the original ticket context for an RCA item. Cached in
  // ticketCtx[id]; re-expanding a row won't refetch.
  const loadTicketContext = useCallback((id: string, force = false) => {
    setTicketCtx((prev) => {
      if (prev[id] && !force) return prev; // already loading or loaded
      agentsApi
        .getRcaContext(id)
        .then((res) => {
          const data = ((res as any)?.data ?? res) as TicketContext | null;
          setTicketCtx((p) => ({ ...p, [id]: { loading: false, data: data || null, error: !data } }));
        })
        .catch(() => {
          setTicketCtx((p) => ({ ...p, [id]: { loading: false, data: null, error: true } }));
        });
      return { ...prev, [id]: { loading: true, data: null, error: false } };
    });
  }, []);

  // Toggle the "Ticket context" block for an item; kick off the lazy load on
  // first open.
  const toggleTicketContext = useCallback(
    (id: string) => {
      setExpandedTicketCtx((prev) => {
        const next = !prev[id];
        if (next) loadTicketContext(id);
        return { ...prev, [id]: next };
      });
    },
    [loadTicketContext]
  );

  // Fetch (once) the resolution journey for an RCA item. Cached in
  // journeyCache[id]; re-expanding a row won't refetch.
  const loadJourney = useCallback((id: string) => {
    setJourneyCache((prev) => {
      if (prev[id]) return prev; // already loading or loaded
      agentsApi
        .getRcaJourney(id)
        .then((res) => {
          const data = ((res as any)?.data ?? res) as Journey | null;
          setJourneyCache((p) => ({ ...p, [id]: { loading: false, data: data || null, error: !data } }));
        })
        .catch(() => {
          setJourneyCache((p) => ({ ...p, [id]: { loading: false, data: null, error: true } }));
        });
      return { ...prev, [id]: { loading: true, data: null, error: false } };
    });
  }, []);

  // Toggle the "Resolution journey" block for an item; kick off the lazy load
  // on first open.
  const toggleJourney = useCallback(
    (id: string) => {
      setExpandedJourney((prev) => {
        const next = !prev[id];
        if (next) loadJourney(id);
        return { ...prev, [id]: next };
      });
    },
    [loadJourney]
  );

  // Fetch (once) the integrated activity thread for an RCA item. force=true busts
  // the cache after an action so the thread reflects the new event.
  const loadTimeline = useCallback((id: string, force = false) => {
    setTimelineCache((prev) => {
      if (prev[id] && !force) return prev; // already loading or loaded
      agentsApi
        .getRcaTimeline(id)
        .then((res) => {
          const data = ((res as any)?.data ?? res) as TimelineData | null;
          setTimelineCache((p) => ({ ...p, [id]: { loading: false, data: data || null, error: !data } }));
        })
        .catch(() => {
          setTimelineCache((p) => ({ ...p, [id]: { loading: false, data: null, error: true } }));
        });
      return { ...prev, [id]: { loading: true, data: null, error: false } };
    });
  }, []);

  // A journey is meaningful only once a fix exists (has a PR) or the ticket is
  // resolved (done/reviewed). Used to gate the collapsible in both render sites.
  const journeyMeaningful = useCallback(
    (item: { pr_url?: string | null; status?: string | null; verdict?: string | null }) => {
      const st = (item.status || '').toLowerCase();
      const vd = (item.verdict || '').toUpperCase();
      // Show the journey for anything on the implementation track: a drafted PR, an
      // in-progress or awaiting-approval ticket, a resolved one, or any code verdict
      // (BUG/GAP/FEATURE) even before a PR exists.
      if (item.pr_url) return true;
      if (['awaiting_review', 'claimed', 'reviewed', 'done'].includes(st)) return true;
      return ['BUG', 'GAP', 'FEATURE'].includes(vd);
    },
    []
  );

  // The search box (ticket key, TKT number or title): component state only, never the URL.
  const ticketQuery = filters.q;
  const setTicketQuery = filters.setQ;
  const debouncedTicketQuery = filters.debouncedQ;

  // The server-side filter set: OR within an axis, AND across axes. An absent axis means
  // "no filter on this axis".
  const activeGroup: RcaGroupBy | '' = paged ? fState.group : '';
  const rcaParams = useMemo<RcaLogParams>(
    () => ({
      ...filters.params,
      q: debouncedTicketQuery ? debouncedTicketQuery.slice(0, 200) : undefined,
      // The server picks WHICH rows come back, so a server-sortable column must be asked of
      // it (or the most urgent tickets past the window would never load). Direction and the
      // other columns are re-sorted client-side over the fetched rows.
      sort: serverSortFor(logSort.key, paged),
      ...(activeGroup ? { group_by: activeGroup, group_limit: groupLimit } : null),
      // Paged backend: its default page (100) + "Load more". Older backend: one 500-row window.
      ...(rcaApiMode === 'legacy' ? { limit: 500 } : null),
    }),
    [filters.params, debouncedTicketQuery, logSort.key, paged, activeGroup, groupLimit, rcaApiMode]
  );
  // load() (full refresh, also fired after actions) reads the latest params from this
  // ref so it re-renders the log for the CURRENT filter view without taking rcaParams
  // as a dependency (which would recreate load — and its interval — on every filter tweak).
  const rcaParamsRef = useRef(rcaParams);
  useEffect(() => {
    rcaParamsRef.current = rcaParams;
  }, [rcaParams]);

  // Apply an RCA-log envelope to state. Shared by load(), loadRcaLog() and the job focus;
  // tolerates the API client's { success, data } wrapping and older backends missing the
  // new rollups, cursor and groups. `keepLoaded` (the 60s poll) refreshes counts and the
  // first page but keeps the rows and cursor of pages the reviewer already loaded.
  const applyRcaLog = useCallback((rl: unknown, opts?: { keepLoaded?: boolean }) => {
    // A refused request (e.g. a 400 on a filter value) is not an "older backend": keep what
    // is on screen rather than wiping it and flipping to the legacy window.
    if ((rl as any)?.success === false) return rcaLogRef.current;
    const rlData = (rl as any)?.data ?? rl;
    const groups = parseRcaGroups<RcaLogItem>(rlData);
    const rlItems: RcaLogItem[] = groups
      ? groups.flatMap((g) => g.items)
      : Array.isArray(rlData)
        ? rlData
        : Array.isArray(rlData?.items)
          ? rlData.items
          : Array.isArray(rlData?.data?.items)
            ? rlData.data.items
            : [];
    const pick = (k: string) => rlData?.[k] ?? rlData?.data?.[k];
    const has = (k: string) => (rlData && typeof rlData === 'object' && k in rlData) || (rlData?.data && typeof rlData.data === 'object' && k in rlData.data);
    // A TRUS-687 backend always sends next_cursor (null at the end) or groups; an older one never does.
    const mode: 'paged' | 'legacy' = has('next_cursor') || groups || has('origin_counts') ? 'paged' : 'legacy';
    if (rlData) {
      lastRcaApiMode = mode;
      if (rcaApiModeRef.current !== mode) {
        rcaApiModeRef.current = mode;
        setRcaApiMode(mode);
      }
    }
    const cursor = typeof pick('next_cursor') === 'string' && pick('next_cursor') ? (pick('next_cursor') as string) : null;
    if (opts?.keepLoaded && !groups) {
      setRcaLog((prev) => {
        if (prev.length <= rlItems.length) return rlItems;
        // Reviewer has loaded more pages: refresh the first page in place, keep the rest.
        const fresh = new Set(rlItems.map((r) => String(r.id)));
        return [...rlItems, ...prev.slice(rlItems.length).filter((r) => !fresh.has(String(r.id)))];
      });
      setNextCursor((prev) => (prev && rcaLogRef.current.length > rlItems.length ? prev : cursor));
    } else {
      setRcaLog(rlItems);
      setNextCursor(cursor);
    }
    setRcaGroups(groups);
    setRcaCounts((pick('counts') ?? {}) as RcaCounts);
    setLabelCounts((pick('label_counts') ?? {}) as RcaCounts);
    setRcaTotal(Number(pick('total') ?? 0));
    setRcaFilteredTotal(Number(pick('filtered_total') ?? pick('total') ?? 0));
    setServerVerdictCounts((pick('verdict_counts') ?? {}) as RcaCounts);
    setServerJourneyCounts((pick('journey_counts') ?? {}) as RcaCounts);
    setPriorityCounts((pick('priority_counts') ?? {}) as Record<string, { rank: number; count: number }>);
    setDueCounts((pick('due_counts') ?? {}) as RcaCounts);
    setRollups(parseRcaRollups(rlData));
    return rlItems as RcaLogItem[];
  }, []);

  // The params of the last log request, so a re-render that rebuilds an identical param set
  // (e.g. the backend mode settling) doesn't refetch.
  const lastRcaParamsKey = useRef<string>('');

  // Refetch ONLY the log (for filter changes) — cheaper than the full load().
  const loadRcaLog = useCallback(
    async (params: RcaLogParams) => {
      lastRcaParamsKey.current = rcaParamsKey(params);
      setRcaLogLoading(true);
      try {
        const rl = await agentsApi.getRcaLog(params).catch(() => null);
        if (rl) applyRcaLog(rl);
      } finally {
        setRcaLogLoading(false);
      }
    },
    [applyRcaLog]
  );

  // "Load more": the next keyset page, appended (rollups stay those of the first page).
  const loadMoreRca = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const rl: any = await agentsApi.getRcaLog({ ...rcaParamsRef.current, cursor: nextCursor }).catch(() => null);
      const d = rl?.data ?? rl;
      const items: RcaLogItem[] = Array.isArray(d?.items) ? d.items : Array.isArray(d?.data?.items) ? d.data.items : [];
      if (!rl || rl.success === false) {
        flash('Could not load more tickets.');
        return;
      }
      setRcaLog((prev) => {
        const seen = new Set(prev.map((r) => String(r.id)));
        return [...prev, ...items.filter((r) => !seen.has(String(r.id)))];
      });
      const c = d?.next_cursor ?? d?.data?.next_cursor;
      setNextCursor(typeof c === 'string' && c ? c : null);
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextCursor, loadingMore]);

  const load = useCallback(async () => {
    try {
      const [reg, rn, sm, gt, rl, ep] = await Promise.all([
        agentsApi.getRegistry(prod).catch(() => null),
        agentsApi.getRuns({ limit: 200, product: prod }).catch(() => null),
        agentsApi.getSummary(prod).catch(() => null),
        agentsApi.getGated(prod).catch(() => null),
        agentsApi.getRcaLog((lastRcaParamsKey.current = rcaParamsKey(rcaParamsRef.current), rcaParamsRef.current)).catch(() => null),
        prod === 'weavein' ? agentsApi.listEpics().catch(() => null) : Promise.resolve(null),
      ]);
      // The platform API client wraps responses as { success, data }. Unwrap
      // .data (falling back to the raw value) before use — otherwise summary
      // fields are undefined and lists aren't arrays.
      const regData = (reg as any)?.data ?? reg;
      const rnData = (rn as any)?.data ?? rn;
      const smData = (sm as any)?.data ?? null;
      const gtData = (gt as any)?.data ?? gt;
      setAgents(Array.isArray(regData) ? regData : []);
      setRuns(Array.isArray(rnData) ? rnData : []);
      setSummary(smData && typeof smData.runs_24h === 'number' ? (smData as Summary) : null);
      // The gated payload is { items: [...], count }, but depending on the
      // envelope the array can arrive as gtData itself, gtData.items, or one
      // more level nested under .data — normalize to a flat GatedItem[].
      const gtItems = Array.isArray(gtData)
        ? gtData
        : Array.isArray(gtData?.items)
          ? gtData.items
          : Array.isArray(gtData?.data?.items)
            ? gtData.data.items
            : [];
      setGated(gtItems);
      // The RCA log payload ({ items, counts, total, filtered_total, … }) is applied via
      // the shared helper so load() and the filter-triggered loadRcaLog() stay in sync.
      if (rl) applyRcaLog(rl, { keepLoaded: true });
      const epData = (ep as any)?.data ?? ep;
      setEpics(Array.isArray(epData?.epics) ? epData.epics : []);
    } catch {
      /* endpoints degrade to empty */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [prod, applyRcaLog]);

  // Re-fetch the log (only) whenever a filter changes. Skips the initial render — the
  // mount fetch is owned by load() above — so we don't double-fetch on first paint.
  const rcaFiltersMounted = useRef(false);
  useEffect(() => {
    if (!rcaFiltersMounted.current) {
      rcaFiltersMounted.current = true;
      return;
    }
    if (rcaParamsKey(rcaParams) === lastRcaParamsKey.current) return;
    void loadRcaLog(rcaParams);
  }, [rcaParams, loadRcaLog]);

  // Coalesce refreshes.
  //
  // Production logs (2026-09-01, 00:50–00:52Z) show each of this page's six
  // endpoints — registry / gated / summary / runs / rca-epics / rca-log — being
  // fetched FIVE to SIX times per 60s cycle, staggered ~80-190ms apart, i.e.
  // load() itself is being invoked repeatedly rather than once. That is ~36
  // requests a minute from one page instead of 6.
  //
  // The trigger is not established: the interval below cleans up correctly,
  // `load` is stable (`prod` is a string, applyRcaLog has [] deps), and all four
  // <AgentControlPage> sites in app.tsx are mutually-exclusive activeTab
  // branches, so it is neither a timer leak nor concurrent mounts. The most
  // likely remaining cause is remount churn while the gating inputs
  // (isAuthenticated, user.role, activeOrgId) resolve, each mount firing its own
  // immediate load().
  //
  // Rather than guess, bound the damage at the source: one full refresh at a
  // time, and no more than one per MIN_REFRESH_GAP_MS.
  //
  // The guards are MODULE-level, deliberately, not useRef. If the cause is
  // remount churn then every remount is a new component instance with fresh
  // refs, which would reset a ref-based gap and suppress nothing — the exact
  // case this needs to cover. Module scope is per-tab and survives remounts.
  //
  // Neither the mount fetch nor the 60s poll passes `force`: the first call ever
  // has lastAgentLoadAt = 0 so it always runs, and the poll is 60s apart, far
  // outside the gap. `force` exists for explicit user actions, which must never
  // be swallowed just because a poll happened to land moments earlier.
  const loadCoalesced = useCallback(
    async (force = false) => {
      if (agentLoadInFlight) return;
      if (!force && Date.now() - lastAgentLoadAt < MIN_AGENT_REFRESH_GAP_MS) return;
      agentLoadInFlight = true;
      try {
        await load();
      } finally {
        agentLoadInFlight = false;
        lastAgentLoadAt = Date.now();
      }
    },
    [load]
  );

  useEffect(() => {
    void loadCoalesced();
    const id = window.setInterval(() => void loadCoalesced(), 60000);
    return () => window.clearInterval(id);
  }, [loadCoalesced]);

  // ---- /agents?job=<id> (TRUS-684): open that RCA job ---------------------------------
  // The job arrives either in the URL (full page load; consumed and stripped so a refresh
  // or Back doesn't re-open it) or parked by an in-app route (agentJobLink). Once the first
  // load has landed, the job is found in the list — else the filters are cleared, else it
  // is looked up by its ticket key — then expanded and scrolled into view.
  const [focusJobId, setFocusJobId] = useState<string | null>(() => {
    consumeAgentJobFromUrl();
    return peekPendingRcaJob();
  });
  const [focusedJobId, setFocusedJobId] = useState<string | null>(null);
  const [scrollToJobId, setScrollToJobId] = useState<string | null>(null);
  const rcaLogRef = useRef<RcaLogItem[]>(rcaLog);
  rcaLogRef.current = rcaLog;
  const focusRunning = useRef<string | null>(null);
  useEffect(() => {
    const onFocus = (e: Event) => {
      const id = normalizeJobId((e as CustomEvent<{ jobId?: string }>).detail?.jobId);
      if (id) setFocusJobId(id);
    };
    window.addEventListener(FOCUS_RCA_JOB_EVENT, onFocus);
    return () => window.removeEventListener(FOCUS_RCA_JOB_EVENT, onFocus);
  }, []);
  // Wait for this mount's first load so it cannot overwrite the list we fetch below. A
  // remount inside the refresh gap skips that load (loadCoalesced) and leaves `loading`
  // set, so only wait while a load is actually in flight — and re-check once it settles.
  const [focusRecheck, setFocusRecheck] = useState(0);
  useEffect(() => {
    if (!focusJobId) return;
    const id = window.setTimeout(() => setFocusRecheck((n) => n + 1), 3000);
    return () => window.clearTimeout(id);
  }, [focusJobId]);
  useEffect(() => {
    if (!focusJobId || focusRunning.current === focusJobId) return;
    if (loading && agentLoadInFlight) return;
    const jobId = focusJobId;
    focusRunning.current = jobId;
    const has = (items: RcaLogItem[] | null | undefined) => !!items?.some((r) => String(r.id) === jobId);
    setAgentTab('tickets');
    void (async () => {
      try {
        let found = has(rcaLogRef.current);
        if (!found) {
          // Not in the current view: drop every filter (and grouping) and look again.
          const { sort } = rcaParamsRef.current;
          const cleared: RcaLogParams = {
            sort,
            status: undefined, verdict: undefined, journey: undefined, label: undefined, q: undefined,
            ...(rcaApiModeRef.current === 'legacy' ? { limit: 500 } : null),
          };
          filters.clearAll({ replace: true, group: '' });
          rcaParamsRef.current = cleared;
          lastRcaParamsKey.current = rcaParamsKey(cleared);
          const rl = await agentsApi.getRcaLog(cleared).catch(() => null);
          if (rl) found = has(applyRcaLog(rl));
          if (!found) {
            // Past the fetched window: find its ticket key and search for it.
            const ctx = await agentsApi.getRcaContext(jobId).catch(() => null);
            const ticket = ((ctx as any)?.data ?? ctx)?.ticket;
            if (typeof ticket === 'string' && ticket.trim()) {
              const withQ = { ...cleared, q: ticket.trim() };
              setTicketQuery(ticket.trim());
              rcaParamsRef.current = withQ;
              lastRcaParamsKey.current = rcaParamsKey(withQ);
              const rq = await agentsApi.getRcaLog(withQ).catch(() => null);
              if (rq) found = has(applyRcaLog(rq));
            }
          }
        }
        if (found) {
          setExpandedLog((prev) => ({ ...prev, [jobId]: true }));
          loadTimeline(jobId);
          setFocusedJobId(jobId);
          setScrollToJobId(jobId);
        } else {
          flash(`RCA job ${jobId} was not found.`);
        }
      } finally {
        clearPendingRcaJob(jobId);
        setFocusJobId((cur) => (cur === jobId ? null : cur));
        focusRunning.current = null;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusJobId, loading, focusRecheck]);

  const flash = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast((t) => (t === msg ? null : t)), 3200);
  };

  const toggleStatus = async (a: Agent) => {
    const next = a.status === 'paused' ? 'active' : 'paused';
    setBusyKey(a.key);
    try {
      await agentsApi.updateAgent(a.key, { status: next });
      setAgents((prev) => prev.map((x) => (x.key === a.key ? { ...x, status: next } : x)));
      flash(`${a.display_name} ${next === 'paused' ? 'paused' : 'resumed'}.`);
    } catch {
      flash('Could not update the agent.');
    } finally {
      setBusyKey(null);
    }
  };

  const runNow = async (a: Agent) => {
    setBusyKey(a.key);
    try {
      await agentsApi.runAgent(a.key);
      flash(`${a.display_name} queued — a run is on its way.`);
    } catch {
      flash("This agent can't be run on demand.");
    } finally {
      setBusyKey(null);
    }
  };

  const setBudget = async (a: Agent) => {
    const current = a.budget_usd != null ? String(a.budget_usd) : '';
    const input = window.prompt(`Monthly budget ceiling for ${a.display_name} (USD). Blank = no limit.`, current);
    if (input === null) return;
    const val = input.trim() === '' ? 0 : Number(input);
    if (Number.isNaN(val) || val < 0) {
      flash('Enter a valid dollar amount.');
      return;
    }
    setBusyKey(a.key);
    try {
      const res: any = await agentsApi.updateAgent(a.key, { budget_usd: val });
      const nextBudget = (res?.data?.budget_usd ?? (val > 0 ? val : null)) as number | null;
      setAgents((prev) => prev.map((x) => (x.key === a.key ? { ...x, budget_usd: nextBudget } : x)));
      flash(val > 0 ? `Budget set to $${val}/mo for ${a.display_name}.` : `Budget cleared for ${a.display_name}.`);
    } catch {
      flash('Could not set the budget.');
    } finally {
      setBusyKey(null);
    }
  };

  const saveSchedule = async (a: Agent, cron: string) => {
    if (cron.trim().split(/\s+/).length !== 5) {
      flash('Enter a valid 5-field cron expression.');
      return;
    }
    setBusyKey(a.key);
    try {
      const res: any = await agentsApi.setAgentSchedule(a.key, { cron_expr: cron.trim() });
      const nextCron = res?.data?.cron_expr ?? cron.trim();
      setAgents((prev) => prev.map((x) => (x.key === a.key ? { ...x, cron_expr: nextCron } : x)));
      setScheduleAgent(null);
      flash(`Schedule updated for ${a.display_name} → ${humanizeCron(nextCron)}.`);
    } catch {
      flash('Could not update the schedule.');
    } finally {
      setBusyKey(null);
    }
  };

  const gateAction = async (item: GatedItem, action: 'approve' | 'reject' | 'done', toastMsg?: string) => {
    setBusyGate(item.id);
    try {
      if ((item as any).kind === 'output') {
        // Output review only knows approve/reject; treat 'done' as approve.
        await agentsApi.reviewAgentOutput(item.id, action === 'done' ? 'approve' : action);
      } else if (item.agent === 'marketing.nba_drafter') {
        if (action === 'reject') await marketingApi.rejectNbaRecommendation(item.id);
        else await marketingApi.approveNbaRecommendation(item.id);
      } else if (item.agent === 'ops.rca_agent') {
        await agentsApi.rcaGateAction(item.id, action);
      }
      setGated((prev) => prev.filter((g) => g.id !== item.id));
      flash(toastMsg ?? (action === 'approve' ? 'Approved.' : action === 'done' ? 'Marked complete.' : 'Dismissed.'));
    } catch {
      flash('Could not complete that action.');
    } finally {
      setBusyGate(null);
    }
  };

  // Persist a corrected verdict on a triaged RCA ticket without re-running the agent —
  // the sync guard for the Jira→rca_jobs drift that stranded TRUS-179 (reclassified in a
  // Jira comment only, so the DB kept the stale verdict and the console kept showing the
  // code-review affordance instead of the OPERATIONAL/CONFIGURATION hand-off). Reloads so
  // the affordance (Approve/Merge PR ↔ Hand off) flips in place.
  const reclassify = async (id: string, verdict: string) => {
    setBusyGate(id);
    try {
      await agentsApi.reclassifyRca(id, verdict);
      flash(`Reclassified as ${verdict}.`);
      await load();
    } catch {
      flash('Could not reclassify this ticket.');
    } finally {
      setBusyGate(null);
    }
  };

  // Gate action fired from an All-tickets row (RCA only). Same endpoints as
  // gateAction's ops.rca_agent branch, but reloads the log so the row's status
  // and timeline update in place (the gate section no longer carries RCA items).
  const rcaRowAction = async (
    id: string,
    action: 'approve' | 'reject' | 'done' | 'approve-spec' | 'defer' | 'decline',
    toastMsg?: string,
  ) => {
    setBusyGate(id);
    try {
      await agentsApi.rcaGateAction(id, action);
      flash(toastMsg ?? (action === 'approve' ? 'Approved.' : action === 'done' ? 'Marked complete.' : 'Dismissed.'));
      await load();
    } catch {
      flash('Could not complete that action.');
    } finally {
      setBusyGate(null);
    }
  };

  // Mark an OPERATIONAL task complete WITH the result of the manual action, so the
  // console records what was done (not just a closed status). Empty/cancelled → falls
  // back to the plain 'done' gate action.
  const rcaMarkComplete = async (id: string) => {
    const entered = window.prompt('What was done? (recorded as the result on this task)');
    if (entered === null) return;
    const result = entered.trim();
    setBusyGate(id);
    try {
      if (result) await agentsApi.rcaCompleteManual(id, result);
      else await agentsApi.rcaGateAction(id, 'done');
      flash('Marked complete.');
      await load();
    } catch {
      flash('Could not complete that task.');
    } finally {
      setBusyGate(null);
    }
  };

  // Close a ticket resolved OUTSIDE the pipeline (fixed in a separate PR / config / already
  // handled) — sets it to done and closes the linked Jira issue, WITHOUT approving a build.
  const rcaResolve = async (id: string) => {
    const entered = window.prompt('Resolution note (optional) — how / where was this fixed?');
    if (entered === null) return; // cancelled
    const note = entered.trim();
    setBusyGate(id);
    try {
      const res = await agentsApi.rcaResolve(id, note || undefined);
      flash(res?.jira_transitioned ? 'Resolved & Jira closed.' : 'Resolved & closed.');
      await load();
    } catch {
      flash('Could not resolve that ticket.');
    } finally {
      setBusyGate(null);
    }
  };

  // --- Multi-select bulk actions on the All-tickets log ---
  const toggleRcaSelect = (id: string) =>
    setSelectedRca((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearRcaSelection = () => setSelectedRca(new Set());
  // Bulk mark done + close Jira for every selected ticket. One optional explanation applies
  // to all; the backend skips ids that are already terminal / out of scope.
  const bulkMarkDone = async () => {
    const ids = Array.from(selectedRca);
    if (!ids.length) return;
    const entered = window.prompt(
      `Mark ${ids.length} ticket(s) Done and close them in Jira.\n\nResolution note (optional) — applied to all selected:`,
    );
    if (entered === null) return; // cancelled
    const note = entered.trim();
    setBulkBusy(true);
    try {
      const res = await agentsApi.rcaBulkAction(ids, 'done', note || undefined);
      const done = res?.done?.length ?? 0;
      const skipped = res?.skipped?.length ?? 0;
      flash(
        `Marked ${done} done (Jira closed: ${res?.jira_closed ?? 0})` +
          (skipped ? ` · ${skipped} skipped (already closed/out of scope)` : ''),
      );
      clearRcaSelection();
      await load();
    } catch {
      flash('Could not bulk-close those tickets.');
    } finally {
      setBulkBusy(false);
    }
  };

  // Set one part's status on a multi-part OPERATIONAL ticket's progress trail.
  const rcaSetPartStatus = async (id: string, n: number, status: string) => {
    setBusyGate(id);
    try {
      await agentsApi.rcaUpdatePart(id, n, status);
      flash('Part updated.');
      await load();
    } catch {
      flash('Could not update that part.');
    } finally {
      setBusyGate(null);
    }
  };

  // Re-queue a ticket with the reviewer's added context. implement=false → re-triage;
  // implement=true → confirm a product decision and have the agent BUILD it. Shared by
  // the gate queue and the All-tickets rows.
  const submitReanalyze = async (id: string, implement = false) => {
    const text = (reanalyzeText[id] || '').trim();
    if (!text) return;
    setReanalyzeBusy(id);
    try {
      await agentsApi.reanalyzeRca(id, text, implement);
      flash(implement ? 'Approved — handed to the agent to build' : 'Re-queued for re-analysis');
      setReanalyzeText((prev) => ({ ...prev, [id]: '' }));
      setReanalyzeOpen((prev) => ({ ...prev, [id]: false }));
      await load();
    } catch {
      flash('Could not re-queue that ticket.');
    } finally {
      setReanalyzeBusy(null);
    }
  };

  // Set an Epic's mode: 'auto' triages children as they arrive (and releases any held);
  // 'hold' parks new children until released.
  const changeEpicMode = async (epicKey: string, mode: 'auto' | 'hold' | 'closed') => {
    setEpicBusy(epicKey);
    try {
      const res: any = await agentsApi.setEpicMode(epicKey, mode);
      const data = res?.data ?? res;
      const released = data?.released ?? 0;
      const closedN = data?.closed_children ?? 0;
      flash(
        mode === 'auto'
          ? `Epic set to auto-triage${released ? ` — released ${released} held` : ''}`
          : mode === 'closed'
            ? `Epic closed${closedN ? ` — ${closedN} ticket(s) retired` : ''}`
            : 'Epic set to hold',
      );
      await load();
    } catch {
      flash('Could not update that Epic.');
    } finally {
      setEpicBusy(null);
    }
  };

  // Hold a ticket awaiting a response from the creator: posts the reviewer's
  // question on the Jira issue and notifies admins in-app. Shared by the gate
  // queue and the All-tickets rows.
  const submitHold = async (id: string) => {
    const text = (holdText[id] || '').trim();
    if (!text) return;
    setHoldBusy(id);
    try {
      await agentsApi.holdRca(id, text);
      flash('Held — creator notified via Jira');
      setHoldText((prev) => ({ ...prev, [id]: '' }));
      setHoldOpen((prev) => ({ ...prev, [id]: false }));
      await load();
    } catch {
      flash('Could not hold that ticket.');
    } finally {
      setHoldBusy(null);
    }
  };

  // Merge a ticket's drafted PR straight from the console. A merged:false
  // response (failing CI / conflict / draft) is EXPECTED — surface the reason
  // inline rather than treating it as a hard error. A thrown 503 means GitHub
  // isn't wired up on the API.
  const mergePr = async (id: string) => {
    setMergeBusy(id);
    setMergeReason((prev) => ({ ...prev, [id]: '' }));
    try {
      const res: any = await agentsApi.mergeRca(id);
      // getPlatformApi() wraps responses in { success, data, error } and does
      // NOT throw on non-2xx. A 503 (GitHub unconfigured) comes back as
      // success:false; a merged:false verdict comes back inside data.
      if (res && res.success === false) {
        const msg = res.error?.message || '';
        // Backend 503 when GitHub isn't wired up on the API.
        if (/github/i.test(msg) || /not configured/i.test(msg)) {
          flash('GitHub not connected — set GITHUB_TOKEN on the API');
        } else {
          flash('Could not merge that PR.');
        }
        return;
      }
      const data = res?.data ?? res;
      if (data?.merged === true) {
        flash(data?.already ? 'Already merged' : 'Merged');
        await load();
        // Refresh the resolution journey so an already-open stepper flips to
        // Merged/Deployed instead of showing stale pre-merge state.
        if (expandedJourney[id]) loadJourney(id);
      } else {
        // Expected non-merge (failing CI / conflict / draft) — surface the
        // reason inline; it is not a hard error.
        const reason = data?.reason || 'PR is not mergeable right now.';
        setMergeReason((prev) => ({ ...prev, [id]: reason }));
        setMergeConflict((prev) => ({ ...prev, [id]: !!(data?.conflict || data?.behind) }));
        setMergeCiFail((prev) => ({ ...prev, [id]: data?.ci_state === 'failed' }));
        // The reason + fallbacks render in the expanded ticket; open it so a merge started from
        // the summary line doesn't fail silently.
        setExpandedLog((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
      }
    } catch {
      flash('Could not merge that PR.');
    } finally {
      setMergeBusy(null);
    }
  };

  // Merge-conflict fallback #1 — ask GitHub to merge the latest base into a stale
  // PR head. Clears the conflict banner on success; if GitHub can't (real conflicts),
  // point the admin at the hand-back button.
  const updateBranch = async (id: string) => {
    setMergeBusy(id);
    try {
      const res: any = await agentsApi.updateRcaBranch(id);
      const data = res?.data ?? res;
      if (res?.success === false || data?.updated === false) {
        const reason = data?.reason || res?.error?.message || 'Could not update the branch.';
        setMergeReason((prev) => ({ ...prev, [id]: reason + (data?.needs_agent ? ' — hand it back to the agent to resolve.' : '') }));
      } else {
        setMergeReason((prev) => ({ ...prev, [id]: '' }));
        setMergeConflict((prev) => ({ ...prev, [id]: false }));
        flash(data?.message || 'Branch updated — wait for CI, then merge.');
        await load();
      }
    } catch {
      flash('Could not update the branch.');
    } finally {
      setMergeBusy(null);
    }
  };

  // Hand the PR back to the agent to FIX the blocker (merge conflict OR red CI). Passes the
  // reason so the agent fixes the right thing (rebase vs. repair the failing build/tests).
  const handBackToAgent = async (id: string, reason?: string) => {
    setMergeBusy(id);
    try {
      await agentsApi.resolveRcaConflicts(id, reason);
      setMergeReason((prev) => ({ ...prev, [id]: '' }));
      setMergeConflict((prev) => ({ ...prev, [id]: false }));
      setMergeCiFail((prev) => ({ ...prev, [id]: false }));
      flash('Handed back to the agent to fix — it will update the PR.');
      await load();
    } catch {
      flash('Could not hand that ticket back to the agent.');
    } finally {
      setMergeBusy(null);
    }
  };

  // Whether the "Add context — re-analyze or build" action applies. Available on ANY
  // triaged ticket in a reviewable state (incl. BUG) — a mixed BUG+FEATURE ticket like
  // TRUS-166 needs it so the shelved feature asks can be re-analyzed or approved-to-build
  // rather than lost when the bug part closes.
  // 'held' lets an admin reviewing a works-as-designed / closed-epic hold add context and send
  // it back for a fresh look; 'pending'/'claimed' (TRUS-618) keep the "Add context" control
  // available after a first instruction re-queues the job, so a SECOND follow-up isn't lost.
  const canReanalyze = (_verdict?: string | null, status?: string | null) => isReanalyzableStatus(status);
  // A reviewer can hold any ticket that isn't already terminal (or already held)
  // — awaiting_review is the primary case, but any non-terminal ticket qualifies.
  const canHold = (status?: string | null) => {
    const s = (status || '').toLowerCase();
    return s !== 'done' && s !== 'reviewed' && s !== 'failed' && s !== 'skipped' && s !== 'held';
  };

  // Roll runs up per agent (match run.task_type to a registry key suffix).
  const statsByKey = useMemo(() => {
    const m: Record<string, { last?: string; count: number; cost: number; failed: number }> = {};
    for (const r of runs) {
      const key = agents.find((a) => a.key === `${a.product}.${r.task_type}` || a.key.endsWith(`.${r.task_type}`))?.key;
      const k = key || r.task_type;
      if (!m[k]) m[k] = { count: 0, cost: 0, failed: 0 };
      m[k].count += 1;
      m[k].cost += r.cost_usd || 0;
      if (r.status === 'failed') m[k].failed += 1;
      if (!m[k].last || (r.at && r.at > (m[k].last as string))) m[k].last = r.at || undefined;
    }
    return m;
  }, [runs, agents]);

  const tiles = [
    { label: 'Runs · 24h', value: summary ? summary.runs_24h.toLocaleString() : '—', icon: Clock, tone: tvTokens.ink },
    { label: 'Failed · 24h', value: summary ? String(summary.failed_24h) : '—', icon: AlertTriangle, tone: summary && summary.failed_24h > 0 ? '#cc3b57' : tvTokens.ink },
    { label: 'Awaiting review', value: summary ? String(summary.gated) : '—', icon: ShieldCheck, tone: summary && summary.gated > 0 ? '#5a4fcf' : tvTokens.ink },
    { label: 'In queue', value: summary && summary.queued_rca != null ? String(summary.queued_rca) : '—', icon: Inbox, tone: tvTokens.ink },
  ];

  const canControl = summary?.can_control ?? true;
  const filteredAgents = kindFilter === 'all' ? agents : agents.filter((a) => a.kind === kindFilter);

  // Triage-log status chips: derived entirely from the server's counts map, so
  // EVERY status present gets a chip (incl 'reviewed' → "Approved") — nothing
  // disappears. 'All' always leads; the rest sort by the display order.
  const rcaChips = [
    'all',
    ...Object.keys(rcaCounts)
      .filter((s) => (rcaCounts[s] ?? 0) > 0)
      .sort((a, b) => statusRank(a) - statusRank(b)),
  ];
  // Verdict (Type) filter options + client-side counts over the full rcaLog.
  // Value is matched case-insensitively against item.verdict; a null verdict
  // matches only "All".
  const VERDICT_OPTIONS: { value: string; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'bug', label: 'Bug' },
    { value: 'gap', label: 'Gap' },
    { value: 'feature', label: 'Feature' },
    { value: 'operational', label: 'Operational' },
    { value: 'configuration', label: 'Configuration' },
    { value: 'preliminary', label: 'Preliminary' },
    { value: 'works_as_designed', label: 'Works-as-designed' },
    { value: 'invalid', label: 'Invalid' },
    { value: 'duplicate', label: 'Duplicate' },
  ];
  // Normalize a verdict to a comparable token (uppercase → lowercase, spaces
  // and dashes → underscore) so 'WORKS_AS_DESIGNED' / 'works-as-designed' match.
  const normVerdict = (v?: string | null) => (v || '').toLowerCase().replace(/[\s-]+/g, '_');
  // True total across the whole log (not the fetched window). Falls back to the loaded
  // row count if an older backend didn't send `total`.
  const totalTickets = rcaTotal || rcaLog.length;
  const verdictCounts = useMemo(() => {
    // Prefer the server's full-set rollup so each Type chip shows its true total; fall
    // back to a client-side scan of the loaded window for older backends.
    const m: Record<string, number> = { all: totalTickets };
    const serverKeys = Object.keys(serverVerdictCounts);
    if (serverKeys.length > 0) {
      for (const k of serverKeys) m[k] = Number(serverVerdictCounts[k]) || 0;
    } else {
      for (const r of rcaLog) {
        const v = normVerdict(r.verdict);
        if (v) m[v] = (m[v] || 0) + 1;
      }
    }
    return m;
  }, [serverVerdictCounts, rcaLog, totalTickets]);

  // Resolution-journey stage predicates — the actionable states an admin must not
  // lose track of. needs_merge = a PR is drafted/approved but the ticket isn't done
  // (awaiting a merge + auto-deploy); building = handed off, agent working; held =
  // blocked on the creator; awaiting = needs review.
  const journeyMatch = (r: RcaLogItem, key: string): boolean => {
    const st = (r.status || '').toLowerCase();
    if (key === 'intake') return st === 'held' && (r.held_for || '').toLowerCase() === 'intake';
    if (key === 'held') return st === 'held';
    if (key === 'needs_merge') return !!r.pr_url && st !== 'done' && st !== 'skipped';
    if (key === 'building') return st === 'pending' && !!r.build_requested;
    if (key === 'awaiting') return st === 'awaiting_review';
    return true;
  };
  const journeyStages: { key: string; label: string; tone: string }[] = [
    { key: 'intake', label: 'Intake review', tone: '#5a4fcf' },
    { key: 'awaiting', label: 'Needs review', tone: '#5a4fcf' },
    { key: 'needs_merge', label: 'Needs merge & deploy', tone: '#b9781f' },
    { key: 'building', label: 'Agent building', tone: '#46739e' },
    { key: 'held', label: 'Held — creator', tone: '#cc3b57' },
  ];
  const journeyCounts = useMemo(() => {
    // Prefer the server's full-set rollup; fall back to a client-side scan of the
    // loaded window for older backends that didn't send journey_counts.
    const stages = ['intake', 'awaiting', 'needs_merge', 'building', 'held'];
    const m: Record<string, number> = {};
    if (Object.keys(serverJourneyCounts).length > 0) {
      for (const s of stages) m[s] = Number(serverJourneyCounts[s]) || 0;
    } else {
      for (const s of stages) m[s] = rcaLog.filter((r) => journeyMatch(r, s)).length;
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverJourneyCounts, rcaLog]);

  // Priorities present across the log, most urgent first: [filterKey, label, count, rank].
  // '' (no priority) becomes the 'none' filter key the backend understands.
  const priorityChips: [string, string, number, number][] = Object.entries(priorityCounts)
    .filter(([, v]) => (v?.count ?? 0) > 0)
    .map(([p, v]): [string, string, number, number] => [
      p || 'none',
      p ? p.charAt(0).toUpperCase() + p.slice(1) : 'None',
      v.count,
      p ? v.rank : PRIORITY_UNRANKED + 1,
    ])
    .sort((a, b) => a[3] - b[3] || a[1].localeCompare(b[1]));
  // Labels present across the log, most-used first, for the "filter by label" chip row.
  const labelChips = Object.keys(labelCounts)
    .filter((l) => (labelCounts[l] ?? 0) > 0)
    .sort((a, b) => (labelCounts[b] ?? 0) - (labelCounts[a] ?? 0) || a.localeCompare(b));

  const trimmedTicketQuery = ticketQuery.trim().toLowerCase();
  const filtersActive = filters.filtersActive;
  // Filtering is authoritative on the SERVER (see rcaParams / loadRcaLog) so the fetched
  // window is drawn from the full matching set. This client pass mirrors the same
  // predicates as a thin optimistic layer: it gives instant feedback on a chip click
  // before the refetch lands (and still works if talking to an older backend). On fresh
  // server-filtered data it's a no-op. OR within an axis, AND across axes; the TRUS-687
  // axes only apply to rows that carry the field (an older backend doesn't send them),
  // and `me` can only be resolved by the server.
  const anyOf = (vals: string[], pred: (v: string) => boolean) => vals.length === 0 || vals.some(pred);
  const rowMatchesFilters = (r: RcaLogItem): boolean => {
    if (!anyOf(fAxes.status, (s) => (r.status || '').toLowerCase() === s)) return false;
    if (!anyOf(fAxes.verdict, (v) => normVerdict(r.verdict) === v)) return false;
    if (!anyOf(fAxes.journey, (j) => journeyMatch(r, j))) return false;
    if (
      trimmedTicketQuery &&
      ![r.ticket, r.title, r.ticket_number, r.jira_key].some((f) => (f || '').toLowerCase().includes(trimmedTicketQuery))
    )
      return false;
    if (!anyOf(fAxes.label, (l) => (r.labels || []).includes(l))) return false;
    if (!anyOf(fAxes.priority, (pf) => { const p = (r.priority || '').trim().toLowerCase(); return pf === 'none' ? p === '' : p === pf; })) return false;
    if (!anyOf(fAxes.due, (d) => dueMatch(r, d))) return false;
    if ('cbo_id' in r && !anyOf(fAxes.org, (o) => (o === 'none' ? r.cbo_id == null : String(r.cbo_id) === o))) return false;
    if ('origin' in r && !anyOf(fAxes.origin, (o) => (o === 'none' ? !r.origin : r.origin === o))) return false;
    const personIs = (p: RcaPerson | null | undefined, v: string) =>
      v === 'me' ? true : v === 'none' ? !p || p.user_id == null : p?.user_id != null && String(p.user_id) === v;
    if ('reporter' in r && !anyOf(fAxes.reporter, (v) => personIs(r.reporter, v))) return false;
    if ('assignee' in r && !anyOf(fAxes.assignee, (v) => personIs(r.assignee, v))) return false;
    if ('issue_type' in r && !anyOf(fAxes.issue_type, (v) => (r.issue_type || 'none').toLowerCase() === v.toLowerCase())) return false;
    if ('jira_status_category' in r && !anyOf(fAxes.jira_category, (v) => r.jira_status_category === v)) return false;
    if (fAxes.has_pr.length) {
      const hasPr = r.has_pr ?? !!r.pr_url;
      if (fAxes.has_pr[0] === 'true' ? !hasPr : hasPr) return false;
    }
    // TRUS-717: waiting-on include + the exclusion axes — the client mirror of the server's
    // _WAITING_ON_SQL and *_exclude predicates. `me` is resolved server-side, so a
    // reporter_exclude=me never hides a row here (the refetch applies it).
    const who = waitingOn(r);
    if (!anyOf(fAxes.waiting_on, (v) => v === who)) return false;
    if (who != null && fAxes.waiting_on_exclude.includes(who)) return false;
    if (fAxes.reporter_exclude.length) {
      const rid = r.reporter?.user_id;
      if (rid != null && fAxes.reporter_exclude.includes(String(rid))) return false;
      if (fAxes.reporter_exclude.includes('none') && (r.reporter == null || r.reporter.user_id == null)) return false;
    }
    if (fAxes.org_exclude.length) {
      if (r.cbo_id != null && fAxes.org_exclude.includes(String(r.cbo_id))) return false;
      if (fAxes.org_exclude.includes('none') && r.cbo_id == null) return false;
    }
    return true;
  };
  const filteredRcaLog = rcaLog.filter(rowMatchesFilters);
  const activeFilterCount = filters.activeCount;
  const clearRcaFilters = () => filters.clearAll();
  // Scroll a focused job's row into view once it is rendered (TRUS-684).
  useEffect(() => {
    if (!scrollToJobId) return;
    const el = document.getElementById(`rca-job-${scrollToJobId}`);
    if (!el) return;
    el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    setScrollToJobId(null);
  });
  const toggleSort = (key: string) =>
    filters.setSort(
      logSort.key === key
        ? { key: key as RcaSortKey, dir: logSort.dir === 'asc' ? 'desc' : 'asc' }
        : { key: key as RcaSortKey, dir: key === 'updated' ? 'desc' : 'asc' },
    );
  // Tickets with no due date always sit after dated ones, whichever way the column points.
  const NO_DUE = '9999-12-31';
  const sortRcaRows = (rows: RcaLogItem[]): RcaLogItem[] => {
    const STATUS_RANK: Record<string, number> = { awaiting_review: 0, held: 1, pending: 2, claimed: 3, reviewed: 4, epic_hold: 5, done: 6, skipped: 7, failed: 8 };
    const ticketNum = (t?: string | null) => { const m = (t || '').match(/(\d+)\s*$/); return m ? parseInt(m[1], 10) : 0; };
    const val = (r: RcaLogItem): string | number => {
      switch (logSort.key) {
        case 'ticket': return ticketNum(r.ticket);
        case 'status': return STATUS_RANK[(r.status || '').toLowerCase()] ?? 99;
        case 'verdict': return (r.verdict || '~').toUpperCase();
        case 'source': return (r.source || '~');
        case 'action': return (r.status || '').toLowerCase() === 'awaiting_review' ? 0 : 1;
        case 'priority': return priorityRank(r);
        case 'due': return r.due_date || NO_DUE;
        case 'created': return r.created_at || '';
        case 'org': return (r.organization || (r.cbo_id != null ? `cbo ${r.cbo_id}` : '~')).toLowerCase();
        case 'origin': return r.origin || '~';
        case 'assignee': return (r.assignee?.name || r.assignee?.email || '~').toLowerCase();
        default: return r.updated_at || '';
      }
    };
    const dir = logSort.dir === 'asc' ? 1 : -1;
    const cmp = (av: string | number, bv: string | number) => (av < bv ? -1 : av > bv ? 1 : 0);
    return [...rows].sort((a, b) => {
      // Undated rows stay last on a due sort in either direction.
      if (logSort.key === 'due' && !a.due_date !== !b.due_date) return a.due_date ? -1 : 1;
      const primary = cmp(val(a), val(b)) * dir;
      if (primary) return primary;
      // Priority ties break on the nearest due date; due ties on priority — the same
      // compound order the server uses for sort=priority / sort=due.
      if (logSort.key === 'priority') {
        const d = cmp(a.due_date || NO_DUE, b.due_date || NO_DUE);
        if (d) return d;
      } else if (logSort.key === 'due') {
        const p = priorityRank(a) - priorityRank(b);
        if (p) return p;
      }
      return ticketNum(a.ticket) - ticketNum(b.ticket); // stable tiebreak
    });
  };
  const sortedRcaLog = sortRcaRows(filteredRcaLog);
  // group_by (TRUS-687): the server's sections, each filtered + sorted like the flat list.
  const sortedRcaGroups = activeGroup && rcaGroups
    ? rcaGroups.map((g) => ({ ...g, items: sortRcaRows(g.items.filter(rowMatchesFilters)) }))
    : null;
  // "Show more in this group": narrow the list to the group (dropping the grouping) when the
  // axis has a filter; otherwise (Epic, or a null bucket) ask for more rows per group.
  const showMoreInGroup = (g: RcaGroup<RcaLogItem>) => {
    if (!activeGroup) return;
    const f = groupFilterFor(activeGroup, g.key);
    if (f) {
      filters.applyView({ ...filters.viewParams(), [f.axis]: f.value, group_by: undefined });
    } else {
      setGroupLimit((n) => n + 25);
    }
  };
  // Grid template shared by the All-tickets header + rows: Status | Type | Ticket |
  // Priority | Due | Title | Jira | Updated | Action.
  // Leading 30px checkbox column for multi-select (control users only).
  const LOG_COLS = (canControl ? '30px ' : '') + '140px 92px 104px 76px 84px minmax(180px,1fr) 116px 66px 222px';
  // Statuses that a bulk "mark done" can act on (mirrors the backend's live set). Terminal
  // rows (done/failed) are excluded so select-all doesn't grab already-closed tickets.
  const isBulkEligible = (status?: string | null) =>
    !['done', 'failed'].includes((status || '').toLowerCase());
  // RCA tickets now live in the All-tickets list (with inline actions), so the shared
  // gate section only carries non-RCA drafts (Triveny NBA outreach). Keeps the two
  // queues from double-listing RCA items.
  const gatedNonRca = gated.filter((g) => g.kind !== 'rca' && g.agent !== 'ops.rca_agent');

  // The filter rows (TRUS-687): the axes the console always had, then provenance & people
  // from the server rollups (each row shown only when there's data for it).
  const filterAxes: FilterAxisDef[] = [
    {
      axis: 'verdict',
      label: 'Type',
      icon: <Filter size={11} />,
      allChip: { label: 'All', count: verdictCounts.all ?? 0 },
      options: VERDICT_OPTIONS.filter((v) => v.value !== 'all').map((v) => ({ value: v.value, label: v.label, count: verdictCounts[v.value] ?? 0 })),
    },
    {
      axis: 'status',
      label: 'Resolution',
      allChip: { label: 'All', count: totalTickets },
      options: rcaChips.filter((x) => x !== 'all').map((x) => ({ value: x, label: statusLabel(x), count: rcaCounts[x] ?? 0 })),
    },
    {
      axis: 'journey',
      label: 'Needs action',
      options: journeyStages
        .filter((x) => (journeyCounts[x.key] ?? 0) > 0)
        .map((x) => ({ value: x.key, label: x.label, count: journeyCounts[x.key] ?? 0, tone: x.tone })),
    },
    { axis: 'label', label: 'Label', options: labelChips.map((l) => ({ value: l, label: l, count: labelCounts[l] ?? 0 })) },
    {
      axis: 'priority',
      label: 'Priority',
      options: priorityChips.map(([key, label, n, rank]) => ({ value: key, label, count: n, tone: rank <= 2 ? priorityTone(rank).color : undefined })),
    },
    {
      axis: 'due',
      label: 'Due',
      options: DUE_OPTIONS.filter((o) => Number(dueCounts[o.key] ?? 0) > 0).map((o) => ({ value: o.key, label: o.label, count: Number(dueCounts[o.key] ?? 0), tone: o.tone })),
    },
    // TRUS-717: filter by who the ticket is waiting on (the hand-off stage, not the Jira
    // assignee), plus a one-click "hide waiting on Vineet" (the "My review queue" ask).
    // Server-enforced, so new-backend only (a legacy window would silently ignore the param).
    ...(paged
      ? ([
          {
            axis: 'waiting_on',
            label: 'Waiting on',
            options: WAITING_ON_ORDER.map((w) => ({ value: w, label: WAITING_ON_LABEL[w] })),
          },
          {
            axis: 'waiting_on_exclude',
            label: 'Hide',
            options: [{ value: 'vineet', label: 'Waiting on Vineet' }],
          },
        ] as FilterAxisDef[])
      : []),
    ...buildProvenanceAxes(rollups, paged),
  ];
  const applySavedView = (v: RcaSavedView) => {
    setGroupLimit(10);
    filters.applyView(v.params);
  };
  // The view the current filters match (sort direction aside), so its name shows on the menu.
  const isActiveView = (v: RcaSavedView) => {
    const { state, q } = stateFromViewParams(v.params, fState);
    return sameFilterState({ ...state, sort: fState.sort }, { ...fState }) && q.trim() === ticketQuery.trim();
  };

  // One ticket row (flat list and group sections share it).
  const renderRcaRow = (t: RcaLogItem) => {
                  const open = !!expandedLog[t.id];
                  const lc = lifecycle(t);
                  const vt = verdictTone(t.verdict);
                  const proposed = t.proposed_changes || '';
                  const summaryText = t.summary || '';
                  const chip: React.CSSProperties = { ...pillMono, fontSize: 10, padding: '1px 7px' };
                  const toggle = () =>
                    setExpandedLog((prev) => {
                      const next = !prev[t.id];
                      if (next) loadTimeline(t.id); // lazy-load the activity thread on first open
                      return { ...prev, [t.id]: next };
                    });
                  const cell: React.CSSProperties = { minWidth: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 };
                  const prNum = (t.pr_url && (t.pr_url.match(/\/pull\/(\d+)/) || [])[1]) || null;
                  // A failed ticket is a dead-end needing a human — highlight it so it can't
                  // be lost in the log (red left accent + faint red wash).
                  const isFailed = (t.status || '').toLowerCase() === 'failed';
                  return (
                    <div
                      key={t.id}
                      id={`rca-job-${t.id}`}
                      data-testid={`rca-job-${t.id}`}
                      data-focused={focusedJobId === String(t.id) || undefined}
                      style={{
                        borderTop: `1px solid ${tvTokens.borderMuted}`,
                        ...(isFailed ? { borderLeft: '3px solid #cc3b57', background: '#fdf2f4' } : null),
                        ...(focusedJobId === String(t.id) ? { outline: `2px solid ${tvTokens.accentInk}`, outlineOffset: -2 } : null),
                      }}
                    >
                      {/* Columnar row — cells click to expand; last cell holds inline actions */}
                      <div style={{ display: 'grid', gridTemplateColumns: LOG_COLS, alignItems: 'center', gap: 8, padding: '8px 16px' }}>
                        {canControl && (
                          <div style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              aria-label={`Select ${t.ticket || t.id}`}
                              checked={selectedRca.has(String(t.id))}
                              disabled={!isBulkEligible(t.status)}
                              onChange={() => toggleRcaSelect(String(t.id))}
                              style={{ cursor: isBulkEligible(t.status) ? 'pointer' : 'not-allowed', width: 14, height: 14 }}
                            />
                          </div>
                        )}
                        <div style={cell} onClick={toggle}>
                          {(() => {
                            // TRUS-611: "<state> · Next: <step>" — the state is the pill, the next
                            // step sits under it so the reviewer sees what to do without expanding.
                            const [state, next] = lc.label.split(' · Next: ');
                            return (
                              <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                <span style={{ ...chip, color: lc.color, background: lc.background, whiteSpace: next ? 'normal' : 'nowrap', lineHeight: 1.3 }}>{state}</span>
                                {next && (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 700, color: lc.color }}>
                                    <ArrowRight size={10} /> Next: {next}
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                        <div style={cell} onClick={toggle}>
                          {t.verdict ? <span style={{ ...chip, color: vt.color, background: vt.background }} title={t.verdict}>{t.verdict.toUpperCase() === 'WORKS-AS-DESIGNED' ? 'As-designed' : t.verdict}</span> : <span style={{ color: tvTokens.ink400 }}>—</span>}
                        </div>
                        <div style={{ ...cell, overflow: 'hidden', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }} onClick={toggle} title={ticketIdentity(t).title}>
                          <span style={{ fontFamily: tvTokens.fontMono, fontSize: 11.5, fontWeight: 600, color: tvTokens.ink700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, maxWidth: '100%' }}>
                            {ticketIdentity(t).primary || '—'}
                          </span>
                          {ticketIdentity(t).jira && (
                            <a
                              href={ticketIdentity(t).jiraUrl || undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ fontFamily: tvTokens.fontMono, fontSize: 10, color: tvTokens.accentInk, textDecoration: 'none', whiteSpace: 'nowrap' }}
                              title="Open the Jira issue this ticket is mirrored to"
                            >
                              {ticketIdentity(t).jira}
                            </a>
                          )}
                        </div>
                        <div style={cell} onClick={toggle}>
                          {t.priority ? (() => {
                            const pt = priorityTone(priorityRank(t));
                            return (
                              <span style={{ ...chip, color: pt.color, background: pt.background, border: pt.background === 'transparent' ? `1px solid ${tvTokens.border}` : 'none', whiteSpace: 'nowrap' }} title={`Jira priority: ${t.priority}`}>
                                {t.priority}
                              </span>
                            );
                          })() : <span style={{ color: tvTokens.ink400 }}>—</span>}
                        </div>
                        <div style={{ ...cell, flexDirection: 'column', alignItems: 'flex-start', gap: 1 }} onClick={toggle} title={t.due_date ? `Jira due date: ${t.due_date}` : 'No due date in Jira'}>
                          {(() => {
                            const days = dueInDays(t.due_date);
                            if (days === null) return <span style={{ color: tvTokens.ink400 }}>—</span>;
                            const isOpen = !isSettled(t.status);
                            const [y, m, d] = (t.due_date || '').slice(0, 10).split('-').map(Number);
                            const label = new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                            const hint = !isOpen ? null : days < 0 ? `${-days}d overdue` : days === 0 ? 'due today' : days <= 7 ? `in ${days}d` : null;
                            const tone = !isOpen ? tvTokens.ink400 : days < 0 ? '#cc3b57' : days <= 1 ? '#b9781f' : tvTokens.ink500;
                            return (
                              <>
                                <span style={{ fontSize: 11.5, fontWeight: isOpen && days <= 1 ? 700 : 500, color: tone, whiteSpace: 'nowrap' }}>{label}</span>
                                {hint && <span style={{ fontSize: 10, fontWeight: 600, color: tone, whiteSpace: 'nowrap' }}>{hint}</span>}
                              </>
                            );
                          })()}
                        </div>
                        <div style={{ ...cell, overflow: 'hidden', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: 1 }} onClick={toggle}>
                          <span style={{ fontSize: 13, color: tvTokens.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                            {t.title || <span style={{ color: tvTokens.ink400 }}>(no title)</span>}
                          </span>
                          {/* TRUS-717: labelled creator / organization / waiting-on (from the
                              hand-off stage, not the Jira assignee). New-backend only — a legacy
                              window sends none of these columns, so the line stays as it was. */}
                          {paged && <RcaPeopleLine id={String(t.id)} reporter={t.reporter} organization={t.organization} waitingOn={waitingOn(t)} />}
                        </div>
                        <div style={{ ...cell, overflow: 'hidden', flexWrap: 'wrap', rowGap: 2 }} onClick={toggle}>
                          {/* TRUS-687: where the ticket came from (CS widget, a person, Claude Code, …). */}
                          <RcaOriginBadge id={String(t.id)} origin={t.origin} />
                          {t.source && !t.origin && <span style={{ fontSize: 11, color: tvTokens.ink400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{t.source}</span>}
                          {/* TRUS-717: the organization now reads on the labelled people line
                              (with a "No organization" fallback), so it isn't repeated here. */}
                          {prNum && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontFamily: tvTokens.fontMono, fontSize: 10.5, color: tvTokens.accentInk }} title="Has a pull request">
                              <GitPullRequest size={11} />#{prNum}
                            </span>
                          )}
                        </div>
                        <div style={cell} onClick={toggle}>
                          <span style={{ fontSize: 11.5, color: tvTokens.ink400, whiteSpace: 'nowrap' }}>{rel(t.updated_at)}</span>
                        </div>
                        {/* Action cell: inline actions for awaiting_review + expand chevron */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between', minWidth: 0 }}>
                          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, flex: 1, minWidth: 0 }}>
                            {canControl && (t.status || '').toLowerCase() === 'awaiting_review' && (
                              isHandoffVerdict(t.verdict) ? (
                                <>
                                  <button onClick={() => rcaRowAction(t.id, 'approve', 'Handed off to the agent')} disabled={busyGate === t.id} style={btnApprove} title="Hand off to the agent to execute">
                                    <Bot size={13} /> Hand off
                                  </button>
                                  <IconAction icon={<Check size={13} />} label="Mark complete" hint="a human did this manually — records the result" onClick={() => rcaMarkComplete(t.id)} disabled={busyGate === t.id} tone={btnDone} />
                                  <IconAction icon={<X size={13} />} label="Dismiss" hint="not needed" onClick={() => rcaRowAction(t.id, 'reject', 'Dismissed')} disabled={busyGate === t.id} tone={btnReject} />
                                </>
                              ) : (
                                <>
                                  {/* "Approve" meant two different things depending on whether a PR
                                      exists — say which. With a PR it approves the code for
                                      deployment (Merge then ships it); without one it sends the
                                      ticket to the agent to build. */}
                                  <button
                                    onClick={() => rcaRowAction(t.id, 'approve', t.pr_url ? 'Approved for deploy — merge the PR to ship it' : 'Approved — the agent will build it')}
                                    disabled={busyGate === t.id}
                                    style={btnApprove}
                                    title={t.pr_url ? 'Approve this PR for deployment. Merging it (next to this) deploys it.' : 'No PR yet — approve and have the agent build the fix'}
                                  >
                                    <Check size={13} /> {t.pr_url ? 'Approve for deploy' : 'Approve & build'}
                                  </button>
                                  <IconAction icon={<ShieldCheck size={13} />} label="Resolve" hint="resolved elsewhere — close this ticket + Jira without building" onClick={() => rcaResolve(t.id)} disabled={busyGate === t.id} tone={btnDone} />
                                  <IconAction icon={<X size={13} />} label="Dismiss" hint="not needed; closes without building or deploying" onClick={() => rcaRowAction(t.id, 'reject')} disabled={busyGate === t.id} tone={btnDismiss} />
                                </>
                              )
                            )}
                            {/* Merge was only reachable by expanding the row. Surface it on the
                                summary line for any open ticket with a PR (not while the ticket is
                                still in intake review — its analysis isn't approved yet). A merge
                                that can't go through expands the row so the reason + fallbacks
                                (update branch / hand back to agent) are visible. */}
                            {canControl && t.pr_url && ['awaiting_review', 'reviewed', 'product_approved'].includes((t.status || '').toLowerCase()) && (
                              (t.status || '').toLowerCase() === 'reviewed' ? (
                                // TRUS-611: approved for implementation → merging is THE next step.
                                <button
                                  onClick={() => mergePr(t.id)}
                                  disabled={mergeBusy === t.id}
                                  style={btnMergeNext}
                                  aria-label="Next step: merge the PR to deploy it"
                                  title="Next step — approved for implementation: merge the PR; the merge auto-deploys"
                                >
                                  {mergeBusy === t.id ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <GitMerge size={13} />} Merge &amp; deploy
                                </button>
                              ) : (
                                <IconAction
                                  icon={mergeBusy === t.id ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <GitMerge size={13} />}
                                  label="Merge"
                                  hint="merge the PR (auto-deploys). Approve it first unless you have reviewed it on GitHub."
                                  onClick={() => mergePr(t.id)}
                                  disabled={mergeBusy === t.id}
                                  tone={btnMerge}
                                />
                              )
                            )}
                            {/* New ticket awaiting the pre-development review: the review panel is
                                in the expanded row; open it from here. */}
                            {canControl && (t.status || '').toLowerCase() === 'held' && (t.held_for || '').toLowerCase() === 'intake' && (
                              <button
                                onClick={() => { if (!open) toggle(); }}
                                style={btnApprove}
                                title="Review the analysis: approve for implementation, verify with the creator, refine/split, or close"
                              >
                                <Search size={13} /> Review
                              </button>
                            )}
                            {/* A ticket the agent is still QUEUED FOR or working on, that a human
                                has resolved elsewhere. Without this there was no console way to
                                close a 'pending'/'claimed' row at all: every other affordance keys
                                off awaiting_review / reviewed / product_approved / held, so a
                                reopened ticket (Jira moved back to To Do -> dispatcher re-queues to
                                'pending') became unclosable from here and had to be settled by hand
                                in Jira. TRUS-190 hit exactly that.

                                The resolve endpoint has always accepted these states — its `live`
                                tuple lists pending and claimed — so this closes a UI gap, not an API
                                one.

                                Safe on 'claimed' specifically because the agent's own report is
                                guarded: /agents/rca/{id}/complete updates
                                `WHERE id = %s AND status IN ('claimed','pending')`, so a late report
                                against a row we just closed is a no-op and cannot resurrect it.
                                'exec_pending'/'executing' are deliberately NOT included — the
                                service-executor's /service-complete updates on `WHERE id = %s` with
                                no status guard, so closing one mid-run could be overwritten back to
                                'held'. Fix that guard first if those states need the button too. */}
                            {canControl && ['pending', 'claimed'].includes((t.status || '').toLowerCase()) && (
                              <IconAction icon={<ShieldCheck size={13} />} label="Resolve" hint="resolved elsewhere — close this ticket + Jira without waiting for the agent" onClick={() => rcaResolve(t.id)} disabled={busyGate === t.id} tone={btnDone} />
                            )}
                            {/* Approved/handed-off OPERATIONAL job the agent hasn't (or can't) auto-execute:
                                let a human record the result and close it out. */}
                            {canControl && (t.status || '').toLowerCase() === 'reviewed' && isHandoffVerdict(t.verdict) && (
                              <button onClick={() => rcaMarkComplete(t.id)} disabled={busyGate === t.id} style={btnDone} title="Record the result and mark this operational task complete">
                                <Check size={13} /> Complete
                              </button>
                            )}
                            {/* Approved CODE ticket (BUG/GAP/FEATURE): once its PR is merged — from the
                                console "Merge PR" or externally — mark it done and close the Jira issue.
                                Without this a 'reviewed' code ticket had no console way to close out (only
                                the merged-PR reconciler cron would eventually catch it). */}
                            {canControl && (t.status || '').toLowerCase() === 'reviewed' && !isHandoffVerdict(t.verdict) && (
                              <IconAction icon={<ShieldCheck size={13} />} label="Mark done & close" hint="fix merged / handled — mark done and close the Jira issue" onClick={() => rcaResolve(t.id)} disabled={busyGate === t.id} tone={btnDone} />
                            )}
                            {/* Product-approved feature (built in a separate session, e.g. the LCC
                                tickets): once the separate build is done, close it out + Jira Done. */}
                            {canControl && (t.status || '').toLowerCase() === 'product_approved' && (
                              <IconAction icon={<Check size={13} />} label="Mark done & close" hint="separate build complete — mark done and close the Jira issue" onClick={() => rcaResolve(t.id)} disabled={busyGate === t.id} tone={btnDone} />
                            )}
                            {/* Reclassify — control users can correct a stale/wrong verdict in
                                place (the sync guard for Jira-only reclassifications like TRUS-179).
                                Switching to Operational/Configuration flips this row to the
                                hand-off affordance on reload. */}
                            {canControl && ['awaiting_review', 'reviewed', 'skipped', 'held'].includes((t.status || '').toLowerCase()) && (
                              <span
                                title="Reclassify — change this ticket's verdict"
                                style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 26, borderRadius: 7, border: `1px dashed ${tvTokens.border}`, color: tvTokens.ink500, flexShrink: 0 }}
                              >
                                <Tags size={13} />
                                <select
                                  value=""
                                  aria-label="Reclassify this ticket's verdict"
                                  disabled={busyGate === t.id}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => { const v = e.target.value; if (v) reclassify(t.id, v); }}
                                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }}
                                >
                                  <option value="">Reclassify…</option>
                                  {VERDICT_OPTIONS.filter((v) => v.value !== 'all' && v.value !== 'preliminary').map((v) => (
                                    <option key={v.value} value={v.value}>{v.label}</option>
                                  ))}
                                </select>
                              </span>
                            )}
                          </span>
                          <button onClick={toggle} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: tvTokens.ink400, flexShrink: 0 }} title={open ? 'Collapse' : 'Expand'}>
                            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        </div>
                      </div>

                      {/* Expanded detail: analysis + proposed changes + PR link */}
                      {open && (
                        <div style={{ padding: '0 16px 12px 16px' }}>
                          {/* Activity timeline — one chronological thread of the whole
                              journey: creator request, agent analysis + proposed changes,
                              reviewer feedback / context, held question + replies, and the
                              resolution milestones, each with its own timestamp. */}
                          <div style={{ marginBottom: 10, padding: '9px 12px', background: tvTokens.paper2, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                              <div style={{ fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
                                Activity timeline
                              </div>
                              <button
                                onClick={() => loadTimeline(t.id, true)}
                                title="Refresh the activity thread"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', color: tvTokens.ink400, fontSize: 10.5, cursor: 'pointer', padding: 0 }}
                              >
                                <RefreshCw size={11} /> Refresh
                              </button>
                            </div>
                            <TicketThread entry={timelineCache[t.id]} item={t} />
                          </div>
                          {(t.branch || t.pr_url) && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                              {t.priority && (
                                <span style={{ ...chip, background: 'transparent', color: tvTokens.ink400, border: `1px solid ${tvTokens.border}` }}>{t.priority}</span>
                              )}
                              {t.branch && (
                                <span style={{ ...chip, color: tvTokens.ink500 }}>{t.branch}</span>
                              )}
                              {t.pr_url && (
                                <a
                                  href={t.pr_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ ...chip, display: 'inline-flex', alignItems: 'center', gap: 4, color: tvTokens.accentInk, background: tvTokens.accentSoft, textDecoration: 'none' }}
                                >
                                  View PR <ExternalLink size={11} />
                                </a>
                              )}
                              {canControl && t.pr_url && (t.status || '').toLowerCase() !== 'done' && (
                                <button
                                  onClick={() => mergePr(t.id)}
                                  disabled={mergeBusy === t.id}
                                  style={btnMerge}
                                  title="Merge this ticket's PR"
                                >
                                  {mergeBusy === t.id ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <GitMerge size={13} />} Merge PR
                                </button>
                              )}
                            </div>
                          )}
                          {canControl && t.pr_url && (t.status || '').toLowerCase() !== 'done' && mergeReason[t.id] && (
                            <div style={mergeReasonStyle}>
                              <div>Can't merge: {mergeReason[t.id]}</div>
                              {/* Fallbacks. Stale branch → Update branch; conflict OR red CI →
                                  hand back to the agent to fix; ANY failure → the manual GitHub
                                  path (covers a token without merge permission, blocked, etc). */}
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                {mergeConflict[t.id] && (
                                  <button
                                    onClick={() => updateBranch(t.id)}
                                    disabled={mergeBusy === t.id}
                                    style={btnMerge}
                                    title="Merge the latest base branch into this PR (fixes a stale branch with no real conflicts)"
                                  >
                                    {mergeBusy === t.id ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={13} />} Update branch
                                  </button>
                                )}
                                {(mergeConflict[t.id] || mergeCiFail[t.id]) && (
                                  <button
                                    onClick={() => handBackToAgent(t.id, mergeReason[t.id])}
                                    disabled={mergeBusy === t.id}
                                    style={btnApprove}
                                    title="Re-queue for the agent to check out the branch, fix the blocker (rebase conflicts / repair the failing build) and push the same PR"
                                  >
                                    <Bot size={13} /> {mergeCiFail[t.id] ? 'Hand back to agent to fix CI' : 'Hand back to agent'}
                                  </button>
                                )}
                                <a
                                  href={t.pr_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ ...btnReject, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                  title="Open the PR on GitHub to view the failing checks or merge manually"
                                >
                                  <ExternalLink size={13} /> Open on GitHub
                                </a>
                              </div>
                            </div>
                          )}
                          {/* A claim the run never reported back on. No other control accepts
                              'claimed'/'executing', so without this the ticket just reads
                              "In progress" until the celery watchdog happens to sweep it
                              (TRUS-361 sat here 19h). Only offered once past the stale
                              threshold, and the API refuses if a branch/PR exists. */}
                          {canControl
                            && ['claimed', 'executing'].includes((t.status || '').toLowerCase())
                            && !t.pr_url
                            && t.updated_at
                            && Date.now() - new Date(t.updated_at).getTime() >= 30 * 60000 && (
                            <div style={{ marginBottom: 8 }}>
                              <button
                                onClick={async () => {
                                  try {
                                    const r = await agentsApi.requeueRca(t.id);
                                    flash(`Released back to ${r?.status === 'exec_pending' ? 'the execution queue' : 'the queue'}.`);
                                    void load();
                                  } catch (e: any) {
                                    flash(e?.message || "Couldn't requeue that job.");
                                  }
                                }}
                                disabled={busyGate === t.id}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: tvTokens.accentInk, border: 'none', borderRadius: tvTokens.rSm, cursor: 'pointer' }}
                                title="This run never reported back. Release the claim so the next agent run picks it up again."
                              >
                                <RefreshCw size={13} /> Requeue stalled job
                              </button>
                            </div>
                          )}
                          {/* TRUS-611 reading order: 1. the ticket in plain language, 2. what the
                              creator actually asked for (description + attachments), 3. the agent's
                              technical analysis and plan — collapsed once a plain summary exists. */}
                          <BusinessSummaryPanel item={t} canGenerate={canControl} onSaved={() => void load()} />

                          <div style={{ marginBottom: 8 }}>
                            <button
                              onClick={() => toggleTicketContext(t.id)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            >
                              <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
                                Ticket context — what the creator reported
                              </span>
                              {expandedTicketCtx[t.id]
                                ? <ChevronUp size={12} style={{ color: tvTokens.accentInk }} />
                                : <ChevronDown size={12} style={{ color: tvTokens.accentInk }} />}
                            </button>
                            {expandedTicketCtx[t.id] && (
                              <div style={{ marginTop: 6, padding: '10px 12px', background: tvTokens.paper2, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r }}>
                                <TicketContextBody entry={ticketCtx[t.id]} fallbackUrl={t.pr_url} ticketId={t.id} canControl={canControl} onReplied={() => { loadTicketContext(t.id, true); flash('Reply posted to Jira'); }} />
                              </div>
                            )}
                          </div>

                          {(() => {
                            const techOpen = expandedTech[t.id] ?? !t.business_summary;
                            return (
                              <div>
                                <button
                                  onClick={() => setExpandedTech((prev) => ({ ...prev, [t.id]: !techOpen }))}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                >
                                  <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
                                    Technical detail — analysis{proposed ? ' & proposed changes' : ''}
                                  </span>
                                  {techOpen
                                    ? <ChevronUp size={12} style={{ color: tvTokens.accentInk }} />
                                    : <ChevronDown size={12} style={{ color: tvTokens.accentInk }} />}
                                </button>
                                {techOpen && (
                                  <div style={{ marginTop: 6 }}>
                                    {summaryText ? (
                                      <div style={{ fontSize: 12.5, lineHeight: 1.5, color: tvTokens.ink500, whiteSpace: 'pre-wrap' }}>
                                        {renderTextWithLinks(summaryText)}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 12.5, color: tvTokens.ink400 }}>No analysis recorded.</div>
                                    )}
                                    {proposed && (
                                      <div style={{ marginTop: 8 }}>
                                        <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
                                          Proposed changes
                                        </span>
                                        <ProposedChangesBlock proposed={proposed} />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          {t.held_question && (
                            <div style={{ marginTop: 8 }}>
                              <div style={{ fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: '#b9781f', marginBottom: 3 }}>
                                {(t.held_for || '').toLowerCase() === 'intake'
                                  ? 'Pre-development review'
                                  : (t.held_for || '').toLowerCase() === 'product'
                                  ? 'Product decision'
                                  : (t.held_for || '').toLowerCase() === 'admin'
                                    ? 'For admin review'
                                    : (t.held_for || '').toLowerCase() === 'creator_draft'
                                      ? 'Proposed question for the creator — not sent yet'
                                      : `Asked of creator${t.creator_asked_at ? ` · sent ${rel(t.creator_asked_at)}` : ''}`}
                              </div>
                              <div
                                style={{
                                  padding: '7px 9px',
                                  background: '#f8efda',
                                  border: `1px solid ${tvTokens.border}`,
                                  borderRadius: tvTokens.rSm,
                                  fontSize: 11.5,
                                  lineHeight: 1.5,
                                  color: tvTokens.ink500,
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {renderTextWithLinks(t.held_question)}
                              </div>
                              {/* WAITING ON THE CREATOR. A question was released to the reporter
                                  by a human. Say what happens when they answer — if a decision is
                                  parked behind the ask (held_for_before_ask), it comes back to the
                                  admin with the answer attached instead of the ticket re-triaging
                                  itself and wiping the analysis (TRUS-370). */}
                              {(t.status || '').toLowerCase() === 'held' && (t.held_for || '').toLowerCase() === 'creator' && (
                                <div style={{ marginTop: 6, fontSize: 11, color: tvTokens.ink400 }}>
                                  {t.held_for_before_ask
                                    ? `The ${({ product: 'product', intake: 'pre-development review' } as Record<string, string>)[(t.held_for_before_ask || '').toLowerCase()] || 'admin'} decision is parked — it comes back to you with their answer.`
                                    : 'Their answer re-queues the ticket for a fresh triage.'}
                                  {' '}A reply on the ticket is picked up automatically; record an answer that came another way.
                                </div>
                              )}
                              {/* THE ANSWER, once it's in. Shown separately from the (restored)
                                  decision note so an admin can see at a glance that the thing they
                                  were waiting on has landed, and when. */}
                              {t.creator_reply && (
                                <div style={{ marginTop: 8 }}>
                                  <div style={{ fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: '#12855f', marginBottom: 3 }}>
                                    The creator answered{t.creator_replied_at ? ` · ${rel(t.creator_replied_at)}` : ''}
                                  </div>
                                  <div
                                    style={{
                                      padding: '7px 9px',
                                      background: '#e3f3ec',
                                      border: `1px solid ${tvTokens.border}`,
                                      borderRadius: tvTokens.rSm,
                                      fontSize: 11.5,
                                      lineHeight: 1.5,
                                      color: tvTokens.ink500,
                                      whiteSpace: 'pre-wrap',
                                    }}
                                  >
                                    {renderTextWithLinks(t.creator_reply)}
                                  </div>
                                  {t.creator_ask_question && (
                                    <div style={{ fontSize: 11, color: tvTokens.ink400, marginTop: 4 }}>
                                      In answer to: “{t.creator_ask_question.length > 180 ? `${t.creator_ask_question.slice(0, 180)}…` : t.creator_ask_question}”
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* Awaiting the creator: capture an answer that arrived by email, on a
                                  call or in Slack, and choose whether it returns the parked decision
                                  or re-queues the agent. */}
                              {canControl && (t.status || '').toLowerCase() === 'held' && (t.held_for || '').toLowerCase() === 'creator' && (
                                <RecordCreatorReplyControl
                                  ticketId={t.id}
                                  parkedFor={t.held_for_before_ask}
                                  onDone={(mode) => {
                                    flash(mode === 'restore' ? 'Answer recorded — back with you for a decision.' : 'Answer recorded — re-queued for the agent.');
                                    void load();
                                  }}
                                />
                              )}
                              {/* A FEATURE held for a PRODUCT decision gets the product-decision bar
                                  (build with the agent, mark for a separate build session, defer, or
                                  decline) instead of the creator/admin unblock+respond controls. Add
                                  context / re-analyze stays available via the re-analyze control. */}
                              {canControl && (t.status || '').toLowerCase() === 'held' && (t.held_for || '').toLowerCase() === 'intake' ? (
                                <IntakeReviewPanel item={t} flash={flash} onDone={() => void load()} />
                              ) : canControl && (t.status || '').toLowerCase() === 'held' && (t.held_for || '').toLowerCase() === 'product' ? (
                                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                  <button
                                    onClick={() => rcaRowAction(t.id, 'approve', 'Handed to the agent to build')}
                                    disabled={busyGate === t.id}
                                    style={btnApprove}
                                    title="Approve and hand this feature to the agent to build (implement mode)"
                                  >
                                    <Bot size={13} /> Build with agent
                                  </button>
                                  <button
                                    onClick={() => rcaRowAction(t.id, 'approve-spec', 'Product-approved — ready for a separate build')}
                                    disabled={busyGate === t.id}
                                    style={btnDone}
                                    title="Approve for a SEPARATE build session — parks it as product-approved (not handed to the agent)"
                                  >
                                    <ShieldCheck size={13} /> Mark for separate build
                                  </button>
                                  <button
                                    onClick={() => rcaResolve(t.id)}
                                    disabled={busyGate === t.id}
                                    style={btnDone}
                                    title="This feature is already built/handled — mark done with an explanation and close the Jira issue"
                                  >
                                    <Check size={13} /> Mark done &amp; close
                                  </button>
                                  <button
                                    onClick={() => rcaRowAction(t.id, 'defer', 'Deferred to the product backlog')}
                                    disabled={busyGate === t.id}
                                    style={btnDone}
                                    title="Park this in the product backlog for later"
                                  >
                                    <Clock size={13} /> Defer
                                  </button>
                                  <button
                                    onClick={() => rcaRowAction(t.id, 'decline', 'Declined as out of scope')}
                                    disabled={busyGate === t.id}
                                    style={btnDecline}
                                    title="Decline as out of scope — closes the ticket and notifies the creator"
                                  >
                                    <X size={13} /> Decline
                                  </button>
                                  {/* A product decision the agent couldn't make alone — its open
                                      questions belong with the reporter. Releasing them keeps this
                                      decision parked; it returns with their answer. */}
                                  <AskCreatorControl
                                    ticketId={t.id}
                                    audience="product"
                                    holdNote={t.held_question || ''}
                                    onDone={() => { flash('Sent to the creator — this decision comes back with their answer.'); void load(); }}
                                  />
                                </div>
                              ) : (
                                <>
                                  {/* Release a question to the creator. On a creator_draft hold the
                                      agent already wrote it and the admin reviews/edits/sends; on an
                                      admin hold the admin writes it, and the decision they're parked
                                      on comes back with the answer. On a ticket already waiting on
                                      the reporter this becomes a follow-up ask (their answer only
                                      got halfway), secondary to recording the answer above. */}
                                  {canControl && (t.status || '').toLowerCase() === 'held' && ['admin', 'creator_draft', 'creator', ''].includes((t.held_for || '').toLowerCase()) && (
                                    <AskCreatorControl
                                      ticketId={t.id}
                                      audience={(t.held_for || '').toLowerCase()}
                                      holdNote={t.held_question || ''}
                                      onDone={() => { flash('Sent to the creator.'); void load(); }}
                                    />
                                  )}
                                  {canControl && (t.status || '').toLowerCase() === 'held' && (
                                    <button
                                      onClick={async () => {
                                        try {
                                          await agentsApi.unholdRca(t.id, 'requeue');
                                          flash('Unblocked — handed to the agent');
                                          void load();
                                        } catch {
                                          flash("Couldn't unblock that ticket.");
                                        }
                                      }}
                                      style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff', background: tvTokens.accentInk, border: 'none', borderRadius: tvTokens.rSm, cursor: 'pointer' }}
                                      title="Proceed without waiting for the creator — re-queues for the agent to build/execute"
                                    >
                                      <Bot size={13} /> Unblock &amp; proceed
                                    </button>
                                  )}
                                  {canControl && (t.status || '').toLowerCase() === 'held' && (
                                    <RespondCloseControl ticketId={t.id} onDone={() => { flash('Responded to creator & closed.'); void load(); }} />
                                  )}
                                </>
                              )}
                            </div>
                          )}
                          {/* Per-part progress trail — for a multi-part OPERATIONAL ticket, shows
                              which parts executed vs. still need a tool/build (TRUS-192). */}
                          {Array.isArray(t.parts) && t.parts.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
                                Progress · {t.parts.filter((p) => (p.status || '') === 'done').length}/{t.parts.length} done
                              </span>
                              <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {t.parts.map((p, i) => {
                                  const st = p.status || 'pending';
                                  const chip =
                                    st === 'done'
                                      ? { bg: '#e7f5ec', fg: '#1a7f37', label: '✓ done' }
                                      : st === 'needs_build'
                                        ? { bg: '#fdece7', fg: '#b3401f', label: '⚙ needs build' }
                                        : st === 'skipped'
                                          ? { bg: '#f0f0f0', fg: '#666', label: '– skipped' }
                                          : { bg: '#fff6e0', fg: '#8a6100', label: '⏳ pending' };
                                  const pn = p.n ?? i + 1;
                                  return (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                                      <span style={{ padding: '1px 7px', borderRadius: 10, background: chip.bg, color: chip.fg, fontWeight: 600, fontSize: 10.5, whiteSpace: 'nowrap' }}>{chip.label}</span>
                                      <span style={{ color: tvTokens.ink, fontWeight: 500 }}>{p.title || `Part ${pn}`}</span>
                                      {p.tool && <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10.5, color: tvTokens.ink400 }}>{p.tool}</span>}
                                      {p.detail && <span style={{ color: tvTokens.ink400, fontSize: 11 }}>— {p.detail}</span>}
                                      {canControl && st !== 'done' && (
                                        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
                                          <button onClick={() => rcaSetPartStatus(t.id, pn, 'done')} disabled={busyGate === t.id} style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: tvTokens.rSm, border: `1px solid ${tvTokens.border}`, background: '#fff', color: tvTokens.ink500, cursor: 'pointer' }} title="Mark this part done">done</button>
                                          <button onClick={() => rcaSetPartStatus(t.id, pn, 'needs_build')} disabled={busyGate === t.id} style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: tvTokens.rSm, border: `1px solid ${tvTokens.border}`, background: '#fff', color: tvTokens.ink500, cursor: 'pointer' }} title="Flag this part as needing a build/tool">needs build</button>
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}


                          {/* Resolution journey — implementation arc. Only where
                              a journey is meaningful (has a PR, or the ticket is
                              resolved). Lazy. */}
                          {journeyMeaningful({ pr_url: t.pr_url, status: t.status, verdict: t.verdict }) && (
                            <div style={{ marginTop: 8 }}>
                              <button
                                onClick={() => toggleJourney(t.id)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                              >
                                <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
                                  Resolution journey
                                </span>
                                {expandedJourney[t.id]
                                  ? <ChevronUp size={12} style={{ color: tvTokens.accentInk }} />
                                  : <ChevronDown size={12} style={{ color: tvTokens.accentInk }} />}
                              </button>
                              {expandedJourney[t.id] && (
                                <div style={{ marginTop: 6, padding: '10px 12px', background: tvTokens.paper2, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r }}>
                                  <JourneyBody entry={journeyCache[t.id]} />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Add context & re-analyze — only for control users
                              and ambiguous/skipped tickets. */}
                          {/* An intake hold has its own "Ask the agent" section in the review panel. */}
                          {canControl && canReanalyze(t.verdict, t.status) && (t.held_for || '').toLowerCase() !== 'intake' && (
                            <ReanalyzeControl
                              reviewerContext={t.reviewer_context}
                              open={!!reanalyzeOpen[t.id]}
                              text={reanalyzeText[t.id] || ''}
                              busy={reanalyzeBusy === t.id}
                              onToggle={() => setReanalyzeOpen((prev) => ({ ...prev, [t.id]: !prev[t.id] }))}
                              onChange={(v) => setReanalyzeText((prev) => ({ ...prev, [t.id]: v }))}
                              onSubmit={(impl) => submitReanalyze(t.id, impl)}
                            />
                          )}

                          {/* Hold & ask creator — pause the ticket pending a
                              reply from the creator (posted to Jira). */}
                          {canControl && canHold(t.status) && (
                            <HoldControl
                              open={!!holdOpen[t.id]}
                              text={holdText[t.id] || ''}
                              busy={holdBusy === t.id}
                              onToggle={() => setHoldOpen((prev) => ({ ...prev, [t.id]: !prev[t.id] }))}
                              onChange={(v) => setHoldText((prev) => ({ ...prev, [t.id]: v }))}
                              onSubmit={() => submitHold(t.id)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
  };

  return (
    <div
      style={{ minHeight: '100vh', background: tvTokens.paper, fontFamily: tvTokens.fontSans, color: tvTokens.ink, overflowX: 'hidden' }}
      className={prod === 'triveny' ? 'triveny-platform' : undefined}
    >
      {/* Triveny gets the slim TvPillarBar ("OPERATE · Agents") from app.tsx, so this
          page-level banner is a duplicate there; WeaveIn has no pillar bar, so keep it. */}
      {prod !== 'triveny' && !tabbed && <TvPageBar eyebrow="Operate · Agent control" title="Agents" />}

      <div style={{ padding: isMobile ? '16px 12px 64px' : '22px 22px 80px', maxWidth: 1180, margin: '0 auto' }}>
        {/* Summary tiles */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 13.5, color: tvTokens.ink500, flex: isMobile ? '1 1 100%' : '1 1 auto' }}>
            Every agent across the platform — catalog, live runs, cost, and the human-approval queue.
          </p>
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8 }}>
            {canControl && (
              <button onClick={() => setShowCreate(true)} style={btnPrimary}>
                <Plus size={13} /> New agent
              </button>
            )}
            {canControl && prod === 'weavein' && (
              <button
                onClick={async () => {
                  setRunningNow(true);
                  try {
                    const res: any = await agentsApi.runRcaNow();
                    if (res && res.success === false) {
                      flash(res.error?.message || "Run-now isn't configured.");
                    } else {
                      flash('Agent run started.');
                      setTimeout(() => { setRefreshing(true); void load(); }, 1500);
                    }
                  } catch {
                    flash("Couldn't start the agent run.");
                  } finally {
                    setRunningNow(false);
                  }
                }}
                disabled={runningNow}
                style={btnGhost}
                title="Fire the RCA agent now instead of waiting for the hourly run"
              >
                <Play size={13} /> {runningNow ? 'Starting…' : 'Run agent now'}
              </button>
            )}
            {prod === 'weavein' && (
              <button
                onClick={openCapabilities}
                style={btnGhost}
                title="See everything 'Hand off to agent' can execute"
              >
                <HelpCircle size={13} /> Agent capabilities
              </button>
            )}
            <button
              onClick={openTools}
              style={btnGhost}
              title="The shared tool catalogue — the actions agents (and this console) can run"
            >
              <Wrench size={13} /> Tool catalogue
            </button>
            <button
              onClick={() => { setRefreshing(true); void load(); }}
              disabled={refreshing}
              style={btnGhost}
            >
              <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} /> Refresh
            </button>
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))', gap: isMobile ? 10 : 12, marginBottom: 20 }}>
          {tiles.map((t) => (
            <div key={t.label} style={tileStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: tvTokens.ink500 }}>
                <t.icon size={13} />
                <span style={labelMono}>{t.label}</span>
              </div>
              <div style={{ fontFamily: tvTokens.fontDisplay, fontSize: 26, fontWeight: 650, letterSpacing: '-0.02em', color: t.tone, fontVariantNumeric: 'tabular-nums' }}>
                {t.value}
              </div>
            </div>
          ))}
        </div>

        {/* Tab switcher — summary tiles stay above; Tickets vs Agent catalog below. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
          {([
            // WeaveIn's queue is RCA tickets; Triveny's is outreach approvals. Same
            // tab, product-appropriate label — no ticket vocabulary leaks to Triveny.
            { value: 'tickets', label: prod === 'weavein' ? 'Tickets' : 'Review queue' },
            { value: 'catalog', label: 'Agent catalog' },
            { value: 'issues', label: 'Observability' },
            // Contributor PRs awaiting the approver — WeaveIn ops only (same
            // visibility as RCA tickets; the API 403s anyone else).
            ...(prod === 'weavein' ? [{ value: 'prs' as const, label: 'Review & deploy' }] : []),
            // Funder rules builder + interface harness (TRUS-778) — WeaveIn billing only.
            ...(prod === 'weavein' ? [{ value: 'funders' as const, label: 'Funder interfaces' }] : []),
          ] as const).map((t) => {
            const active = agentTab === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setAgentTab(t.value)}
                style={{
                  fontFamily: tvTokens.fontDisplay,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '7px 16px',
                  borderRadius: tvTokens.r,
                  cursor: 'pointer',
                  border: `1px solid ${active ? tvTokens.accent : tvTokens.border}`,
                  background: active ? tvTokens.accentSoft : 'transparent',
                  color: active ? tvTokens.accentInk : tvTokens.ink500,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* ── Tickets tab: gate queue + all-tickets triage log ── */}
        {agentTab === 'tickets' && (
          <>
        {/* Epics — per-Epic gate. New Epics default to Hold (children parked out of the
            queue) until a human sets Auto-triage (releases held children) or keeps Hold. */}
        {epics.length > 0 && (() => {
          const undecided = epics.filter((e) => (e.mode || 'hold') === 'hold').length;
          return (
          <div style={{ ...cardStyle, borderColor: '#e3d9b0', marginBottom: 20 }}>
            <button
              onClick={() => setEpicsOpen((v) => !v)}
              style={{ ...cardHead, width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              title={epicsOpen ? 'Collapse Epics' : 'Expand Epics'}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                {epicsOpen ? <ChevronDown size={14} color="#b9781f" /> : <ChevronUp size={14} color="#b9781f" />}
                <Layers size={15} color="#b9781f" />
                <span style={cardTitle}>Epics</span>
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {undecided > 0 && <span style={{ ...pill, background: '#f8efda', color: '#b9781f' }}>{undecided} to decide</span>}
                <span style={{ ...pill, background: '#f3f0e4', color: '#8a7a4f' }}>{epics.length}</span>
              </span>
            </button>
            {epicsOpen && (
            <div>
              {epics.map((e) => {
                const c = e.counts || {};
                const held = c.held || 0;
                const mode = e.mode || 'hold';
                const isHold = mode === 'hold';
                const isClosed = mode === 'closed';
                const modeStyle = isClosed
                  ? { color: tvTokens.ink400, background: tvTokens.borderMuted }
                  : isHold
                    ? { color: '#b9781f', background: '#f8efda' }
                    : { color: '#12855f', background: '#e3f3ec' };
                return (
                  <div key={e.epic_key} style={{ borderTop: `1px solid ${tvTokens.borderMuted}`, padding: isMobile ? '10px 12px' : '10px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, opacity: isClosed ? 0.6 : 1 }}>
                    <span style={{ ...pillMono, fontSize: 11, fontWeight: 600, color: tvTokens.ink700 }}>{e.epic_key}</span>
                    <span style={{ minWidth: 0, flex: isMobile ? '1 1 100%' : 1, fontSize: 13, color: tvTokens.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
                      {e.epic_summary || <span style={{ color: tvTokens.ink400 }}>(untitled epic)</span>}
                    </span>
                    <span style={{ ...pillMono, fontSize: 10, padding: '1px 7px', ...modeStyle }}>
                      {isClosed ? 'Closed' : isHold ? 'Hold' : 'Auto-triage'}
                    </span>
                    <span style={{ fontSize: 11, color: tvTokens.ink400, fontVariantNumeric: 'tabular-nums' }}>
                      {c.total || 0} tickets{held ? ` · ${held} held` : ''}{c.in_flight ? ` · ${c.in_flight} in progress` : ''}{c.done ? ` · ${c.done} done` : ''}
                    </span>
                    {canControl && (
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        {isClosed ? (
                          <button onClick={() => changeEpicMode(e.epic_key, 'hold')} disabled={epicBusy === e.epic_key} style={btnGhost} title="Reopen this Epic (back to Hold)">
                            <Play size={13} /> Reopen
                          </button>
                        ) : (
                          <>
                            {isHold ? (
                              <button onClick={() => changeEpicMode(e.epic_key, 'auto')} disabled={epicBusy === e.epic_key} style={btnApprove} title="Triage children as they arrive and release any held now">
                                <Play size={13} /> Auto-triage{held ? ` & release ${held}` : ''}
                              </button>
                            ) : (
                              <button onClick={() => changeEpicMode(e.epic_key, 'hold')} disabled={epicBusy === e.epic_key} style={btnGhost} title="Park new children until you release the Epic">
                                <PauseCircle size={13} /> Hold
                              </button>
                            )}
                            <button onClick={() => changeEpicMode(e.epic_key, 'closed')} disabled={epicBusy === e.epic_key} style={btnGhost} title="Close this Epic — stop triaging it and retire its open tickets (the Jira Epic stays open)">
                              <X size={13} /> Close
                            </button>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
          );
        })()}
        {/* Gate queue — non-RCA drafts only (Triveny NBA). RCA tickets are actioned
            inline in the All-tickets list below. */}
        {gatedNonRca.length > 0 && (
          <div style={{ ...cardStyle, borderColor: '#d9d5f5', marginBottom: 20 }}>
            <div style={cardHead}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <ShieldCheck size={15} color="#5a4fcf" />
                <span style={cardTitle}>Awaiting human review</span>
              </span>
              <span style={{ ...pill, background: '#efedfb', color: '#5a4fcf' }}>{gatedNonRca.length}</span>
            </div>
            <div>
              {gatedNonRca.map((g) => {
                const isRca = g.kind === 'rca' || g.agent === 'ops.rca_agent';
                const summaryText = g.summary || g.detail || '';
                const isLong = summaryText.length > 180;
                const open = !!expandedGate[g.id];
                const changesOpen = !!expandedChanges[g.id];
                const proposed = g.proposed_changes || '';
                const vt = verdictTone(g.verdict);
                const isOperational = isHandoffVerdict(g.verdict);
                const chip: React.CSSProperties = { ...pillMono, fontSize: 10, padding: '1px 7px' };
                return (
                  <div key={g.id} style={gateItemStyle}>
                    {/* Header line: title + agent + time + actions */}
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap', gap: isMobile ? 8 : 10 }}>
                      <div style={{ minWidth: 0, flex: isMobile ? '1 1 100%' : 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: tvTokens.ink, minWidth: 0, flex: '1 1 auto' }}>{g.title}</div>
                        {isMobile && <span style={{ ...pillMono }}>{g.agent}</span>}
                        {isMobile && <span style={{ fontSize: 11.5, color: tvTokens.ink400 }}>{rel(g.created_at)}</span>}
                      </div>
                      {!isMobile && <span style={{ ...pillMono }}>{g.agent}</span>}
                      {!isMobile && <span style={{ fontSize: 11.5, color: tvTokens.ink400, minWidth: 54, textAlign: 'right' }}>{rel(g.created_at)}</span>}
                      {canControl && (
                        isOperational ? (
                          // OPERATIONAL items need an action, not a code review:
                          // hand off to the agent, confirm a human did it, or dismiss.
                          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                            <button
                              onClick={() => gateAction(g, 'approve', 'Handed off to the agent')}
                              disabled={busyGate === g.id}
                              style={btnApprove}
                              title="Hand off to the agent to execute"
                            >
                              <Bot size={13} /> Hand off to agent
                            </button>
                            <button
                              onClick={() => gateAction(g, 'done', 'Marked complete')}
                              disabled={busyGate === g.id}
                              style={btnDone}
                              title="A human completed this manually"
                            >
                              <Check size={13} /> Confirm complete
                            </button>
                            <button
                              onClick={() => gateAction(g, 'reject', 'Dismissed')}
                              disabled={busyGate === g.id}
                              style={btnReject}
                              title="Not needed"
                            >
                              <X size={13} />
                            </button>
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                            <button onClick={() => gateAction(g, 'approve')} disabled={busyGate === g.id} style={btnApprove} title="Approve">
                              <Check size={13} /> Approve
                            </button>
                            {isRca && g.pr_url && (
                              <button
                                onClick={() => mergePr(g.id)}
                                disabled={mergeBusy === g.id}
                                style={btnMerge}
                                title="Merge this ticket's PR"
                              >
                                {mergeBusy === g.id ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <GitMerge size={13} />} Merge PR
                              </button>
                            )}
                            {isRca && (
                              <button onClick={() => rcaResolve(g.id)} disabled={busyGate === g.id} style={btnDone} title="Resolved elsewhere — close this ticket + Jira without building">
                                <ShieldCheck size={13} /> Resolve
                              </button>
                            )}
                            <button onClick={() => gateAction(g, 'reject')} disabled={busyGate === g.id} style={btnReject} title="Dismiss">
                              <X size={13} />
                            </button>
                          </span>
                        )
                      )}
                    </div>
                    {isRca && g.pr_url && mergeReason[g.id] && (
                      <div style={mergeReasonStyle}>Can't merge: {mergeReason[g.id]}</div>
                    )}

                    {/* Detail chips: verdict / priority / source / ticket / branch */}
                    {(g.verdict || g.priority || g.source || g.ticket || g.branch || g.pr_url) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 8 }}>
                        {g.verdict && (
                          <span style={{ ...chip, color: vt.color, background: vt.background }}>{g.verdict}</span>
                        )}
                        {g.priority && (
                          <span style={{ ...chip, background: 'transparent', color: tvTokens.ink400, border: `1px solid ${tvTokens.border}` }}>{g.priority}</span>
                        )}
                        {g.source && (
                          <span style={{ ...chip, background: 'transparent', color: tvTokens.ink400, border: `1px solid ${tvTokens.border}` }}>{g.source}</span>
                        )}
                        {/* Ticket identity: the reporter's TKT- number plus the Jira
                            issue it mirrors to, instead of a bare UUID. */}
                        {ticketIdentity(g).primary && (
                          <span style={{ ...chip, color: tvTokens.ink500 }} title={ticketIdentity(g).title}>
                            {ticketIdentity(g).primary}
                          </span>
                        )}
                        {ticketIdentity(g).jira && (
                          <a
                            href={ticketIdentity(g).jiraUrl || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ ...chip, display: 'inline-flex', alignItems: 'center', gap: 4, color: tvTokens.accentInk, background: tvTokens.accentSoft, textDecoration: 'none' }}
                            title="Open the Jira issue this ticket is mirrored to"
                          >
                            {ticketIdentity(g).jira} <ExternalLink size={11} />
                          </a>
                        )}
                        {g.branch && (
                          <span style={{ ...chip, color: tvTokens.ink500 }}>{g.branch}</span>
                        )}
                        {g.pr_url && (
                          <a
                            href={g.pr_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ ...chip, display: 'inline-flex', alignItems: 'center', gap: 4, color: tvTokens.accentInk, background: tvTokens.accentSoft, textDecoration: 'none' }}
                          >
                            View PR <ExternalLink size={11} />
                          </a>
                        )}
                      </div>
                    )}

                    {/* Summary / detail — expandable so the queue stays scannable */}
                    {summaryText && (
                      <div style={{ marginTop: 8 }}>
                        <div
                          style={{
                            fontSize: 12.5,
                            lineHeight: 1.5,
                            color: tvTokens.ink500,
                            whiteSpace: 'pre-wrap',
                            ...(isRca
                              ? open || !isLong
                                ? {}
                                : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }
                              : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
                          }}
                        >
                          {renderTextWithLinks(summaryText)}
                        </div>
                        {isRca && isLong && (
                          <button
                            onClick={() => setExpandedGate((prev) => ({ ...prev, [g.id]: !prev[g.id] }))}
                            style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: tvTokens.accentInk }}
                          >
                            {open ? <>Show less <ChevronUp size={12} /></> : <>Show more <ChevronDown size={12} /></>}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Proposed changes — the actual diff/design; long, collapsed by default */}
                    {proposed && (
                      <div style={{ marginTop: 8 }}>
                        <button
                          onClick={() => setExpandedChanges((prev) => ({ ...prev, [g.id]: !prev[g.id] }))}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                          <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
                            Proposed changes
                          </span>
                          {changesOpen
                            ? <ChevronUp size={12} style={{ color: tvTokens.accentInk }} />
                            : <ChevronDown size={12} style={{ color: tvTokens.accentInk }} />}
                        </button>
                        {changesOpen && <ProposedChangesBlock proposed={proposed} />}
                      </div>
                    )}

                    {/* Ticket context — the ORIGINAL ticket's description +
                        screenshots, so a reviewer has full context. Lazy. */}
                    {isRca && (
                      <div style={{ marginTop: 8 }}>
                        <button
                          onClick={() => toggleTicketContext(g.id)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                          <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
                            Ticket context
                          </span>
                          {expandedTicketCtx[g.id]
                            ? <ChevronUp size={12} style={{ color: tvTokens.accentInk }} />
                            : <ChevronDown size={12} style={{ color: tvTokens.accentInk }} />}
                        </button>
                        {expandedTicketCtx[g.id] && (
                          <div style={{ marginTop: 6, padding: '10px 12px', background: tvTokens.paper2, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r }}>
                            <TicketContextBody entry={ticketCtx[g.id]} fallbackUrl={g.pr_url} ticketId={g.id} canControl={canControl} onReplied={() => { loadTicketContext(g.id, true); flash('Reply posted to Jira'); }} />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Resolution journey — implementation arc (drafted → CI →
                        approved → merged → deployed). Only where a journey is
                        meaningful (has a PR, or ticket is resolved). Lazy. */}
                    {isRca && journeyMeaningful({ pr_url: g.pr_url, status: (g as any).status, verdict: (g as any).verdict }) && (
                      <div style={{ marginTop: 8 }}>
                        <button
                          onClick={() => toggleJourney(g.id)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                          <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: tvTokens.ink500 }}>
                            Resolution journey
                          </span>
                          {expandedJourney[g.id]
                            ? <ChevronUp size={12} style={{ color: tvTokens.accentInk }} />
                            : <ChevronDown size={12} style={{ color: tvTokens.accentInk }} />}
                        </button>
                        {expandedJourney[g.id] && (
                          <div style={{ marginTop: 6, padding: '10px 12px', background: tvTokens.paper2, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r }}>
                            <JourneyBody entry={journeyCache[g.id]} />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Add context & re-analyze — only for control users and
                        ambiguous/skipped items. */}
                    {isRca && canControl && canReanalyze(g.verdict, (g as any).status) && (
                      <ReanalyzeControl
                        reviewerContext={g.reviewer_context}
                        open={!!reanalyzeOpen[g.id]}
                        text={reanalyzeText[g.id] || ''}
                        busy={reanalyzeBusy === g.id}
                        onToggle={() => setReanalyzeOpen((prev) => ({ ...prev, [g.id]: !prev[g.id] }))}
                        onChange={(v) => setReanalyzeText((prev) => ({ ...prev, [g.id]: v }))}
                        onSubmit={(impl) => submitReanalyze(g.id, impl)}
                      />
                    )}

                    {/* Hold & ask creator — pause the ticket pending a reply
                        from the creator (posted to Jira). */}
                    {isRca && canControl && canHold((g as any).status) && (
                      <HoldControl
                        open={!!holdOpen[g.id]}
                        text={holdText[g.id] || ''}
                        busy={holdBusy === g.id}
                        onToggle={() => setHoldOpen((prev) => ({ ...prev, [g.id]: !prev[g.id] }))}
                        onChange={(v) => setHoldText((prev) => ({ ...prev, [g.id]: v }))}
                        onSubmit={() => submitHold(g.id)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <p style={{ margin: '10px 14px 2px', fontSize: 12, color: tvTokens.ink400 }}>
              Approving an outreach draft creates a draft campaign to review; approving an RCA fix marks it reviewed.
            </p>
          </div>
        )}

        {/* All tickets — the RCA triage log (read-only; actions live in the gate) */}
        {/* Show the card whenever the system has ANY tickets (not just the current
            filtered page) — otherwise a filter that returns zero rows would hide the
            filter chips too, trapping the user with no way to clear it. */}
        {(totalTickets > 0 || rcaLog.length > 0) && (
          <div style={{ ...cardStyle, marginBottom: 20 }}>
            <div style={cardHead}>
              <span style={cardTitle}>All tickets</span>
              <span style={{ ...pill, background: tvTokens.paper2, color: tvTokens.ink500 }} title="Tickets in the RCA pipeline (every source) — not the Jira issue count">{totalTickets}</span>
              <button
                onClick={toggleCoverage}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6, background: 'none', border: 'none', padding: 0, fontSize: 11.5, color: tvTokens.accentInk, cursor: 'pointer' }}
                title="Compare this total with the Jira project's issue count"
              >
                <HelpCircle size={12} /> Why this total?
              </button>
              {canControl && (
                <button
                  onClick={() => setIconKeyOpen((o) => !o)}
                  aria-expanded={iconKeyOpen}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 10, background: 'none', border: 'none', padding: 0, fontSize: 11.5, color: tvTokens.accentInk, cursor: 'pointer' }}
                  title="What the action icons on each ticket mean"
                >
                  <HelpCircle size={12} /> Icon key
                </button>
              )}
            </div>
            {canControl && iconKeyOpen && (
              // TRUS-611: row actions are icons; this is the key. Every icon also has a tooltip.
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', padding: '8px 16px', borderBottom: `1px solid ${tvTokens.borderMuted}`, background: tvTokens.paper2, fontSize: 12, color: tvTokens.ink500 }}>
                {[
                  { icon: <Search size={13} />, label: 'Review the analysis (new ticket)' },
                  { icon: <Check size={13} />, label: 'Approve / mark complete' },
                  { icon: <GitMerge size={13} />, label: 'Merge the PR (deploys it)' },
                  { icon: <ShieldCheck size={13} />, label: 'Resolve / mark done & close' },
                  { icon: <X size={13} />, label: 'Dismiss — not needed' },
                  { icon: <Bot size={13} />, label: 'Hand off to the agent' },
                  { icon: <Tags size={13} />, label: 'Reclassify the verdict' },
                ].map((k) => (
                  <span key={k.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {k.icon} {k.label}
                  </span>
                ))}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#5a4fcf', fontWeight: 600 }}>
                  <ArrowRight size={13} /> The labelled button on a row is its next step
                </span>
              </div>
            )}
            {coverageOpen && (
              <div style={{ padding: '10px 16px', borderBottom: `1px solid ${tvTokens.borderMuted}`, background: tvTokens.paper2, fontSize: 12.5, color: tvTokens.ink500, lineHeight: 1.55 }}>
                {coverageBusy ? (
                  'Comparing with Jira…'
                ) : coverageErr ? (
                  <span style={{ color: '#cc3b57' }}>{coverageErr}</span>
                ) : coverage ? (
                  <>
                    <div>
                      <strong>{totalTickets}</strong> tickets are in the RCA pipeline
                      {Object.keys(coverage.rca_by_source || {}).length > 0 && (
                        <> ({Object.entries(coverage.rca_by_source).map(([k, v]) => `${v} ${k === 'cs_ticket' ? 'support widget' : k}`).join(', ')})</>
                      )}
                      ; Jira project {coverage.project} has <strong>{coverage.jira_total}</strong> issues, {coverage.jira_tracked} of them in the pipeline.
                    </div>
                    {coverage.jira_missing > 0 ? (
                      <div style={{ marginTop: 4 }}>
                        <strong>{coverage.jira_missing}</strong> Jira issues never entered the pipeline:{' '}
                        {Object.entries(coverage.jira_missing_by_type || {})
                          .sort((a, b) => b[1] - a[1])
                          .map(([k, v]) => `${v} ${k}`)
                          .join(' · ')}
                        {coverage.missing_created_before_first_job > 0 && (
                          <> — {coverage.missing_created_before_first_job} were created before the pipeline's first ticket{coverage.first_job_enqueued_at ? ` (${new Date(coverage.first_job_enqueued_at).toLocaleDateString()})` : ''}</>
                        )}
                        .
                        <details style={{ marginTop: 4 }}>
                          <summary style={{ cursor: 'pointer', color: tvTokens.accentInk, fontSize: 11.5 }}>Show the keys</summary>
                          <div style={{ marginTop: 4, fontFamily: tvTokens.fontMono, fontSize: 11, maxHeight: 140, overflowY: 'auto' }}>
                            {coverage.missing.map((m) => `${m.key} (${m.type}${m.status ? `, ${m.status}` : ''})`).join(' · ')}
                          </div>
                        </details>
                      </div>
                    ) : (
                      <div style={{ marginTop: 4 }}>Every Jira issue in {coverage.project} is in the pipeline.</div>
                    )}
                  </>
                ) : null}
              </div>
            )}
            {/* Bulk action bar — appears once ≥1 ticket is selected. Mark all selected Done and
                close their Jira issues in one shot. */}
            {canControl && selectedRca.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', background: tvTokens.accentSoft, borderBottom: `1px solid ${tvTokens.borderMuted}` }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: tvTokens.accentInk }}>
                  {selectedRca.size} selected
                </span>
                <button
                  onClick={bulkMarkDone}
                  disabled={bulkBusy}
                  style={{ ...btnDone, opacity: bulkBusy ? 0.6 : 1 }}
                  title="Mark all selected tickets Done and close them in Jira"
                >
                  {bulkBusy ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <ShieldCheck size={13} />} Mark done &amp; close
                </button>
                <button
                  onClick={clearRcaSelection}
                  disabled={bulkBusy}
                  style={{ background: 'none', border: 'none', color: tvTokens.ink500, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Clear
                </button>
              </div>
            )}
            {/* Failed-ticket alert — a failed job is a dead-end the pipeline won't retry on
                its own; surface it here (admins are also notified in-app) with a one-click
                jump to the failed set so it isn't buried in the log. */}
            {(rcaCounts['failed'] ?? 0) > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', background: '#fbe6ea', borderBottom: `1px solid ${tvTokens.borderMuted}`, color: '#8f2740', fontSize: 12.5 }}>
                <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                <span style={{ fontWeight: 600 }}>{rcaCounts['failed']} failed ticket{(rcaCounts['failed'] ?? 0) === 1 ? '' : 's'}</span>
                <span style={{ color: '#a3536a' }}>need attention — the agent hit an error and stopped.</span>
                {!(fAxes.status.length === 1 && fAxes.status[0] === 'failed') && (
                  <button
                    onClick={() => filters.setAxis('status', ['failed'])}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#8f2740', textDecoration: 'underline' }}
                  >
                    Show failed
                  </button>
                )}
              </div>
            )}
            {/* Filter bar (TRUS-687) — one multi-select row per axis: OR within a row, AND
                across rows. Filtering is server-side over the WHOLE log (rcaParams), and
                the counts are the server's faceted rollups (each axis counted under every
                OTHER active filter). Rows appear only for axes the server has data for. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '11px 16px', borderBottom: `1px solid ${tvTokens.borderMuted}` }}>
              {filtersOpen && (
                <RcaFilterBar
                  axes={filterAxes}
                  state={fState}
                  onToggle={filters.toggle}
                  onClearAxis={(axis) => filters.setAxis(axis, [])}
                />
              )}
              {/* Search + views + group-by + count — always visible, with the fold toggle */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                <button
                  onClick={toggleFiltersOpen}
                  aria-expanded={filtersOpen}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, background: tvTokens.paper, color: tvTokens.ink500, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}
                  title={filtersOpen ? 'Hide the filter chips' : 'Show the filter chips'}
                >
                  <Filter size={11} />
                  {filtersOpen ? 'Hide filters' : 'Filters'}
                  {!filtersOpen && activeFilterCount > 0 && (
                    <span style={{ padding: '0 6px', borderRadius: 8, background: tvTokens.accentSoft, color: tvTokens.accentInk }}>{activeFilterCount}</span>
                  )}
                  {filtersOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {!filtersOpen && (journeyCounts['intake'] ?? 0) > 0 && (
                  <button onClick={() => filters.toggle('journey', 'intake')} style={filterChip(fAxes.journey.includes('intake'))}>
                    Intake review <span style={{ color: '#5a4fcf' }}>{journeyCounts['intake']}</span>
                  </button>
                )}
                <span style={{ ...filterLabel }}>Search</span>
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <Search size={12} style={{ position: 'absolute', left: 9, color: tvTokens.ink400, pointerEvents: 'none' }} />
                  <input
                    value={ticketQuery}
                    onChange={(e) => setTicketQuery(e.target.value)}
                    placeholder="Ticket key, TKT number or title"
                    aria-label="Search tickets by key, TKT number or title"
                    maxLength={200}
                    style={{
                      height: 30,
                      width: 240,
                      padding: '0 26px 0 26px',
                      borderRadius: tvTokens.r,
                      border: `1px solid ${tvTokens.border}`,
                      background: tvTokens.paper,
                      color: tvTokens.ink,
                      fontFamily: tvTokens.fontMono,
                      fontSize: 12,
                      outline: 'none',
                    }}
                  />
                  {ticketQuery && (
                    <button
                      onClick={() => setTicketQuery('')}
                      title="Clear the search"
                      style={{ position: 'absolute', right: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, padding: 0, border: 'none', borderRadius: 8, background: 'transparent', color: tvTokens.ink400, cursor: 'pointer' }}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {paged && (
                  <RcaViewsMenu
                    currentParams={filters.viewParams}
                    onApply={applySavedView}
                    onMessage={flash}
                    isActive={isActiveView}
                  />
                )}
                {paged && (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ ...filterLabel, minWidth: 0 }}><Layers size={11} /> Group</span>
                    <select
                      aria-label="Group by"
                      value={fState.group}
                      onChange={(e) => {
                        setGroupLimit(10);
                        filters.setGroup(e.target.value as RcaGroupBy | '');
                      }}
                      style={{ height: 28, padding: '0 6px', borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, background: tvTokens.paper, color: tvTokens.ink700, fontSize: 12 }}
                    >
                      <option value="">None</option>
                      {GROUP_BY_OPTIONS.map((g) => (
                        <option key={g} value={g}>{GROUP_BY_LABEL[g]}</option>
                      ))}
                    </select>
                  </label>
                )}
                <span data-testid="rca-count" style={{ fontSize: 11.5, color: tvTokens.ink400, fontVariantNumeric: 'tabular-nums' }}>
                  {rcaLogLoading ? (
                    'Updating…'
                  ) : sortedRcaGroups ? (
                    <>{sortedRcaGroups.length} group{sortedRcaGroups.length === 1 ? '' : 's'} · {rcaFilteredTotal} matching</>
                  ) : (
                    <>
                      Showing {filteredRcaLog.length} of {filtersActive ? rcaFilteredTotal : totalTickets}
                      {!paged && (filtersActive ? rcaFilteredTotal : totalTickets) > rcaLog.length && (
                        <span title={`Showing the ${rcaLog.length} most recently updated of ${filtersActive ? `${rcaFilteredTotal} matching` : `${totalTickets} total`}. Chip counts reflect the full totals.`}>
                          {' '}· most recent shown
                        </span>
                      )}
                    </>
                  )}
                </span>
              </div>
              <RcaActiveFilters
                axes={filterAxes}
                state={fState}
                q={ticketQuery}
                onRemove={filters.toggle}
                onClearQ={() => setTicketQuery('')}
                onClearAll={clearRcaFilters}
              />
            </div>
            {filteredRcaLog.length === 0 && !rcaLogLoading ? (
              <p style={{ padding: '18px 16px', margin: 0, fontSize: 13, color: tvTokens.ink400 }}>
                No tickets match {filtersActive ? 'these filters' : 'this status'}.
                {filtersActive && (
                  <>
                    {' '}
                    <button onClick={clearRcaFilters} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: tvTokens.accentInk }}>
                      Clear filters
                    </button>
                  </>
                )}
              </p>
            ) : (
              <div style={{ maxHeight: filtersOpen ? 560 : 760, overflowY: 'auto', overflowX: 'auto', opacity: rcaLogLoading ? 0.55 : 1, transition: 'opacity 120ms ease' }}>
                <div style={{ minWidth: isMobile ? 760 : 0 }}>
                {/* Sortable column header */}
                <div style={{ display: 'grid', gridTemplateColumns: LOG_COLS, alignItems: 'center', gap: 8, padding: '7px 16px', borderBottom: `1px solid ${tvTokens.border}`, position: 'sticky', top: 0, background: tvTokens.paper, zIndex: 1 }}>
                  {canControl && (() => {
                    const eligible = sortedRcaLog.filter((r) => isBulkEligible(r.status)).map((r) => String(r.id));
                    const allSel = eligible.length > 0 && eligible.every((id) => selectedRca.has(id));
                    return (
                      <input
                        type="checkbox"
                        aria-label="Select all eligible tickets"
                        title="Select all eligible tickets on this page"
                        checked={allSel}
                        ref={(el) => { if (el) el.indeterminate = !allSel && eligible.some((id) => selectedRca.has(id)); }}
                        onChange={() => setSelectedRca(allSel ? new Set() : new Set(eligible))}
                        style={{ cursor: 'pointer', width: 14, height: 14 }}
                      />
                    );
                  })()}
                  {([['status', 'Status'], ['verdict', 'Type'], ['ticket', 'Ticket'], ['priority', 'Priority'], ['due', 'Due'], ['title', 'Title'], ['source', 'Jira'], ['updated', 'Updated'], ['action', 'Action']] as [string, string][]).map(([key, label]) => {
                    const active = logSort.key === key;
                    const sortable = key !== 'title';
                    return (
                      <button
                        key={key}
                        onClick={sortable ? () => toggleSort(key) : undefined}
                        disabled={!sortable}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', padding: 0, cursor: sortable ? 'pointer' : 'default', textAlign: 'left', fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, color: active ? tvTokens.accentInk : tvTokens.ink400 }}
                      >
                        {label}
                        {active && (logSort.dir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
                      </button>
                    );
                  })}
                </div>
                {sortedRcaGroups && activeGroup ? (
                  <RcaGroupSections groupBy={activeGroup} groups={sortedRcaGroups} renderRow={renderRcaRow} onShowMore={showMoreInGroup} />
                ) : (
                  sortedRcaLog.map(renderRcaRow)
                )}
                {!sortedRcaGroups && nextCursor && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderTop: `1px solid ${tvTokens.borderMuted}` }}>
                    <button onClick={() => void loadMoreRca()} disabled={loadingMore} style={btnGhost}>
                      {loadingMore ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <ChevronDown size={13} />} Load more
                    </button>
                    <span style={{ fontSize: 11.5, color: tvTokens.ink400, fontVariantNumeric: 'tabular-nums' }}>
                      Showing {filteredRcaLog.length} of {filtersActive ? rcaFilteredTotal : totalTickets}
                    </span>
                  </div>
                )}
                </div>
              </div>
            )}
          </div>
        )}
          </>
        )}

        {/* ── Agent catalog tab: catalog table + recent runs feed ── */}
        {/* ── Observability tab: the observability agent's issue feed ── */}
        {agentTab === 'issues' && (
          <div style={{ ...cardStyle, marginBottom: 20, padding: 16 }}>
            <ObservabilityIssuesPanel focusIssueId={focusIssueId} />
          </div>
        )}

        {/* ── Review & deploy tab: contributor PRs awaiting the approver ── */}
        {agentTab === 'prs' && prod === 'weavein' && (
          <div style={{ ...cardStyle, marginBottom: 20, padding: 16 }}>
            <ContributorPrsPanel canControl={canControl} />
          </div>
        )}

        {/* ── Funder interfaces tab: rules builder + interface harness (TRUS-778) ── */}
        {agentTab === 'funders' && prod === 'weavein' && (
          <div style={{ ...cardStyle, marginBottom: 20, padding: 16 }}>
            <FunderInterfacesPanel canControl={canControl} />
          </div>
        )}

        {agentTab === 'catalog' && (
          <>
        {/* Agent catalog */}
        <div style={{ ...cardStyle, marginBottom: 20 }}>
          <div style={{ ...cardHead, flexWrap: 'wrap', gap: 10 }}>
            <span style={cardTitle}>Agent catalog</span>
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {['all', 'llm', 'ml', 'workflow', 'operator'].map((k) => (
                <button
                  key={k}
                  onClick={() => setKindFilter(k)}
                  style={{
                    fontFamily: tvTokens.fontMono,
                    fontSize: 10.5,
                    fontWeight: 600,
                    padding: '3px 9px',
                    borderRadius: 20,
                    cursor: 'pointer',
                    textTransform: k === 'all' ? 'none' : 'uppercase',
                    border: `1px solid ${kindFilter === k ? tvTokens.accent : tvTokens.border}`,
                    background: kindFilter === k ? tvTokens.accentSoft : 'transparent',
                    color: kindFilter === k ? tvTokens.accentInk : tvTokens.ink500,
                  }}
                >
                  {k === 'all' ? `All ${agents.length}` : KIND_LABEL[k]}
                </button>
              ))}
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
              <thead>
                <tr>
                  {['Agent', 'Kind', 'Trigger', 'Gate', 'Last run', '24h', 'Budget', 'Status', ''].map((h, i) => (
                    <th key={i} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: tvTokens.ink400 }}>Loading…</td></tr>
                ) : filteredAgents.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: tvTokens.ink400 }}>No agents match this filter.</td></tr>
                ) : (
                  filteredAgents.map((a) => {
                    const s = statsByKey[a.key];
                    return (
                      <tr key={a.key} style={{ borderTop: `1px solid ${tvTokens.borderMuted}` }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600, color: tvTokens.ink, display: 'flex', alignItems: 'center', gap: 7 }}>
                            {a.display_name}
                            {a.org_scope && a.org_scope !== 'global' && (
                              <span style={{ fontFamily: tvTokens.fontMono, fontSize: 9.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tvTokens.ink500, background: tvTokens.paper2, padding: '1px 6px', borderRadius: 4 }}>
                                {a.org_scope === 'per_program' ? 'per-program' : a.org_scope === 'per_cbo' ? 'per-agency' : a.org_scope}
                              </span>
                            )}
                            {a.voice_mode && a.voice_mode !== 'none' && (
                              <span
                                title={a.voice_mode === 'dialogue' ? 'Voice: continuous conversation' : 'Voice: spoken command'}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: tvTokens.fontMono, fontSize: 9.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5a4fcf', background: '#efedfb', padding: '1px 6px', borderRadius: 4 }}
                              >
                                <Phone size={9} /> {a.voice_mode}
                              </span>
                            )}
                          </div>
                          <div style={{ fontFamily: tvTokens.fontMono, fontSize: 10.5, color: tvTokens.ink400 }}>{a.key}</div>
                        </td>
                        <td style={tdStyle}><span style={{ ...pillMono, color: kindColor(a.kind), background: 'transparent', border: `1px solid ${tvTokens.border}` }}>{KIND_LABEL[a.kind] || a.kind}</span></td>
                        <td style={{ ...tdStyle, fontFamily: tvTokens.fontMono, fontSize: 11, color: tvTokens.ink500 }}>
                          {a.schedule_editable && canControl ? (
                            <button
                              onClick={() => setScheduleAgent(a)}
                              disabled={busyKey === a.key}
                              title={`Edit cadence (${a.cron_expr || ''})`}
                              style={{ ...budgetBtn, fontSize: 10.5, fontFamily: tvTokens.fontSans }}
                            >
                              <Clock size={11} /> {humanizeCron(a.cron_expr) || humanizeCron(a.schedule_ref)}
                            </button>
                          ) : (
                            <span title={a.cron_expr || a.schedule_ref || ''}>{humanizeCron(a.cron_expr) || humanizeCron(a.schedule_ref) || a.trigger_type}</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {a.gate_policy === 'human_approval'
                            ? <span style={{ ...pillMono, color: '#5a4fcf', background: '#efedfb' }}>gated</span>
                            : <span style={{ ...pillMono, color: tvTokens.ink400, background: 'transparent' }}>auto</span>}
                        </td>
                        <td style={{ ...tdStyle, color: tvTokens.ink500 }}>{s?.last ? rel(s.last) : '—'}</td>
                        <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>
                          {s ? s.count : 0}
                          {s && s.failed > 0 && <span style={{ color: '#cc3b57', marginLeft: 5 }}>· {s.failed}✗</span>}
                        </td>
                        <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>
                          {canControl ? (
                            <button onClick={() => setBudget(a)} disabled={busyKey === a.key} style={budgetBtn} title="Set monthly budget">
                              <Wallet size={11} /> {a.budget_usd != null ? `$${a.budget_usd}` : 'set'}
                            </button>
                          ) : (
                            <span style={{ fontFamily: tvTokens.fontMono, fontSize: 11, color: tvTokens.ink500 }}>
                              {a.budget_usd != null ? `$${a.budget_usd}` : '—'}
                            </span>
                          )}
                          {s && s.cost > 0 && <div style={{ fontSize: 10.5, color: tvTokens.ink400, marginTop: 2 }}>${s.cost.toFixed(3)}/24h</div>}
                        </td>
                        <td style={tdStyle}>
                          <span style={{ ...pillMono, ...(a.status === 'active' ? { color: '#12855f', background: '#e3f3ec' } : a.status === 'paused' ? { color: '#b9781f', background: '#f8efda' } : { color: tvTokens.ink500, background: 'transparent' }) }}>{a.status}</span>
                        </td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {a.has_policy && (
                            <button onClick={() => setPolicyAgent(a)} style={{ ...iconBtn, width: 'auto', padding: '0 9px', gap: 4, fontFamily: tvTokens.fontMono, fontSize: 10.5 }} title={`Edit policy${a.policy_version != null ? ` (v${a.policy_version})` : ''}`}>
                              <ScrollText size={12} /> Policy
                            </button>
                          )}
                          {canControl ? (
                            <>
                              <button onClick={() => toggleStatus(a)} disabled={busyKey === a.key} style={{ ...iconBtn, marginLeft: a.has_policy ? 6 : 0 }} title={a.status === 'paused' ? 'Resume' : 'Pause'}>
                                {a.status === 'paused' ? <Play size={13} /> : <Pause size={13} />}
                              </button>
                              {a.can_run_now && (
                                <button onClick={() => runNow(a)} disabled={busyKey === a.key} style={{ ...iconBtn, marginLeft: 6 }} title="Run now">
                                  <RefreshCw size={13} style={{ animation: busyKey === a.key ? 'spin 1s linear infinite' : 'none' }} />
                                </button>
                              )}
                              {a.org_scope && a.org_scope !== 'global' && (
                                <button onClick={() => setScopeAgent(a)} style={{ ...iconBtn, marginLeft: 6, width: 'auto', padding: '0 9px', fontFamily: tvTokens.fontMono, fontSize: 10.5 }} title="Per-agency / per-program scopes">
                                  Scopes
                                </button>
                              )}
                              {a.trigger_type === 'manual' && a.kind !== 'ml' && (
                                <button onClick={() => { setRunTarget(a); setRunInstruction(''); setRunResult(null); }} style={{ ...iconBtn, marginLeft: 6, width: 'auto', padding: '0 9px', gap: 4, fontFamily: tvTokens.fontMono, fontSize: 10.5, color: tvTokens.accentInk }} title="Run this agent on an instruction">
                                  <Play size={12} /> Run
                                </button>
                              )}
                              {a.voice_mode === 'dialogue' && (
                                <button onClick={() => { setVoiceAgent(a); setVoiceNumber(''); }} style={{ ...iconBtn, marginLeft: 6, width: 'auto', padding: '0 9px', gap: 4, fontFamily: tvTokens.fontMono, fontSize: 10.5, color: '#5a4fcf' }} title="Start a voice conversation with this agent">
                                  <Phone size={12} /> Call
                                </button>
                              )}
                            </>
                          ) : (
                            !a.has_policy && <span style={{ fontSize: 11, color: tvTokens.ink400 }}>view only</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent runs */}
        <div style={cardStyle}>
          <div style={cardHead}>
            <span style={cardTitle}>Recent runs</span>
            <span style={{ ...pill, background: tvTokens.paper2, color: tvTokens.ink500 }}>{runs.length}</span>
          </div>
          {runs.length === 0 ? (
            <p style={{ padding: '18px 16px', margin: 0, fontSize: 13, color: tvTokens.ink400 }}>
              No runs recorded yet. Agent runs appear here as the scheduled and event-driven agents fire.
            </p>
          ) : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {runs.slice(0, 100).map((r) => (
                <div key={r.id} style={isMobile ? { ...rowStyle, flexWrap: 'wrap', padding: '10px 12px' } : rowStyle}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: r.status === 'failed' ? '#cc3b57' : '#12855f', flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: isMobile ? '1 1 100%' : 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: tvTokens.ink }}>{r.task_name || r.task_type}</span>
                    {r.error && <span style={{ fontSize: 12, color: '#cc3b57', marginLeft: 8 }}>{r.error}</span>}
                  </div>
                  {r.model && r.model !== 'n/a' && <span style={pillMono}>{r.model}</span>}
                  {r.total_tokens > 0 && <span style={{ fontFamily: tvTokens.fontMono, fontSize: 11, color: tvTokens.ink400 }}>{r.total_tokens.toLocaleString()} tok</span>}
                  {r.cost_usd > 0 && <span style={{ fontFamily: tvTokens.fontMono, fontSize: 11, color: tvTokens.ink700 }}>${r.cost_usd.toFixed(4)}</span>}
                  <span style={{ ...pillMono, background: 'transparent', color: tvTokens.ink400 }}>{r.execution_mode}</span>
                  <span style={{ fontSize: 11.5, color: tvTokens.ink400, minWidth: 64, textAlign: 'right' }}>{rel(r.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}
      </div>
      {showCreate && (
        <CreateAgentModal
          onClose={() => setShowCreate(false)}
          onCreated={(name) => {
            setShowCreate(false);
            flash(`Agent "${name}" created and active.`);
            void load();
          }}
        />
      )}

      {scopeAgent && (
        <ScopePanel agent={scopeAgent} onClose={() => setScopeAgent(null)} onToast={flash} />
      )}

      {showCaps && (
        <div
          onClick={() => setShowCaps(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,22,34,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(680px, 100%)', maxHeight: '86vh', overflowY: 'auto', background: tvTokens.paper, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r, boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
          >
            <div style={{ ...cardHead, position: 'sticky', top: 0, background: tvTokens.paper, zIndex: 1 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <Bot size={15} color={tvTokens.accentInk} />
                <span style={cardTitle}>What “Hand off to agent” can do</span>
              </span>
              <button onClick={() => setShowCaps(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tvTokens.ink400 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '12px 16px 16px' }}>
              <p style={{ margin: '0 0 12px', fontSize: 12.5, color: tvTokens.ink500, lineHeight: 1.5 }}>
                When you hand a ticket to the agent, it executes these operational actions directly (or drafts a
                reviewable PR for code). It never merges — a human always does.
              </p>
              {!caps ? (
                <div style={{ fontSize: 13, color: tvTokens.ink400, padding: '10px 0' }}>Loading…</div>
              ) : (
                <>
                  {(caps.groups || []).map((g: any) => (
                    <div key={g.group} style={{ marginBottom: 14 }}>
                      <div style={{ fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, color: tvTokens.accentInk, marginBottom: 6 }}>
                        {g.group}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {(g.items || []).map((it: any) => (
                          <div key={it.key} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                            <Check size={13} style={{ color: '#12855f', flexShrink: 0, marginTop: 2 }} />
                            <div style={{ minWidth: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: tvTokens.ink }}>{it.label}</span>
                              {it.needs && (
                                <span style={{ ...pillMono, fontSize: 9, padding: '0 6px', marginLeft: 6, color: '#b9781f', background: '#f8efda' }}>needs {it.needs}</span>
                              )}
                              <div style={{ fontSize: 12, color: tvTokens.ink500, lineHeight: 1.45 }}>{it.detail}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(caps.cannot || []).length > 0 && (
                    <div style={{ marginTop: 6, paddingTop: 12, borderTop: `1px solid ${tvTokens.borderMuted}` }}>
                      <div style={{ fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, color: '#cc3b57', marginBottom: 6 }}>
                        Can’t do (needs a human)
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {caps.cannot.map((c: string) => (
                          <div key={c} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                            <X size={12} style={{ color: '#cc3b57', flexShrink: 0, marginTop: 2 }} />
                            <span style={{ fontSize: 12.5, color: tvTokens.ink500 }}>{c}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showTools && (
        <div
          onClick={() => setShowTools(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,22,34,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(720px, 100%)', maxHeight: '86vh', overflowY: 'auto', background: tvTokens.paper, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r, boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
          >
            <div style={{ ...cardHead, position: 'sticky', top: 0, background: tvTokens.paper, zIndex: 1 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <Wrench size={15} color={tvTokens.accentInk} />
                <span style={cardTitle}>Agent tool catalogue</span>
              </span>
              <button onClick={() => setShowTools(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tvTokens.ink400 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '12px 16px 16px' }}>
              <p style={{ margin: '0 0 12px', fontSize: 12.5, color: tvTokens.ink500, lineHeight: 1.5 }}>
                The shared actions agents can run. The same tools power voice AI and this console; a{' '}
                <span style={{ ...pillMono, fontSize: 9, padding: '0 6px', color: '#5a4fcf', background: '#efedfb' }}>gated</span>{' '}
                tool needs approval before it takes effect.
              </p>
              {!toolGroups ? (
                <div style={{ fontSize: 13, color: tvTokens.ink400, padding: '10px 0' }}>Loading…</div>
              ) : Object.keys(toolGroups).length === 0 ? (
                <div style={{ fontSize: 13, color: tvTokens.ink400, padding: '10px 0' }}>No tools available.</div>
              ) : (
                Object.entries(toolGroups).map(([group, items]) => (
                  <div key={group} style={{ marginBottom: 14 }}>
                    <div style={{ fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, color: tvTokens.accentInk, marginBottom: 6 }}>
                      {group}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(items || []).map((t: any) => (
                        <div key={t.name} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                          {t.gate === 'human_approval'
                            ? <ShieldCheck size={13} style={{ color: '#5a4fcf', flexShrink: 0, marginTop: 2 }} />
                            : <Check size={13} style={{ color: '#12855f', flexShrink: 0, marginTop: 2 }} />}
                          <div style={{ minWidth: 0 }}>
                            <span style={{ fontFamily: tvTokens.fontMono, fontSize: 12, fontWeight: 700, color: tvTokens.ink }}>{t.name}</span>
                            {t.gate === 'human_approval' && (
                              <span style={{ ...pillMono, fontSize: 9, padding: '0 6px', marginLeft: 6, color: '#5a4fcf', background: '#efedfb' }}>gated</span>
                            )}
                            <div style={{ fontSize: 12.5, color: tvTokens.ink500, lineHeight: 1.45 }}>{t.summary}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {voiceAgent && (
        <div
          onClick={() => setVoiceAgent(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,22,34,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(420px, 100%)', background: tvTokens.paper, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r, boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
          >
            <div style={cardHead}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <Phone size={15} color="#5a4fcf" />
                <span style={cardTitle}>Start a voice conversation</span>
              </span>
              <button onClick={() => setVoiceAgent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tvTokens.ink400 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '14px 16px 18px' }}>
              <p style={{ margin: '0 0 12px', fontSize: 12.5, color: tvTokens.ink500, lineHeight: 1.5 }}>
                <strong style={{ color: tvTokens.ink }}>{voiceAgent.display_name}</strong> will call the number below and
                hold a live conversation. It can look things up and draft, but sending or launching still needs approval here.
              </p>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: tvTokens.ink500, marginBottom: 5 }}>Phone number</label>
              <input
                value={voiceNumber}
                onChange={(e) => setVoiceNumber(e.target.value)}
                placeholder="+15551234567"
                autoFocus
                style={{ width: '100%', padding: '9px 11px', fontSize: 14, fontFamily: tvTokens.fontMono, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, background: tvTokens.paper, color: tvTokens.ink, boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button onClick={() => setVoiceAgent(null)} style={btnGhost}>Cancel</button>
                <button onClick={startCall} disabled={voiceBusy || !voiceNumber.trim()} style={{ ...btnPrimary, opacity: voiceBusy || !voiceNumber.trim() ? 0.6 : 1 }}>
                  <Phone size={13} /> {voiceBusy ? 'Calling…' : 'Place call'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {runTarget && (
        <div
          onClick={() => { if (!runBusy) setRunTarget(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,22,34,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(620px, 100%)', maxHeight: '86vh', overflowY: 'auto', background: tvTokens.paper, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.r, boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
          >
            <div style={{ ...cardHead, position: 'sticky', top: 0, background: tvTokens.paper, zIndex: 1 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <Play size={15} color={tvTokens.accentInk} />
                <span style={cardTitle}>Run {runTarget.display_name}</span>
              </span>
              <button onClick={() => { if (!runBusy) setRunTarget(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tvTokens.ink400 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '14px 16px 18px' }}>
              <p style={{ margin: '0 0 10px', fontSize: 12.5, color: tvTokens.ink500, lineHeight: 1.5 }}>
                Give the agent an instruction. It works step by step and stops at anything that needs approval (publishing, sending).
              </p>
              <textarea
                value={runInstruction}
                onChange={(e) => setRunInstruction(e.target.value)}
                placeholder="e.g. Build a landing page for our new plan"
                rows={3}
                autoFocus
                style={{ width: '100%', padding: '9px 11px', fontSize: 13.5, fontFamily: tvTokens.fontSans, border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rSm, background: tvTokens.paper, color: tvTokens.ink, boxSizing: 'border-box', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button onClick={() => setRunTarget(null)} disabled={runBusy} style={btnGhost}>Close</button>
                <button onClick={doRun} disabled={runBusy || !runInstruction.trim()} style={{ ...btnPrimary, opacity: runBusy || !runInstruction.trim() ? 0.6 : 1 }}>
                  <Play size={13} /> {runBusy ? 'Running…' : 'Run'}
                </button>
              </div>
              {runResult && (
                <div style={{ marginTop: 16, borderTop: `1px solid ${tvTokens.borderMuted}`, paddingTop: 14 }}>
                  {runResult.ok === false ? (
                    <div style={{ fontSize: 13, color: '#cc3b57' }}>{runResult.error || 'The run failed.'}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 13.5, color: tvTokens.ink, lineHeight: 1.5, marginBottom: 10 }}>{runResult.summary}</div>
                      {Array.isArray(runResult.steps) && runResult.steps.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, color: tvTokens.ink400, marginBottom: 5 }}>Steps</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {runResult.steps.map((s: any, i: number) => (
                              <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'baseline', fontSize: 12.5 }}>
                                {s.ok ? <Check size={12} style={{ color: '#12855f', flexShrink: 0 }} /> : <Circle size={11} style={{ color: tvTokens.ink400, flexShrink: 0 }} />}
                                <span style={{ fontFamily: tvTokens.fontMono, color: tvTokens.ink500 }}>{s.tool}</span>
                                {s.status && s.status !== 'done' && <span style={{ ...pillMono, fontSize: 9, padding: '0 6px', color: s.status === 'gated' ? '#5a4fcf' : tvTokens.ink400, background: s.status === 'gated' ? '#efedfb' : 'transparent' }}>{s.status}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {Array.isArray(runResult.pending_approvals) && runResult.pending_approvals.length > 0 && (
                        <div style={{ background: '#f8efda', borderRadius: tvTokens.rSm, padding: '8px 11px' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#b9781f', marginBottom: 3 }}>Awaiting your approval</div>
                          <div style={{ fontFamily: tvTokens.fontMono, fontSize: 11.5, color: tvTokens.ink500 }}>
                            {runResult.pending_approvals.map((p: any) => p.tool).join(', ')}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {scheduleAgent && (
        <ScheduleModal
          agent={scheduleAgent}
          busy={busyKey === scheduleAgent.key}
          onClose={() => setScheduleAgent(null)}
          onSave={(cron) => saveSchedule(scheduleAgent, cron)}
        />
      )}

      {policyAgent && (
        <PolicyModal
          agent={policyAgent}
          canControl={canControl}
          onClose={() => setPolicyAgent(null)}
          onToast={flash}
          onSaved={() => { setPolicyAgent(null); void load(); }}
        />
      )}

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: tvTokens.ink,
            color: '#fff',
            padding: '10px 18px',
            borderRadius: 10,
            fontSize: 13.5,
            fontWeight: 500,
            boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
            zIndex: 3000,
          }}
        >
          {toast}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 28,
  borderRadius: 7,
  border: `1px solid ${tvTokens.border}`,
  background: tvTokens.surface,
  color: tvTokens.ink700,
  cursor: 'pointer',
};
const budgetBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 8px',
  borderRadius: 6,
  border: `1px solid ${tvTokens.border}`,
  background: tvTokens.surface,
  color: tvTokens.ink700,
  fontFamily: tvTokens.fontMono,
  fontSize: 11,
  cursor: 'pointer',
};
// Gate-queue item: column layout (header row + detail block below), unlike the
// centered single-line rowStyle used elsewhere.
const gateItemStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: '11px 16px',
  borderTop: `1px solid ${tvTokens.borderMuted}`,
};
const btnApprove: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  padding: '5px 10px',
  borderRadius: 7,
  border: 'none',
  background: '#12855f',
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
// "Confirm complete" — a neutral, outlined confirm (distinct from the green
// primary approve/hand-off) so the three OPERATIONAL actions read clearly.
const btnDone: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  padding: '5px 10px',
  borderRadius: 7,
  border: `1px solid ${tvTokens.border}`,
  background: tvTokens.surface,
  color: tvTokens.ink700,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const btnReject: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 28,
  borderRadius: 7,
  border: `1px solid ${tvTokens.border}`,
  background: tvTokens.surface,
  color: tvTokens.ink500,
  cursor: 'pointer',
};
// btnReject with a text label ("Dismiss"). btnReject is a fixed 30px icon square, so a label
// inside it spills over the neighbouring button — this sizes to its content instead.
const btnDismiss: React.CSSProperties = {
  ...btnReject,
  width: 'auto',
  height: 'auto',
  gap: 4,
  padding: '5px 10px',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
// "Decline" — an outlined, red-tinted text button for declining a feature request
// (distinct from the icon-only btnReject dismiss, and from the neutral Defer/Done).
const btnDecline: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '5px 10px',
  borderRadius: 7,
  border: '1px solid #f0c2cc',
  background: '#fbe6ea',
  color: '#cc3b57',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
// "Merge PR" — outlined, mirrors "Confirm complete" so it reads as a secondary
// action next to the green primary approve.
const btnMerge: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  padding: '5px 10px',
  borderRadius: 7,
  border: `1px solid ${tvTokens.border}`,
  background: tvTokens.surface,
  color: tvTokens.ink700,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
// TRUS-611: Merge once a ticket is approved for implementation — the single next step, so it
// is the one labelled, filled action on the row.
const btnMergeNext: React.CSSProperties = {
  ...btnMerge,
  border: 'none',
  background: '#5a4fcf',
  color: '#fff',
};
// Inline red "Can't merge: {reason}" note shown under the button on a
// merged:false verdict (failing CI / conflict / draft).
const mergeReasonStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: 11.5,
  lineHeight: 1.4,
  color: '#cc3b57',
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 12px',
  background: 'transparent',
  border: `1px solid ${tvTokens.border}`,
  borderRadius: tvTokens.r,
  color: tvTokens.ink700,
  fontFamily: tvTokens.fontDisplay,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
};
const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 13px',
  background: tvTokens.accent,
  border: 'none',
  borderRadius: tvTokens.r,
  color: '#fff',
  fontFamily: tvTokens.fontDisplay,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const tileStyle: React.CSSProperties = {
  background: tvTokens.surface,
  border: `1px solid ${tvTokens.border}`,
  borderRadius: 12,
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const cardStyle: React.CSSProperties = {
  background: tvTokens.surface,
  border: `1px solid ${tvTokens.border}`,
  borderRadius: 12,
  overflow: 'hidden',
};
const cardHead: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '13px 16px',
  borderBottom: `1px solid ${tvTokens.borderMuted}`,
};
const cardTitle: React.CSSProperties = { fontFamily: tvTokens.fontDisplay, fontSize: 15, fontWeight: 650, color: tvTokens.ink };
const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 16px',
  borderTop: `1px solid ${tvTokens.borderMuted}`,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  fontFamily: tvTokens.fontMono,
  fontSize: 10,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: tvTokens.ink500,
  fontWeight: 600,
  padding: '10px 14px',
  background: tvTokens.paper2,
};
const tdStyle: React.CSSProperties = { padding: '10px 14px', verticalAlign: 'middle', color: tvTokens.ink700 };
const labelMono: React.CSSProperties = { fontFamily: tvTokens.fontMono, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' };
const pill: React.CSSProperties = { fontFamily: tvTokens.fontMono, fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20 };
const pillMono: React.CSSProperties = { fontFamily: tvTokens.fontMono, fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 5, background: tvTokens.paper2, color: tvTokens.ink500, whiteSpace: 'nowrap' };

// The ticket filter-bar styles (filterLabel / filterChip) live in components/agents/rcaStyles.

// Tier-1 authoring modal — compose a no-code rule agent (trigger → action).
const TRIGGERS = [
  { value: 'intent_signal', label: 'Intent signal (score/type)' },
  { value: 'campaign_reply', label: 'Campaign reply' },
  { value: 'form_submitted', label: 'Form submitted' },
  { value: 'page_view', label: 'Page view' },
  { value: 'list_joined', label: 'Added to a list' },
];

function CreateAgentModal({ onClose, onCreated }: { onClose: () => void; onCreated: (name: string) => void }) {
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('intent_signal');
  const [signalType, setSignalType] = useState('');
  const [minScore, setMinScore] = useState('');
  const [actionType, setActionType] = useState<'add_to_list' | 'enroll_in_campaign'>('add_to_list');
  const [target, setTarget] = useState('');
  const [lists, setLists] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<'rule' | 'llm' | 'coded'>('rule');
  const [taskTarget, setTaskTarget] = useState('');
  const [cronExpr, setCronExpr] = useState('0 * * * *');
  const [product, setProduct] = useState('shared');
  const [llmPrompt, setLlmPrompt] = useState('');
  const [llmModel, setLlmModel] = useState('claude-sonnet-4-6');
  const [llmGate, setLlmGate] = useState('human_approval');

  useEffect(() => {
    (async () => {
      try {
        const [l, c]: any[] = await Promise.all([
          marketingApi.getLists({ page_size: 100 } as any).catch(() => null),
          marketingApi.getCampaigns({ page_size: 100 } as any).catch(() => null),
        ]);
        // Responses are { success, data } and data may itself wrap the array
        // ({lists:[…]} / {campaigns:[…]}). Dig to the array so .map never throws.
        const toArray = (r: any, ...keys: string[]): any[] => {
          const d = r?.data ?? r;
          if (Array.isArray(d)) return d;
          for (const k of keys) if (Array.isArray(d?.[k])) return d[k];
          return [];
        };
        setLists(toArray(l, 'lists', 'items', 'data'));
        setCampaigns(toArray(c, 'campaigns', 'items', 'data'));
      } catch {
        /* leave empty; user can still cancel */
      }
    })();
  }, []);

  const submit = async () => {
    if (name.trim().length < 2) {
      setErr('Give the agent a name.');
      return;
    }
    if (mode === 'llm') {
      if (llmPrompt.trim().length < 8) {
        setErr('Write a prompt for the agent.');
        return;
      }
      if (cronExpr.trim().split(/\s+/).length !== 5) {
        setErr('Schedule must be a 5-field cron expression.');
        return;
      }
      setSaving(true);
      setErr(null);
      try {
        await agentsApi.createLlmAgent({
          display_name: name.trim(),
          prompt: llmPrompt.trim(),
          cron_expr: cronExpr.trim(),
          model: llmModel,
          gate_policy: llmGate,
          product,
        });
        onCreated(name.trim());
      } catch {
        setErr('Could not create the LLM agent. Please try again.');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (mode === 'coded') {
      if (!taskTarget.trim()) {
        setErr('Enter the Celery task name (e.g. jobs.foo.bar).');
        return;
      }
      if (cronExpr.trim().split(/\s+/).length !== 5) {
        setErr('Schedule must be a 5-field cron expression.');
        return;
      }
      setSaving(true);
      setErr(null);
      try {
        await agentsApi.createCodedAgent({
          display_name: name.trim(),
          task_target: taskTarget.trim(),
          cron_expr: cronExpr.trim(),
          product,
        });
        onCreated(name.trim());
      } catch {
        setErr('Could not register the task. Please try again.');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!target) {
      setErr(actionType === 'add_to_list' ? 'Pick a list to add matches to.' : 'Pick a campaign to enroll matches in.');
      return;
    }
    const triggerConfig: Record<string, any> = {};
    if (trigger === 'intent_signal') {
      if (signalType.trim()) triggerConfig.signal_type = signalType.trim();
      if (minScore.trim()) triggerConfig.min_score = Number(minScore);
    }
    const actionConfig = actionType === 'add_to_list' ? { list_id: target } : { campaign_id: target };
    setSaving(true);
    setErr(null);
    try {
      await agentsApi.createAgent({
        display_name: name.trim(),
        trigger_type: trigger,
        trigger_config: triggerConfig,
        action_type: actionType,
        action_config: actionConfig,
      });
      onCreated(name.trim());
    } catch {
      setErr('Could not create the agent. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const field: React.CSSProperties = {
    width: '100%',
    height: 38,
    padding: '0 11px',
    borderRadius: tvTokens.r,
    border: `1px solid ${tvTokens.border}`,
    background: tvTokens.paper,
    color: tvTokens.ink,
    fontSize: 14,
    outline: 'none',
  };
  const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: tvTokens.ink700, marginBottom: 5, display: 'block' };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,23,28,0.45)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(12px, 3vw, 20px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="triveny-platform"
        style={{ width: 'min(460px, 92vw)', background: tvTokens.surface, borderRadius: 14, border: `1px solid ${tvTokens.border}`, boxShadow: '0 24px 60px rgba(0,0,0,0.28)', padding: 22, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Bot size={18} color={tvTokens.accent} />
          <h2 style={{ margin: 0, fontFamily: tvTokens.fontDisplay, fontSize: 19, fontWeight: 650, color: tvTokens.ink }}>New agent</h2>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: tvTokens.ink500 }}>
          {mode === 'rule'
            ? 'No code — when the trigger fires, the action runs. Governed like every other agent.'
            : mode === 'llm'
              ? 'Write a prompt and a schedule; Claude runs it on cadence. Gated output waits for your review.'
              : 'Schedule an existing worker task as a governed agent (the task must already run in the worker).'}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {(['rule', 'llm', 'coded'] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setErr(null); }}
              style={{
                fontFamily: tvTokens.fontDisplay,
                fontSize: 12,
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: tvTokens.r,
                cursor: 'pointer',
                border: `1px solid ${mode === m ? tvTokens.accent : tvTokens.border}`,
                background: mode === m ? tvTokens.accentSoft : 'transparent',
                color: mode === m ? tvTokens.accentInk : tvTokens.ink500,
              }}
            >
              {m === 'rule' ? 'No-code rule' : m === 'llm' ? 'LLM agent' : 'Coded task'}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          <div>
            <label style={lbl}>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={mode === 'rule' ? 'e.g. Hot demo requests → Sales list' : 'e.g. Nightly data reconcile'} style={field} />
          </div>

          {mode === 'coded' && (
            <>
              <div>
                <label style={lbl}>Celery task</label>
                <input value={taskTarget} onChange={(e) => setTaskTarget(e.target.value)} placeholder="jobs.my_module.my_task" style={{ ...field, fontFamily: tvTokens.fontMono, fontSize: 13 }} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={lbl}>Schedule</label>
                  <CronPicker value={cronExpr} onChange={setCronExpr} />
                </div>
                <div style={{ flex: '1 1 130px' }}>
                  <label style={lbl}>Product</label>
                  <select value={product} onChange={(e) => setProduct(e.target.value)} style={field}>
                    <option value="shared">Shared</option>
                    <option value="triveny">Triveny</option>
                    <option value="weavein">WeaveIn</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {mode === 'llm' && (
            <>
              <div>
                <label style={lbl}>Prompt</label>
                <textarea
                  value={llmPrompt}
                  onChange={(e) => setLlmPrompt(e.target.value)}
                  placeholder="e.g. Summarize this week's hot accounts and suggest one play for each."
                  rows={5}
                  style={{ ...field, height: 'auto', padding: '9px 11px', resize: 'vertical', lineHeight: 1.5 }}
                />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={lbl}>Schedule</label>
                  <CronPicker value={cronExpr} onChange={setCronExpr} />
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={lbl}>Model</label>
                  <select value={llmModel} onChange={(e) => setLlmModel(e.target.value)} style={field}>
                    <option value="claude-sonnet-4-6">Sonnet 4.6</option>
                    <option value="claude-haiku-4-5">Haiku 4.5</option>
                    <option value="claude-opus-4-8">Opus 4.8</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={lbl}>Output</label>
                <select value={llmGate} onChange={(e) => setLlmGate(e.target.value)} style={field}>
                  <option value="human_approval">Hold for review (recommended)</option>
                  <option value="autonomous">Post automatically</option>
                </select>
              </div>
            </>
          )}

          {mode === 'rule' && (
          <><div>
            <label style={lbl}>When… (trigger)</label>
            <select value={trigger} onChange={(e) => setTrigger(e.target.value)} style={field}>
              {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {trigger === 'intent_signal' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ flex: '1 1 160px' }}>
                <label style={lbl}>Signal type (optional)</label>
                <input value={signalType} onChange={(e) => setSignalType(e.target.value)} placeholder="demo_request" style={field} />
              </div>
              <div style={{ flex: '1 1 110px' }}>
                <label style={lbl}>Min score</label>
                <input value={minScore} onChange={(e) => setMinScore(e.target.value)} placeholder="70" inputMode="numeric" style={field} />
              </div>
            </div>
          )}
          <div>
            <label style={lbl}>Then… (action)</label>
            <select value={actionType} onChange={(e) => { setActionType(e.target.value as any); setTarget(''); }} style={field}>
              <option value="add_to_list">Add the contact to a list</option>
              <option value="enroll_in_campaign">Enroll the contact in a campaign</option>
            </select>
          </div>
          <div>
            <label style={lbl}>{actionType === 'add_to_list' ? 'List' : 'Campaign'}</label>
            <select value={target} onChange={(e) => setTarget(e.target.value)} style={field}>
              <option value="">Select…</option>
              {(actionType === 'add_to_list' ? lists : campaigns).map((o) => (
                <option key={o.id} value={o.id}>{o.name || o.list_name || o.id}</option>
              ))}
            </select>
            {(actionType === 'add_to_list' ? lists : campaigns).length === 0 && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: tvTokens.ink500 }}>
                No {actionType === 'add_to_list' ? 'contact lists' : 'campaigns'} yet — create one first, or pick a different action.
              </p>
            )}
          </div>
          </>
          )}
          {err && <div style={{ fontSize: 13, color: '#cc3b57' }}>{err}</div>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={saving} style={btnPrimary}>{saving ? 'Creating…' : 'Create agent'}</button>
        </div>
      </div>
    </div>
  );
}

// Friendly cadence editor (presets + advanced cron).
function ScheduleModal({ agent, busy, onClose, onSave }: { agent: Agent; busy: boolean; onClose: () => void; onSave: (cron: string) => void }) {
  const [cron, setCron] = useState(agent.cron_expr || '0 * * * *');
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,23,28,0.45)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(12px, 3vw, 20px)' }}>
      <div onClick={(e) => e.stopPropagation()} className="triveny-platform" style={{ width: 'min(420px, 92vw)', background: tvTokens.surface, borderRadius: 14, border: `1px solid ${tvTokens.border}`, boxShadow: '0 24px 60px rgba(0,0,0,0.28)', padding: 22, maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ margin: '0 0 2px', fontFamily: tvTokens.fontDisplay, fontSize: 18, fontWeight: 650, color: tvTokens.ink }}>{agent.display_name} · cadence</h2>
        <p style={{ margin: '0 0 16px', fontSize: 12.5, color: tvTokens.ink500 }}>How often this agent runs. Currently <strong>{humanizeCron(agent.cron_expr) || '—'}</strong>.</p>
        <CronPicker value={cron} onChange={setCron} />
        <div style={{ marginTop: 10, fontSize: 12, color: tvTokens.ink500 }}>Runs {humanizeCron(cron).toLowerCase()}.</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={() => onSave(cron)} disabled={busy} style={btnPrimary}>{busy ? 'Saving…' : 'Save cadence'}</button>
        </div>
      </div>
    </div>
  );
}

// Editable, versioned agent policy (the prompt the agent's executor follows).
// GET loads the current policy + version history; PATCH creates a new version.
// Editing is gated: page-level canControl AND the per-response can_control.
function PolicyModal({
  agent,
  canControl,
  onClose,
  onToast,
  onSaved,
}: {
  agent: Agent;
  canControl: boolean;
  onClose: () => void;
  onToast: (m: string) => void;
  onSaved: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState('');
  const [note, setNote] = useState('');
  const [version, setVersion] = useState<number | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [versions, setVersions] = useState<{ version: number; note?: string; created_by?: string; created_at?: string }[]>([]);
  const [displayName, setDisplayName] = useState(agent.display_name);
  const [respCanControl, setRespCanControl] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res: any = await agentsApi.getAgentPolicy(agent.key);
        const d = res?.data ?? res;
        setPolicy(typeof d?.policy === 'string' ? d.policy : '');
        setVersion(typeof d?.version === 'number' ? d.version : null);
        setUpdatedAt(d?.updated_at ?? null);
        setUpdatedBy(d?.updated_by ?? null);
        setVersions(Array.isArray(d?.versions) ? d.versions : []);
        if (d?.display_name) setDisplayName(d.display_name);
        setRespCanControl(d?.can_control !== false);
      } catch {
        onToast('Could not load the policy.');
      } finally {
        setLoading(false);
      }
    })();
  }, [agent.key, onToast]);

  const editable = canControl && respCanControl;

  const save = async () => {
    if (!editable) return;
    if (policy.trim().length < 4) {
      onToast('The policy can’t be empty.');
      return;
    }
    setSaving(true);
    try {
      await agentsApi.setAgentPolicy(agent.key, { policy, note: note.trim() || undefined });
      onToast(`Policy saved for ${displayName}.`);
      onSaved();
    } catch {
      onToast('Could not save the policy.');
    } finally {
      setSaving(false);
    }
  };

  const ta: React.CSSProperties = {
    width: '100%',
    minHeight: 340,
    padding: '11px 12px',
    borderRadius: tvTokens.r,
    border: `1px solid ${tvTokens.border}`,
    background: editable ? tvTokens.paper : tvTokens.paper2,
    color: tvTokens.ink,
    fontFamily: tvTokens.fontMono,
    fontSize: 12.5,
    lineHeight: 1.55,
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const fld: React.CSSProperties = { height: 36, width: '100%', padding: '0 10px', borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, background: tvTokens.paper, color: tvTokens.ink, fontSize: 13, boxSizing: 'border-box' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,23,28,0.45)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(12px, 3vw, 20px)' }}>
      <div onClick={(e) => e.stopPropagation()} className="triveny-platform" style={{ width: 'min(640px, 92vw)', background: tvTokens.surface, borderRadius: 14, border: `1px solid ${tvTokens.border}`, boxShadow: '0 24px 60px rgba(0,0,0,0.28)', padding: 22, maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ margin: '0 0 2px', fontFamily: tvTokens.fontDisplay, fontSize: 18, fontWeight: 650, color: tvTokens.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ScrollText size={17} /> {displayName} · policy
        </h2>
        <p style={{ margin: '0 0 14px', fontSize: 12.5, color: tvTokens.ink500 }}>
          The prompt this agent’s executor follows. Saving creates a new version.
          {!editable && <strong style={{ color: '#b9781f' }}> · view only</strong>}
        </p>

        {loading ? (
          <p style={{ padding: '30px 0', textAlign: 'center', color: tvTokens.ink400, fontSize: 13 }}>Loading…</p>
        ) : (
          <>
            {(version != null || updatedAt) && (
              <div style={{ fontSize: 12, color: tvTokens.ink500, marginBottom: 8 }}>
                {version != null && <strong style={{ color: tvTokens.ink }}>v{version}</strong>}
                {updatedAt && <> · updated {rel(updatedAt)}</>}
                {updatedBy && <> by {updatedBy}</>}
              </div>
            )}

            <textarea
              value={policy}
              onChange={(e) => setPolicy(e.target.value)}
              readOnly={!editable}
              rows={18}
              spellCheck={false}
              style={ta}
            />

            {editable && (
              <div style={{ marginTop: 10 }}>
                <label style={{ fontSize: 11, color: tvTokens.ink500, display: 'block', marginBottom: 4 }}>Change note (optional)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed and why" style={fld} />
              </div>
            )}

            {versions.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: tvTokens.ink500, fontFamily: tvTokens.fontMono, marginBottom: 6 }}>Version history</div>
                <div style={{ border: `1px solid ${tvTokens.border}`, borderRadius: 10, overflow: 'hidden', maxHeight: 160, overflowY: 'auto' }}>
                  {versions.map((v, i) => (
                    <div key={v.version ?? i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 12px', borderTop: i === 0 ? 'none' : `1px solid ${tvTokens.borderMuted}` }}>
                      <span style={{ ...pillMono, background: 'transparent', color: tvTokens.ink500 }}>v{v.version}</span>
                      <span style={{ fontSize: 12.5, color: tvTokens.ink, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.note || <span style={{ color: tvTokens.ink400 }}>no note</span>}</span>
                      <span style={{ fontSize: 11, color: tvTokens.ink400, whiteSpace: 'nowrap' }}>{rel(v.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} style={btnGhost}>{editable ? 'Cancel' : 'Close'}</button>
          {editable && !loading && (
            <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save policy'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// Phase 4 fan-out: view + set per-agency/per-program overrides for an agent.
function ScopePanel({ agent, onClose, onToast }: { agent: Agent; onClose: () => void; onToast: (m: string) => void }) {
  const [globalCfg, setGlobalCfg] = useState<any>({});
  const [overrides, setOverrides] = useState<any[]>([]);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [level, setLevel] = useState<'agency' | 'program'>('agency');
  const [targetId, setTargetId] = useState('');
  const [status, setStatus] = useState('paused');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [scopesR, optsR]: any[] = await Promise.all([
      agentsApi.getAgentScopes(agent.key).catch(() => null),
      agentsApi.getScopeOptions().catch(() => null),
    ]);
    const s = (scopesR as any)?.data ?? scopesR;
    setGlobalCfg(s?.global || {});
    setOverrides(Array.isArray(s?.overrides) ? s.overrides : []);
    const o = (optsR as any)?.data ?? optsR;
    setOrgs(Array.isArray(o?.orgs) ? o.orgs : []);
    setPrograms(Array.isArray(o?.programs) ? o.programs : []);
  }, [agent.key]);
  useEffect(() => { void load(); }, [load]);

  const orgName = (id: number) => orgs.find((o) => o.id === id)?.name || `cbo ${id}`;
  const programName = (id: number) => programs.find((p) => p.id === id)?.name || `program ${id}`;

  const addOverride = async () => {
    const id = Number(targetId);
    if (!id) {
      onToast(level === 'agency' ? 'Pick an agency.' : 'Pick a program.');
      return;
    }
    setSaving(true);
    try {
      await agentsApi.setAgentScope(agent.key, {
        scope_level: level,
        cbo_id: level === 'agency' ? id : undefined,
        program_id: level === 'program' ? id : undefined,
        status,
      });
      onToast(`${level === 'agency' ? orgName(id) : programName(id)} set to ${status}.`);
      setTargetId('');
      await load();
    } catch {
      onToast('Could not save the override.');
    } finally {
      setSaving(false);
    }
  };

  const fld: React.CSSProperties = { height: 36, padding: '0 10px', borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, background: tvTokens.paper, color: tvTokens.ink, fontSize: 13 };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,23,28,0.45)', zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(12px, 3vw, 20px)' }}>
      <div onClick={(e) => e.stopPropagation()} className="triveny-platform" style={{ width: 'min(520px, 92vw)', background: tvTokens.surface, borderRadius: 14, border: `1px solid ${tvTokens.border}`, boxShadow: '0 24px 60px rgba(0,0,0,0.28)', padding: 22, maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ margin: '0 0 2px', fontFamily: tvTokens.fontDisplay, fontSize: 18, fontWeight: 650, color: tvTokens.ink }}>{agent.display_name} · scopes</h2>
        <p style={{ margin: '0 0 16px', fontSize: 12.5, color: tvTokens.ink500 }}>
          Overrides win most-specific-first: program → agency → global. A paused scope stops this agent there.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: `1px solid ${tvTokens.border}`, borderRadius: 10, marginBottom: 12 }}>
          <span style={{ ...pillMono, background: 'transparent', color: tvTokens.ink500 }}>GLOBAL</span>
          <span style={{ fontSize: 13, color: tvTokens.ink }}>default</span>
          <span style={{ marginLeft: 'auto', ...pillMono, ...((globalCfg?.status || 'active') === 'paused' ? { color: '#b9781f', background: '#f8efda' } : { color: '#12855f', background: '#e3f3ec' }) }}>
            {globalCfg?.status || 'active'}
          </span>
        </div>

        {overrides.length > 0 && (
          <div style={{ border: `1px solid ${tvTokens.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
            {overrides.map((o, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderTop: i === 0 ? 'none' : `1px solid ${tvTokens.borderMuted}` }}>
                <span style={{ ...pillMono, background: 'transparent', color: tvTokens.ink500 }}>{o.scope_level === 'program' ? 'PROGRAM' : 'AGENCY'}</span>
                <span style={{ fontSize: 13, color: tvTokens.ink }}>{o.scope_level === 'program' ? programName(o.program_id) : orgName(o.cbo_id)}</span>
                <span style={{ marginLeft: 'auto', ...pillMono, ...(o.status === 'paused' ? { color: '#b9781f', background: '#f8efda' } : { color: '#12855f', background: '#e3f3ec' }) }}>{o.status || 'inherit'}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 11, color: tvTokens.ink500, display: 'block', marginBottom: 4 }}>Scope</label>
            <select value={level} onChange={(e) => { setLevel(e.target.value as any); setTargetId(''); }} style={fld}>
              <option value="agency">Agency</option>
              <option value="program">Program</option>
            </select>
          </div>
          <div style={{ minWidth: 200 }}>
            <label style={{ fontSize: 11, color: tvTokens.ink500, display: 'block', marginBottom: 4 }}>{level === 'agency' ? 'Agency' : 'Program'}</label>
            <select value={targetId} onChange={(e) => setTargetId(e.target.value)} style={{ ...fld, width: '100%' }}>
              <option value="">Select…</option>
              {level === 'agency'
                ? orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)
                : programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: tvTokens.ink500, display: 'block', marginBottom: 4 }}>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={fld}>
              <option value="paused">Paused</option>
              <option value="active">Active</option>
            </select>
          </div>
          <button onClick={addOverride} disabled={saving} style={{ ...btnPrimary, height: 36 }}>{saving ? 'Saving…' : 'Set override'}</button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onClose} style={btnGhost}>Done</button>
        </div>
      </div>
    </div>
  );
}

export default AgentControlPage;
